/*
 * @license
 * Broadcast Theme (c) Invisible Themes
 *
 * This file is included for advanced development by
 * Shopify Agencies.  Modified versions of the theme
 * code are not supported by Shopify or Invisible.
 *
 * In order to use this file you will need to change
 * theme.js to theme.dev.js in /layout/theme.liquid
 *
 */

(function (themeCurrency, themeImages, themeAddresses, Sqrl, Flickity, Rellax, FlickityFade, AOS) {
	'use strict';

	window.theme = window.theme || {};

	window.theme.sizes = {
		mobile: 480,
		small: 750,
		large: 990,
		widescreen: 1400,
	};

	window.theme.keyboardKeys = {
		TAB: 9,
		ENTER: 13,
		ESCAPE: 27,
		SPACE: 32,
		LEFTARROW: 37,
		RIGHTARROW: 39,
	};

	window.theme.focusable = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

	function floatLabels(container) {
		const floats = container.querySelectorAll('.form-field');
		floats.forEach((element) => {
			const label = element.querySelector('label');
			const input = element.querySelector('input, textarea');
			if (label && input) {
				input.addEventListener('keyup', (event) => {
					if (event.target.value !== '') {
						label.classList.add('label--float');
					} else {
						label.classList.remove('label--float');
					}
				});
				if (input.value && input.value.length) {
					label.classList.add('label--float');
				}
			}
		});
	}

	function readHeights() {
		const h = {};
		h.windowHeight = window.innerHeight;
		h.announcementHeight = getHeight('[data-section-type*="announcement"]');
		h.footerHeight = getHeight('[data-section-type*="footer"]');
		h.menuHeight = getHeight('[data-header-height]');
		h.headerHeight = h.menuHeight + h.announcementHeight;
		h.logoHeight = getFooterLogoWithPadding();
		return h;
	}

	function setVarsOnResize() {
		document.addEventListener('theme:resize', resizeVars);
		setVars();
	}

	function setVars() {
		const { windowHeight, announcementHeight, headerHeight, logoHeight, menuHeight, footerHeight } = readHeights();
		document.documentElement.style.setProperty('--full-screen', `${windowHeight}px`);
		document.documentElement.style.setProperty('--three-quarters', `${windowHeight * (3 / 4)}px`);
		document.documentElement.style.setProperty('--two-thirds', `${windowHeight * (2 / 3)}px`);
		document.documentElement.style.setProperty('--one-half', `${windowHeight / 2}px`);
		document.documentElement.style.setProperty('--one-third', `${windowHeight / 3}px`);

		document.documentElement.style.setProperty('--menu-height', `${menuHeight}px`);
		document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
		document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);

		document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
		document.documentElement.style.setProperty('--content-full', `${windowHeight - headerHeight - logoHeight / 2}px`);
		document.documentElement.style.setProperty('--content-min', `${windowHeight - headerHeight - footerHeight}px`);
	}

	function resizeVars() {
		// restrict the heights that are changed on resize to avoid iOS jump when URL bar is shown and hidden
		const { windowHeight, announcementHeight, headerHeight, logoHeight, menuHeight, footerHeight } = readHeights();
		document.documentElement.style.setProperty('--menu-height', `${menuHeight}px`);
		document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
		document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);

		document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
		document.documentElement.style.setProperty('--content-full', `${windowHeight - headerHeight - logoHeight / 2}px`);
		document.documentElement.style.setProperty('--content-min', `${windowHeight - headerHeight - footerHeight}px`);
	}

	function getHeight(selector) {
		const el = document.querySelector(selector);
		if (el) {
			return el.clientHeight;
		} else {
			return 0;
		}
	}

	function getFooterLogoWithPadding() {
		const height = getHeight('[data-footer-logo]');
		if (height > 0) {
			return height + 20;
		} else {
			return 0;
		}
	}

	function singles(frame, wrappers) {
		// sets the height of any frame passed in with the
		// tallest preventOverflowContent as well as any image in that frame
		const padding = 64;
		let tallest = 0;

		wrappers.forEach((wrap) => {
			if (wrap.offsetHeight > tallest) {
				tallest = wrap.offsetHeight;
			}
		});
		const images = frame.querySelectorAll('[data-overflow-background]');
		const frames = [frame, ...images];
		frames.forEach((el) => {
			el.style.setProperty('min-height', `calc(${tallest + padding}px + var(--header-padding)`);
		});
	}

	function doubles(section) {
		if (window.innerWidth <= window.theme.sizes.small) {
			// if we are below the small breakpoint, the double section acts like two independent
			// single frames
			let singleFrames = section.querySelectorAll('[data-overflow-frame]');
			singleFrames.forEach((singleframe) => {
				const wrappers = singleframe.querySelectorAll('[data-overflow-content]');
				singles(singleframe, wrappers);
			});
			return;
		}

		const padding = 64;
		let tallest = 0;

		const frames = section.querySelectorAll('[data-overflow-frame]');
		const contentWrappers = section.querySelectorAll('[data-overflow-content]');
		contentWrappers.forEach((content) => {
			if (content.offsetHeight > tallest) {
				tallest = content.offsetHeight;
			}
		});
		const images = section.querySelectorAll('[data-overflow-background]');
		let applySizes = [section, ...frames, ...images];
		applySizes.forEach((el) => {
			el.style.setProperty('min-height', `${tallest + padding}px`);
		});
	}

	function preventOverflow(container) {
		const singleFrames = container.querySelectorAll('.js-overflow-container');
		if (singleFrames) {
			singleFrames.forEach((frame) => {
				const wrappers = frame.querySelectorAll('.js-overflow-content');
				singles(frame, wrappers);
				document.addEventListener('theme:resize', () => {
					singles(frame, wrappers);
				});
			});
		}

		const doubleSections = container.querySelectorAll('[data-overflow-wrapper]');
		if (doubleSections) {
			doubleSections.forEach((section) => {
				doubles(section);
				document.addEventListener('theme:resize', () => {
					doubles(section);
				});
			});
		}
	}

	function debounce(fn, time) {
		let timeout;
		return function () {
			// eslint-disable-next-line prefer-rest-params
			if (fn) {
				const functionCall = () => fn.apply(this, arguments);
				clearTimeout(timeout);
				timeout = setTimeout(functionCall, time);
			}
		};
	}

	function dispatch() {
		document.dispatchEvent(
			new CustomEvent('theme:resize', {
				bubbles: true,
			})
		);
	}

	function resizeListener() {
		window.addEventListener(
			'resize',
			debounce(function () {
				dispatch();
			}, 50)
		);
	}

	let prev = window.pageYOffset;
	let up = null;
	let down = null;
	let wasUp = null;
	let wasDown = null;
	let position = 0;

	function dispatch$1() {
		const position = window.pageYOffset;
		if (position > prev) {
			down = true;
			up = false;
		} else if (position < prev) {
			down = false;
			up = true;
		} else {
			up = null;
			down = null;
		}
		prev = position;
		document.dispatchEvent(
			new CustomEvent('theme:scroll', {
				detail: {
					up,
					down,
					position,
				},
				bubbles: false,
			})
		);
		if (up && !wasUp) {
			document.dispatchEvent(
				new CustomEvent('theme:scroll:up', {
					detail: { position },
					bubbles: false,
				})
			);
		}
		if (down && !wasDown) {
			document.dispatchEvent(
				new CustomEvent('theme:scroll:down', {
					detail: { position },
					bubbles: false,
				})
			);
		}
		wasDown = down;
		wasUp = up;
	}

	function lock() {
		position = window.pageYOffset;

		document.documentElement.style.setProperty('scroll-behavior', 'unset');
		document.body.style.setProperty('position', 'fixed');
		document.body.style.setProperty('top', `-${position}px`);
		document.body.style.setProperty('left', '0');
		document.body.style.setProperty('width', '100%');
		document.body.style.setProperty('overflow', 'hidden');

		document.documentElement.setAttribute('data-scroll-locked', '');
		document.documentElement.style.setProperty('position', 'relative');
		document.documentElement.style.setProperty('overflow', 'hidden');
	}

	function unlock() {
		document.body.style.removeProperty('position');
		document.body.style.removeProperty('top');
		document.body.style.removeProperty('left');
		document.body.style.removeProperty('width');
		document.body.style.removeProperty('height');
		document.body.style.removeProperty('overflow');

		document.documentElement.removeAttribute('data-scroll-locked');
		document.documentElement.style.removeProperty('position');
		document.documentElement.style.removeProperty('overflow');

		window.scroll({
			left: 0,
			top: position,
		});

		document.documentElement.style.setProperty('scroll-behavior', 'smooth');
	}

	function scrollListener() {
		let timeout;
		window.addEventListener(
			'scroll',
			function () {
				if (timeout) {
					window.cancelAnimationFrame(timeout);
				}
				timeout = window.requestAnimationFrame(function () {
					dispatch$1();
				});
			},
			{ passive: true }
		);

		window.addEventListener('theme:scroll:lock', lock);
		window.addEventListener('theme:scroll:unlock', unlock);
	}

	function wasTouched() {
		window.theme.touched = true;
		document.removeEventListener('touchstart', wasTouched, { passive: true });
		document.querySelector('body').classList.add('supports-touch');
		document.dispatchEvent(
			new CustomEvent('theme:touch', {
				bubbles: true,
			})
		);
	}

	document.addEventListener('touchstart', wasTouched, { passive: true });

	function getScript(url, callback, callbackError) {
		let head = document.getElementsByTagName('head')[0];
		let done = false;
		let script = document.createElement('script');
		script.src = url;

		// Attach handlers for all browsers
		script.onload = script.onreadystatechange = function () {
			if (!done && (!this.readyState || this.readyState == 'loaded' || this.readyState == 'complete')) {
				done = true;
				callback();
			} else {
				callbackError();
			}
		};

		head.appendChild(script);
	}

	const loaders = {};

	function loadScript(options = {}) {
		if (!options.type) {
			options.type = 'json';
		}

		if (options.url) {
			if (loaders[options.url]) {
				return loaders[options.url];
			} else {
				return getScriptWithPromise(options.url, options.type);
			}
		} else if (options.json) {
			if (loaders[options.json]) {
				return Promise.resolve(loaders[options.json]);
			} else {
				return window
					.fetch(options.json)
					.then((response) => {
						return response.json();
					})
					.then((response) => {
						loaders[options.json] = response;
						return response;
					});
			}
		} else if (options.name) {
			const key = ''.concat(options.name, options.version);
			if (loaders[key]) {
				return loaders[key];
			} else {
				return loadShopifyWithPromise(options);
			}
		} else {
			return Promise.reject();
		}
	}

	function getScriptWithPromise(url, type) {
		const loader = new Promise((resolve, reject) => {
			if (type === 'text') {
				fetch(url)
					.then((response) => response.text())
					.then((data) => {
						resolve(data);
					})
					.catch((error) => {
						reject(error);
					});
			} else {
				getScript(
					url,
					function () {
						resolve();
					},
					function () {
						reject();
					}
				);
			}
		});

		loaders[url] = loader;
		return loader;
	}

	function loadShopifyWithPromise(options) {
		const key = ''.concat(options.name, options.version);
		const loader = new Promise((resolve, reject) => {
			try {
				window.Shopify.loadFeatures([
					{
						name: options.name,
						version: options.version,
						onLoad: (err) => {
							onLoadFromShopify(resolve, reject, err);
						},
					},
				]);
			} catch (err) {
				reject(err);
			}
		});
		loaders[key] = loader;
		return loader;
	}

	function onLoadFromShopify(resolve, reject, err) {
		if (err) {
			return reject(err);
		} else {
			return resolve();
		}
	}

	const selectors = {
		popupContainer: '.pswp',
		popupCloseBtn: '.pswp__custom-close',
		popupIframe: 'iframe',
		popupCustomIframe: '.pswp__custom-iframe',
		popupThumbs: '.pswp__thumbs',
		dataOptionClasses: 'data-pswp-option-classes',
		dataVideoType: 'data-video-type',
		dataDisableSound: 'data-disable-sound',
		dataVideoId: 'data-video-id',
	};

	const classes = {
		classCurrent: 'is-current',
		classCustomLoader: 'pswp--custom-loader',
		classCustomOpen: 'pswp--custom-opening',
		classLoader: 'pswp__loader',
		loaded: 'loaded',
	};

	const loaderHTML = `<div class="${classes.classLoader}"><div class="loader pswp__loader-line"><div class="loader-indeterminate"></div></div></div>`;

	const players = [];
	const loadVideoYT = (playerId, videoId, disableSound) => {
		const defaultYoutubeOptions = {
			height: '720',
			width: '1280',
			events: {
				onReady: function (event) {
					const eventIframe = event.target.getIframe();

					eventIframe.setAttribute('tabindex', '-1');

					if (disableSound) {
						event.target.mute();
					} else {
						event.target.unMute();
					}
					event.target.playVideo();
				},
				onStateChange: function (event) {
					// Loop video if state is ended
					if (event.data == 0) {
						event.target.playVideo();
					}
					if (event.data == 1 && event.target.getIframe().parentElement) {
						// video is playing
						event.target.getIframe().parentElement.classList.add(classes.loaded);
					}
				},
			},
		};
		const currentYoutubeOptions = Object.assign({}, defaultYoutubeOptions);
		currentYoutubeOptions.videoId = videoId;
		if (videoId.length) {
			players[playerId] = new YT.Player(playerId, currentYoutubeOptions);
		}
	};

	const loadVideoVimeo = (videoId, state, disableSound) => {
		const oembed_url = 'https://vimeo.com/api/oembed.json';
		const vimeo_url = 'https://vimeo.com/' + videoId;
		let paramsString = '';

		const params = {
			url: vimeo_url,
			background: false,
			muted: disableSound,
			autoplay: true,
		};

		for (let key in params) {
			paramsString += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&';
		}

		fetch(`${oembed_url}?${paramsString}`)
			.then((response) => response.json())
			.then(function (data) {
				state.innerHTML = data.html;

				setTimeout(function () {
					state.parentElement.classList.add(classes.loaded);
				}, 1000);
			})
			.catch(function () {
				console.log('error');
			});
	};

	const openPhotoSwipe = (items, options = '') => {
		const pswpElement = document.querySelectorAll(selectors.popupContainer)[0];
		pswpElement.classList.add(classes.classCustomOpen);

		if (pswpElement.classList.contains(classes.classCustomLoader) && options !== '' && options.mainClass) {
			pswpElement.setAttribute(selectors.dataOptionClasses, options.mainClass);
			let loaderElem = document.createElement('div');
			loaderElem.innerHTML = loaderHTML;
			loaderElem = loaderElem.firstChild;
			pswpElement.appendChild(loaderElem);
		} else {
			pswpElement.setAttribute(selectors.dataOptionClasses, '');
		}

		const loadedPromise = loadScript({ url: window.theme.assets.photoswipe });
		const returnPhotoSwipe = loadedPromise
			.then(() => {
				const PhotoSwipe = window.themePhotoswipe.PhotoSwipe.default;
				const PhotoSwipeUI = window.themePhotoswipe.PhotoSwipeUI.default;
				const popupThumbsContainer = pswpElement.querySelector(selectors.popupThumbs);

				if (pswpElement.classList.contains(classes.classCustomLoader)) {
					pswpElement.classList.remove(classes.classCustomLoader);
				}

				pswpElement.classList.remove(classes.classCustomOpen);

				let newOptions = {
					history: false,
					focus: false,
					mainClass: '',
				};

				if (options !== '') {
					newOptions = options;
				}

				const popup = new PhotoSwipe(pswpElement, PhotoSwipeUI, items, newOptions);
				popup.init();

				const videoContainer = pswpElement.querySelector(selectors.popupCustomIframe);
				if (videoContainer) {
					const videoType = videoContainer.getAttribute(selectors.dataVideoType);
					const videoId = videoContainer.getAttribute(selectors.dataVideoId);
					const videoDisableSound = videoContainer.getAttribute(selectors.dataDisableSound) === 'true';
					const videoContainerId = videoContainer.id;

					if (videoType == 'youtube') {
						if (window.isYoutubeAPILoaded) {
							loadVideoYT(videoContainerId, videoId, videoDisableSound);
						} else {
							// Load Youtube API if not loaded yet
							window.loadYoutubeAPI();
							document.body.addEventListener('youtubeAPIReady', () => loadVideoYT(videoContainerId, videoId, videoDisableSound));
						}
					} else if (videoType == 'vimeo') {
						loadVideoVimeo(videoId, videoContainer, videoDisableSound);
					}
				}

				const closeBtn = pswpElement.querySelector(selectors.popupCloseBtn);
				if (closeBtn) {
					closeBtn.addEventListener('click', function () {
						popup.close();
					});
				}

				popup.listen('close', function () {
					const popupIframe = pswpElement.querySelector(selectors.popupIframe);
					if (popupIframe) {
						popupIframe.setAttribute('src', '');
						popupIframe.parentNode.removeChild(popupIframe);
					}

					if (popupThumbsContainer && popupThumbsContainer.firstChild) {
						while (popupThumbsContainer.firstChild) popupThumbsContainer.removeChild(popupThumbsContainer.firstChild);
					}

					pswpElement.setAttribute(selectors.dataOptionClasses, '');
					const loaderElem = pswpElement.querySelector(`.${classes.classLoader}`);
					if (loaderElem) {
						pswpElement.removeChild(loaderElem);
					}
				});

				if (popupThumbsContainer && popupThumbsContainer.firstChild) {
					const stopDisabledScroll = (e) => {
						e.stopPropagation();
					};
					popupThumbsContainer.addEventListener('wheel', stopDisabledScroll);
					popupThumbsContainer.addEventListener('mousewheel', stopDisabledScroll);
					popupThumbsContainer.addEventListener('DOMMouseScroll', stopDisabledScroll);

					const popupThumbs = pswpElement.querySelectorAll(`${selectors.popupThumbs} > *`);
					popupThumbs.forEach((element, i) => {
						element.addEventListener('click', function (e) {
							e.preventDefault();
							this.parentElement.querySelector(`.${classes.classCurrent}`).classList.remove(classes.classCurrent);
							this.classList.add(classes.classCurrent);
							popup.goTo(i);
						});
					});

					const setCurrentThumb = () => {
						const lastCurrentThumb = pswpElement.querySelector(`${selectors.popupThumbs} > .${classes.classCurrent}`);
						if (lastCurrentThumb) {
							lastCurrentThumb.classList.remove(classes.classCurrent);
						}
						const currentThumb = popupThumbs[popup.getCurrentIndex()];
						const thumbsContainerLeft = popupThumbsContainer.scrollLeft;
						const thumbsContainerWidth = popupThumbsContainer.offsetWidth;
						const thumbsContainerPos = thumbsContainerLeft + thumbsContainerWidth;
						const currentThumbLeft = currentThumb.offsetLeft;
						const currentThumbWidth = currentThumb.offsetWidth;
						const currentThumbPos = currentThumbLeft + currentThumbWidth;

						currentThumb.classList.add(classes.classCurrent);

						if (thumbsContainerPos <= currentThumbPos || thumbsContainerPos > currentThumbLeft) {
							const currentThumbMarginLeft = parseInt(window.getComputedStyle(currentThumb).marginLeft);
							popupThumbsContainer.scrollTo({
								top: 0,
								left: currentThumbLeft - currentThumbMarginLeft,
								behavior: 'smooth',
							});
						}
					};

					popup.listen('imageLoadComplete', setCurrentThumb);
					popup.listen('beforeChange', setCurrentThumb);
				}
			})
			.catch((e) => console.error(e));
		return returnPhotoSwipe;
	};

	function ariaToggle(container) {
		const toggleButtons = container.querySelectorAll('[data-aria-toggle]');
		if (toggleButtons.length) {
			toggleButtons.forEach((element) => {
				element.addEventListener('click', function (event) {
					event.preventDefault();
					const currentTarget = event.currentTarget;
					currentTarget.setAttribute('aria-expanded', currentTarget.getAttribute('aria-expanded') == 'false' ? 'true' : 'false');
					const toggleID = currentTarget.getAttribute('aria-controls');
					document.querySelector(`#${toggleID}`).classList.toggle('expanding');
					setTimeout(function () {
						document.querySelector(`#${toggleID}`).classList.toggle('expanded');
					}, 40);
				});
			});
		}
	}

	function videoPopups(container) {
		const videoPopups = container.querySelectorAll('[data-video-popup]');
		if (videoPopups.length) {
			videoPopups.forEach((element) => {
				element.addEventListener('click', function (e) {
					const videoPopupHtml = element.getAttribute('data-video-popup');
					if (videoPopupHtml.trim() !== '') {
						e.preventDefault();

						const items = [
							{
								html: videoPopupHtml,
							},
						];

						openPhotoSwipe(items);
					}
				});
			});
		}
	}

	function lazyImageBackgrounds() {
		document.addEventListener('lazyloaded', function (e) {
			const lazyImage = e.target.parentNode;
			if (lazyImage.classList.contains('lazy-image')) {
				lazyImage.style.backgroundImage = 'none';
			}
		});
	}

	resizeListener();
	scrollListener();
	lazyImageBackgrounds();

	window.addEventListener('load', () => {
		setVarsOnResize();
		floatLabels(document);
		preventOverflow(document);
		videoPopups(document);
		ariaToggle(document);
	});

	document.addEventListener('shopify:section:load', (e) => {
		const container = e.target;
		floatLabels(container);
		preventOverflow(container);
		videoPopups(container);
		ariaToggle(document);
	});

	(function () {
		function n(n) {
			var i = window.innerWidth || document.documentElement.clientWidth,
				r = window.innerHeight || document.documentElement.clientHeight,
				t = n.getBoundingClientRect();
			return t.top >= 0 && t.bottom <= r && t.left >= 0 && t.right <= i;
		}
		function t(n) {
			var i = window.innerWidth || document.documentElement.clientWidth,
				r = window.innerHeight || document.documentElement.clientHeight,
				t = n.getBoundingClientRect(),
				u = (t.left >= 0 && t.left <= i) || (t.right >= 0 && t.right <= i),
				f = (t.top >= 0 && t.top <= r) || (t.bottom >= 0 && t.bottom <= r);
			return u && f;
		}
		function i(n, i) {
			function r() {
				var r = t(n);
				r != u && ((u = r), typeof i == 'function' && i(r, n));
			}
			var u = t(n);
			window.addEventListener('load', r);
			window.addEventListener('resize', r);
			window.addEventListener('scroll', r);
		}
		function r(t, i) {
			function r() {
				var r = n(t);
				r != u && ((u = r), typeof i == 'function' && i(r, t));
			}
			var u = n(t);
			window.addEventListener('load', r);
			window.addEventListener('resize', r);
			window.addEventListener('scroll', r);
		}
		window.visibilityHelper = { isElementTotallyVisible: n, isElementPartiallyVisible: t, inViewportPartially: i, inViewportTotally: r };
	})();

	const showElement = (elem, removeProp = false, prop = 'block') => {
		if (elem) {
			if (removeProp) {
				elem.style.removeProperty('display');
			} else {
				elem.style.display = prop;
			}
		}
	};

	/**
	 * Module to show Recently Viewed Products
	 *
	 * Copyright (c) 2014 Caroline Schnapp (11heavens.com)
	 * Dual licensed under the MIT and GPL licenses:
	 * http://www.opensource.org/licenses/mit-license.php
	 * http://www.gnu.org/licenses/gpl.html
	 *
	 */

	Shopify.Products = (function () {
		const config = {
			howManyToShow: 4,
			howManyToStoreInMemory: 10,
			wrapperId: 'recently-viewed-products',
			templateId: 'recently-viewed-product-template',
			onComplete: null,
		};

		let productHandleQueue = [];
		let wrapper = null;
		let template = null;

		const cookie = {
			configuration: {
				expires: 90,
				path: '/',
				domain: window.location.hostname,
			},
			name: 'shopify_recently_viewed',
			write: function (recentlyViewed) {
				const recentlyViewedString = recentlyViewed.join(' ');
				document.cookie = `${this.name}=${recentlyViewedString}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
			},
			read: function () {
				let recentlyViewed = [];
				let cookieValue = null;
				const templateProduct = document.querySelector('#template-product');

				if (templateProduct) {
					const currentProduct = templateProduct.getAttribute('data-product-handle');

					if (document.cookie.indexOf('; ') !== -1 && document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
						cookieValue = document.cookie
							.split('; ')
							.find((row) => row.startsWith(this.name))
							.split('=')[1];
					}

					if (cookieValue !== null) {
						recentlyViewed = cookieValue.split(' ');
					}

					// Remove current product from the array
					if (recentlyViewed.indexOf(currentProduct) != -1) {
						const currentProductIndex = recentlyViewed.indexOf(currentProduct);
						recentlyViewed.splice(currentProductIndex, 1);
					}
				}

				return recentlyViewed;
			},
			destroy: function () {
				const cookieVal = null;
				document.cookie = `${this.name}=${cookieVal}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
			},
			remove: function (productHandle) {
				const recentlyViewed = this.read();
				const position = recentlyViewed.indexOf(productHandle);
				if (position !== -1) {
					recentlyViewed.splice(position, 1);
					this.write(recentlyViewed);
				}
			},
		};

		const finalize = () => {
			showElement(wrapper, true);

			// If we have a callback.
			if (config.onComplete) {
				try {
					config.onComplete();
				} catch (error) {}
			}
		};

		const moveAlong = (shown) => {
			if (productHandleQueue.length && shown < config.howManyToShow) {
				fetch('/products/' + productHandleQueue[0] + '.js')
					.then((response) => response.json())
					.then((product) => {
						product.priceFormatted = themeCurrency.formatMoney(product.price, theme.moneyFormat);
						product.compareFormatted = themeCurrency.formatMoney(product.compare_at_price, theme.moneyFormat);
						product.topImage = product.media[0] ? themeImages.getSizedImageUrl(product.media[0].preview_image.src, '900x900') : '';
						product.bottomImage = product.media[1] ? themeImages.getSizedImageUrl(product.media[1].preview_image.src, '900x900') : '';

						let doubleImageClass = '';
						let bottomImageClass = '';
						let saleClass = 'is-hidden hidden';
						let priceClass = '';

						if (product.media.length > 1) {
							doubleImageClass = 'double__image';
						} else {
							bottomImageClass = 'is-hidden hidden';
						}

						if (product.compare_at_price > product.price) {
							priceClass = 'sale';
							saleClass = '';
						}

						let markup = template.innerHTML;
						markup = markup.replace(/\|\|itemUrl\|\|/g, product.url);
						markup = markup.replace(/\|\|itemDoubleImageClass\|\|/g, doubleImageClass);
						markup = markup.replace(/\|\|itemTopImage\|\|/g, product.topImage);
						markup = markup.replace(/\|\|itemBottomImage\|\|/g, product.bottomImage);
						markup = markup.replace(/\|\|itemBottomImageClass\|\|/g, bottomImageClass);
						markup = markup.replace(/\|\|itemSaleClass\|\|/g, saleClass);
						markup = markup.replace(/\|\|itemPriceClass\|\|/g, priceClass);
						markup = markup.replace(/\|\|itemTitle\|\|/g, product.title);
						markup = markup.replace(/\|\|itemComparedPrice\|\|/g, product.compareFormatted);
						markup = markup.replace(/\|\|itemPrice\|\|/g, product.priceFormatted);
						markup = markup.replace(/\|\|itemHandle\|\|/g, product.handle);

						wrapper.innerHTML += markup;

						productHandleQueue.shift();
						shown++;
						moveAlong(shown);
					})
					.catch((error) => {
						cookie.remove(productHandleQueue[0]);
						productHandleQueue.shift();
						moveAlong(shown);
					});
			} else {
				finalize();
			}
		};

		return {
			showRecentlyViewed: function (params) {
				const paramsNew = params || {};
				const shown = 0;

				// Update defaults.
				Object.assign(config, paramsNew);

				// Read cookie.
				productHandleQueue = cookie.read();

				// Template and element where to insert.
				template = document.querySelector(`#${config.templateId}`);
				wrapper = document.querySelector(`#${config.wrapperId}`);

				// How many products to show.
				config.howManyToShow = Math.min(productHandleQueue.length, config.howManyToShow);

				// If we have any to show.
				if (config.howManyToShow && template && wrapper) {
					// Getting each product with an Ajax call and rendering it on the page.
					moveAlong(shown);
				}
			},

			getConfig: function () {
				return config;
			},

			clearList: function () {
				cookie.destroy();
			},

			recordRecentlyViewed: function (params) {
				const paramsNew = params || {};

				// Update defaults.
				Object.assign(config, paramsNew);

				// Read cookie.
				let recentlyViewed = cookie.read();

				// If we are on a product page.
				if (window.location.pathname.indexOf('/products/') !== -1) {
					// What is the product handle on this page.
					const productHandle = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/)[1];
					// In what position is that product in memory.
					const position = recentlyViewed.indexOf(productHandle);
					// If not in memory.
					if (position === -1) {
						// Add product at the start of the list.
						recentlyViewed.unshift(productHandle);
						// Only keep what we need.
						recentlyViewed = recentlyViewed.splice(0, config.howManyToStoreInMemory);
					} else {
						// Remove the product and place it at start of list.
						recentlyViewed.splice(position, 1);
						recentlyViewed.unshift(productHandle);
					}

					// Update cookie.
					cookie.write(recentlyViewed);
				}
			},

			hasProducts: cookie.read().length > 0,
		};
	})();

	const getUrlString = (params, keys = [], isArray = false) => {
		const p = Object.keys(params)
			.map((key) => {
				let val = params[key];

				if ('[object Object]' === Object.prototype.toString.call(val) || Array.isArray(val)) {
					if (Array.isArray(params)) {
						keys.push('');
					} else {
						keys.push(key);
					}
					return getUrlString(val, keys, Array.isArray(val));
				} else {
					let tKey = key;

					if (keys.length > 0) {
						const tKeys = isArray ? keys : [...keys, key];
						tKey = tKeys.reduce((str, k) => {
							return '' === str ? k : `${str}[${k}]`;
						}, '');
					}
					if (isArray) {
						return `${tKey}[]=${val}`;
					} else {
						return `${tKey}=${val}`;
					}
				}
			})
			.join('&');

		keys.pop();
		return p;
	};

	const hideElement = (elem) => {
		if (elem) {
			elem.style.display = 'none';
		}
	};

	const fadeIn = (el, display, callback = null) => {
		el.style.opacity = 0;
		el.style.display = display || 'block';

		(function fade() {
			let val = parseFloat(el.style.opacity);
			if (!((val += 0.1) > 1)) {
				el.style.opacity = val;
				requestAnimationFrame(fade);
			}

			if (val === 1 && typeof callback === 'function') {
				callback();
			}
		})();
	};

	/**
	 * Module to add a shipping rates calculator to cart page.
	 *
	 * Copyright (c) 2011-2012 Caroline Schnapp (11heavens.com)
	 * Dual licensed under the MIT and GPL licenses:
	 * http://www.opensource.org/licenses/mit-license.php
	 * http://www.gnu.org/licenses/gpl.html
	 *
	 * Modified version -- coupled with Broadcast theme markup
	 *
	 */

	if (typeof Shopify.Cart === 'undefined') {
		Shopify.Cart = {};
	}

	Shopify.Cart.ShippingCalculator = (function () {
		const _config = {
			submitButton: theme.strings.shippingCalcSubmitButton,
			submitButtonDisabled: theme.strings.shippingCalcSubmitButtonDisabled,
			templateId: 'shipping-calculator-response-template',
			wrapperId: 'wrapper-response',
			customerIsLoggedIn: false,
		};
		const _render = function (response) {
			const template = document.querySelector(`#${_config.templateId}`);
			const wrapper = document.querySelector(`#${_config.wrapperId}`);

			if (template && wrapper) {
				wrapper.innerHTML = '';
				let ratesList = '';
				let ratesText = '';
				let successClass = 'error center';
				let markup = template.innerHTML;
				const rateRegex = /[^[\]]+(?=])/g;

				if (response.rates && response.rates.length) {
					let rateTemplate = rateRegex.exec(markup)[0];
					response.rates.forEach((rate) => {
						let rateHtml = rateTemplate;
						rateHtml = rateHtml.replace(/\|\|rateName\|\|/, rate.name);
						rateHtml = rateHtml.replace(/\|\|ratePrice\|\|/, Shopify.Cart.ShippingCalculator.formatRate(rate.price));
						ratesList += rateHtml;
					});
				}

				if (response.success) {
					successClass = 'success center';
					const createdNewElem = document.createElement('div');
					createdNewElem.innerHTML = template.innerHTML;
					const noShippingElem = createdNewElem.querySelector('[data-template-no-shipping]');

					if (response.rates.length < 1 && noShippingElem) {
						ratesText = noShippingElem.getAttribute('data-template-no-shipping');
					}
				} else {
					ratesText = response.errorFeedback;
				}

				markup = markup.replace(rateRegex, '').replace('[]', '');
				markup = markup.replace(/\|\|ratesList\|\|/g, ratesList);
				markup = markup.replace(/\|\|successClass\|\|/g, successClass);
				markup = markup.replace(/\|\|ratesText\|\|/g, ratesText);

				wrapper.innerHTML += markup;
			}
		};
		const _enableButtons = function () {
			const getRatesButton = document.querySelector('.get-rates');
			getRatesButton.removeAttribute('disabled');
			getRatesButton.classList.remove('disabled');
			getRatesButton.value = _config.submitButton;
		};
		const _disableButtons = function () {
			const getRatesButton = document.querySelector('.get-rates');
			getRatesButton.setAttribute('disabled', 'disabled');
			getRatesButton.classList.add('disabled');
			getRatesButton.value = _config.submitButtonDisabled;
		};
		const _getCartShippingRatesForDestination = function (shipping_address) {
			const encodedShippingAddressData = encodeURI(
				getUrlString({
					shipping_address: shipping_address,
				})
			);
			const url = `${window.theme.routes.cart}/shipping_rates.json?${encodedShippingAddressData}`;
			const request = new XMLHttpRequest();
			request.open('GET', url, true);

			request.onload = function () {
				if (this.status >= 200 && this.status < 400) {
					const response = JSON.parse(this.response);
					const rates = response.shipping_rates;
					_onCartShippingRatesUpdate(rates, shipping_address);
				} else {
					_onError(this);
				}
			};

			request.onerror = function () {
				_onError(this);
			};

			request.send();
		};
		const _fullMessagesFromErrors = function (errors) {
			const fullMessages = [];

			for (const error in errors) {
				for (const message of errors[error]) {
					fullMessages.push(error + ' ' + message);
				}
			}

			return fullMessages;
		};
		const _onError = function (XMLHttpRequest) {
			hideElement(document.querySelector('#estimated-shipping'));

			const shippingChild = document.querySelector('#estimated-shipping em');
			if (shippingChild) {
				while (shippingChild.firstChild) shippingChild.removeChild(shippingChild.firstChild);
			}
			_enableButtons();
			let feedback = '';
			const data = eval('(' + XMLHttpRequest.responseText + ')');
			if (data.message) {
				feedback = data.message + '(' + data.status + '): ' + data.description;
			} else {
				feedback = 'Error : ' + _fullMessagesFromErrors(data).join('; ');
			}
			if (feedback === 'Error : country is not supported.') {
				feedback = 'We do not ship to this destination.';
			}
			_render({
				rates: [],
				errorFeedback: feedback,
				success: false,
			});

			showElement(document.querySelector(`#${_config.wrapperId}`));
		};
		const _onCartShippingRatesUpdate = function (rates, shipping_address) {
			_enableButtons();
			let readable_address = '';
			if (shipping_address.zip) {
				readable_address += shipping_address.zip + ', ';
			}
			if (shipping_address.province) {
				readable_address += shipping_address.province + ', ';
			}
			readable_address += shipping_address.country;
			const shippingChild = document.querySelector('#estimated-shipping em');
			if (rates.length && shippingChild) {
				if (rates[0].price == '0.00') {
					shippingChild.textContent = 'FREE';
				} else {
					shippingChild.textContent = themeCurrency.formatMoney(rates[0].price, theme.moneyFormat);
				}
			}
			_render({
				rates: rates,
				address: readable_address,
				success: true,
			});

			const fadeElements = document.querySelectorAll(`#${_config.wrapperId}, #estimated-shipping`);

			if (fadeElements.length) {
				fadeElements.forEach((element) => {
					fadeIn(element);
				});
			}
		};

		const _init = function () {
			const getRatesButton = document.querySelector('.get-rates');
			const fieldsContainer = document.querySelector('#address_container');
			const selectCountry = document.querySelector('#address_country');
			const selectProvince = document.querySelector('#address_province');
			const htmlEl = document.querySelector('html');
			let locale = 'en';
			if (htmlEl.hasAttribute('lang') && htmlEl.getAttribute('lang') !== '') {
				locale = htmlEl.getAttribute('lang');
			}

			if (fieldsContainer) {
				themeAddresses.AddressForm(fieldsContainer, locale, {
					shippingCountriesOnly: true,
				});
			}

			if (selectCountry && selectCountry.hasAttribute('data-default') && selectProvince && selectProvince.hasAttribute('data-default')) {
				selectCountry.addEventListener('change', function () {
					selectCountry.removeAttribute('data-default');
					selectProvince.removeAttribute('data-default');
				});
			}

			if (getRatesButton) {
				getRatesButton.addEventListener('click', function (e) {
					_disableButtons();
					const wrapper = document.querySelector(`#${_config.wrapperId}`);
					while (wrapper.firstChild) wrapper.removeChild(wrapper.firstChild);
					hideElement(wrapper);
					const shippingAddress = {};
					let elemCountryVal = selectCountry.value;
					let elemProvinceVal = selectProvince.value;
					const elemCountryData = selectCountry.getAttribute('data-default-fullname');
					if (elemCountryVal === '' && elemCountryData && elemCountryData !== '') {
						elemCountryVal = elemCountryData;
					}
					const elemProvinceData = selectProvince.getAttribute('data-default-fullname');
					if (elemProvinceVal === '' && elemProvinceData && elemProvinceData !== '') {
						elemProvinceVal = elemProvinceData;
					}
					shippingAddress.zip = document.querySelector('#address_zip').value || '';
					shippingAddress.country = elemCountryVal || '';
					shippingAddress.province = elemProvinceVal || '';
					_getCartShippingRatesForDestination(shippingAddress);
				});

				if (_config.customerIsLoggedIn && getRatesButton.classList.contains('get-rates--trigger')) {
					getRatesButton.dispatchEvent(new Event('click'));
				}
			}
		};
		return {
			show: function (params) {
				params = params || {};
				Object.assign(_config, params);
				document.addEventListener('DOMContentLoaded', function () {
					_init();
				});
			},
			getConfig: function () {
				return _config;
			},
			formatRate: function (cents) {
				return themeCurrency.formatMoney(cents, theme.moneyFormat);
			},
		};
	})();

	/**
	 * A11y Helpers
	 * -----------------------------------------------------------------------------
	 * A collection of useful functions that help make your theme more accessible
	 */

	/**
	 * Moves focus to an HTML element
	 * eg for In-page links, after scroll, focus shifts to content area so that
	 * next `tab` is where user expects. Used in bindInPageLinks()
	 * eg move focus to a modal that is opened. Used in trapFocus()
	 *
	 * @param {Element} container - Container DOM element to trap focus inside of
	 * @param {Object} options - Settings unique to your theme
	 * @param {string} options.className - Class name to apply to element on focus.
	 */
	function forceFocus(element, options) {
		options = options || {};

		var savedTabIndex = element.tabIndex;

		element.tabIndex = -1;
		element.dataset.tabIndex = savedTabIndex;
		element.focus();
		if (typeof options.className !== 'undefined') {
			element.classList.add(options.className);
		}
		element.addEventListener('blur', callback);

		function callback(event) {
			event.target.removeEventListener(event.type, callback);

			element.tabIndex = savedTabIndex;
			delete element.dataset.tabIndex;
			if (typeof options.className !== 'undefined') {
				element.classList.remove(options.className);
			}
		}
	}

	/**
	 * If there's a hash in the url, focus the appropriate element
	 * This compensates for older browsers that do not move keyboard focus to anchor links.
	 * Recommendation: To be called once the page in loaded.
	 *
	 * @param {Object} options - Settings unique to your theme
	 * @param {string} options.className - Class name to apply to element on focus.
	 * @param {string} options.ignore - Selector for elements to not include.
	 */

	function focusHash(options) {
		options = options || {};
		var hash = window.location.hash;
		var element = document.getElementById(hash.slice(1));

		// if we are to ignore this element, early return
		if (element && options.ignore && element.matches(options.ignore)) {
			return false;
		}

		if (hash && element) {
			forceFocus(element, options);
		}
	}

	/**
	 * When an in-page (url w/hash) link is clicked, focus the appropriate element
	 * This compensates for older browsers that do not move keyboard focus to anchor links.
	 * Recommendation: To be called once the page in loaded.
	 *
	 * @param {Object} options - Settings unique to your theme
	 * @param {string} options.className - Class name to apply to element on focus.
	 * @param {string} options.ignore - CSS selector for elements to not include.
	 */

	function bindInPageLinks(options) {
		options = options || {};
		var links = Array.prototype.slice.call(document.querySelectorAll('a[href^="#"]'));

		function queryCheck(selector) {
			return document.getElementById(selector) !== null;
		}

		return links.filter(function (link) {
			if (link.hash === '#' || link.hash === '') {
				return false;
			}

			if (options.ignore && link.matches(options.ignore)) {
				return false;
			}

			if (!queryCheck(link.hash.substr(1))) {
				return false;
			}

			var element = document.querySelector(link.hash);

			if (!element) {
				return false;
			}

			link.addEventListener('click', function () {
				forceFocus(element, options);
			});

			return true;
		});
	}

	function focusable(container) {
		var elements = Array.prototype.slice.call(
			container.querySelectorAll(
				'[tabindex],' +
					'[draggable],' +
					'a[href],' +
					'area,' +
					'button:enabled,' +
					'input:not([type=hidden]):enabled,' +
					'object,' +
					'select:enabled,' +
					'textarea:enabled'
			)
		);

		// Filter out elements that are not visible.
		// Copied from jQuery https://github.com/jquery/jquery/blob/2d4f53416e5f74fa98e0c1d66b6f3c285a12f0ce/src/css/hiddenVisibleSelectors.js
		return elements.filter(function (element) {
			return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
		});
	}

	/**
	 * Traps the focus in a particular container
	 *
	 * @param {Element} container - Container DOM element to trap focus inside of
	 * @param {Element} elementToFocus - Element to be focused on first
	 * @param {Object} options - Settings unique to your theme
	 * @param {string} options.className - Class name to apply to element on focus.
	 */

	var trapFocusHandlers = {};

	function trapFocus(container, options) {
		options = options || {};
		var elements = focusable(container);
		var elementToFocus = options.elementToFocus || container;
		var first = elements[0];
		var last = elements[elements.length - 1];

		removeTrapFocus();

		trapFocusHandlers.focusin = function (event) {
			if (container !== event.target && !container.contains(event.target)) {
				first.focus();
			}

			if (event.target !== container && event.target !== last && event.target !== first) return;
			document.addEventListener('keydown', trapFocusHandlers.keydown);
		};

		trapFocusHandlers.focusout = function () {
			document.removeEventListener('keydown', trapFocusHandlers.keydown);
		};

		trapFocusHandlers.keydown = function (event) {
			if (event.keyCode !== 9) return; // If not TAB key

			// On the last focusable element and tab forward, focus the first element.
			if (event.target === last && !event.shiftKey) {
				event.preventDefault();
				first.focus();
			}

			//  On the first focusable element and tab backward, focus the last element.
			if ((event.target === container || event.target === first) && event.shiftKey) {
				event.preventDefault();
				last.focus();
			}
		};

		document.addEventListener('focusout', trapFocusHandlers.focusout);
		document.addEventListener('focusin', trapFocusHandlers.focusin);

		forceFocus(elementToFocus, options);
	}

	/**
	 * Removes the trap of focus from the page
	 */
	function removeTrapFocus() {
		document.removeEventListener('focusin', trapFocusHandlers.focusin);
		document.removeEventListener('focusout', trapFocusHandlers.focusout);
		document.removeEventListener('keydown', trapFocusHandlers.keydown);
	}

	/**
	 * Add a preventive message to external links and links that open to a new window.
	 * @param {string} elements - Specific elements to be targeted
	 * @param {object} options.messages - Custom messages to overwrite with keys: newWindow, external, newWindowExternal
	 * @param {string} options.messages.newWindow - When the link opens in a new window (e.g. target="_blank")
	 * @param {string} options.messages.external - When the link is to a different host domain.
	 * @param {string} options.messages.newWindowExternal - When the link is to a different host domain and opens in a new window.
	 * @param {object} options.prefix - Prefix to namespace "id" of the messages
	 */
	function accessibleLinks(elements, options) {
		if (typeof elements !== 'string') {
			throw new TypeError(elements + ' is not a String.');
		}

		elements = document.querySelectorAll(elements);

		if (elements.length === 0) {
			return;
		}

		options = options || {};
		options.messages = options.messages || {};

		var messages = {
			newWindow: options.messages.newWindow || 'Opens in a new window.',
			external: options.messages.external || 'Opens external website.',
			newWindowExternal: options.messages.newWindowExternal || 'Opens external website in a new window.',
		};

		var prefix = options.prefix || 'a11y';

		var messageSelectors = {
			newWindow: prefix + '-new-window-message',
			external: prefix + '-external-message',
			newWindowExternal: prefix + '-new-window-external-message',
		};

		function generateHTML(messages) {
			var container = document.createElement('ul');
			var htmlMessages = Object.keys(messages).reduce(function (html, key) {
				return (html += '<li id=' + messageSelectors[key] + '>' + messages[key] + '</li>');
			}, '');

			container.setAttribute('hidden', true);
			container.innerHTML = htmlMessages;

			document.body.appendChild(container);
		}

		function externalSite(link) {
			return link.hostname !== window.location.hostname;
		}

		elements.forEach(function (link) {
			var target = link.getAttribute('target');
			var rel = link.getAttribute('rel');
			var isExternal = externalSite(link);
			var isTargetBlank = target === '_blank';
			var missingRelNoopener = rel === null || rel.indexOf('noopener') === -1;

			if (isTargetBlank && missingRelNoopener) {
				var relValue = rel === null ? 'noopener' : rel + ' noopener';
				link.setAttribute('rel', relValue);
			}

			if (isExternal && isTargetBlank) {
				link.setAttribute('aria-describedby', messageSelectors.newWindowExternal);
			} else if (isExternal) {
				link.setAttribute('aria-describedby', messageSelectors.external);
			} else if (isTargetBlank) {
				link.setAttribute('aria-describedby', messageSelectors.newWindow);
			}
		});

		generateHTML(messages);
	}

	var a11y = /*#__PURE__*/ Object.freeze({
		__proto__: null,
		forceFocus: forceFocus,
		focusHash: focusHash,
		bindInPageLinks: bindInPageLinks,
		focusable: focusable,
		trapFocus: trapFocus,
		removeTrapFocus: removeTrapFocus,
		accessibleLinks: accessibleLinks,
	});

	const slideDown = (target, duration = 500, checkHidden = true) => {
		let display = window.getComputedStyle(target).display;
		if (checkHidden && display !== 'none') {
			return;
		}
		target.style.removeProperty('display');
		if (display === 'none') display = 'block';
		target.style.display = display;
		let height = target.offsetHeight;
		target.style.overflow = 'hidden';
		target.style.height = 0;
		target.style.paddingTop = 0;
		target.style.paddingBottom = 0;
		target.style.marginTop = 0;
		target.style.marginBottom = 0;
		target.offsetHeight;
		target.style.boxSizing = 'border-box';
		target.style.transitionProperty = 'height, margin, padding';
		target.style.transitionDuration = duration + 'ms';
		target.style.height = height + 'px';
		target.style.removeProperty('padding-top');
		target.style.removeProperty('padding-bottom');
		target.style.removeProperty('margin-top');
		target.style.removeProperty('margin-bottom');
		window.setTimeout(() => {
			target.style.removeProperty('height');
			target.style.removeProperty('overflow');
			target.style.removeProperty('transition-duration');
			target.style.removeProperty('transition-property');
		}, duration);
	};

	const slideUp = (target, duration = 500) => {
		target.style.transitionProperty = 'height, margin, padding';
		target.style.transitionDuration = duration + 'ms';
		target.style.boxSizing = 'border-box';
		target.style.height = target.offsetHeight + 'px';
		target.offsetHeight;
		target.style.overflow = 'hidden';
		target.style.height = 0;
		target.style.paddingTop = 0;
		target.style.paddingBottom = 0;
		target.style.marginTop = 0;
		target.style.marginBottom = 0;
		window.setTimeout(() => {
			target.style.display = 'none';
			target.style.removeProperty('height');
			target.style.removeProperty('padding-top');
			target.style.removeProperty('padding-bottom');
			target.style.removeProperty('margin-top');
			target.style.removeProperty('margin-bottom');
			target.style.removeProperty('overflow');
			target.style.removeProperty('transition-duration');
			target.style.removeProperty('transition-property');
		}, duration);
	};

	const slideToggle = (target, duration = 500) => {
		if (window.getComputedStyle(target).display === 'none') {
			return slideDown(target, duration);
		} else {
			return slideUp(target, duration);
		}
	};

	const getSiblings = (el) => {
		return Array.prototype.filter.call(el.parentNode.children, function (child) {
			return child !== el;
		});
	};

	function FetchError(object) {
		this.status = object.status || null;
		this.headers = object.headers || null;
		this.json = object.json || null;
		this.body = object.body || null;
	}
	FetchError.prototype = Error.prototype;

	const selectors$1 = {
		quantityHolder: '[data-quantity-holder]',
		quantityField: '[data-quantity-field]',
		quantityButton: '[data-quantity-button]',
		quantityMinusButton: '[data-quantity-minus]',
		quantityPlusButton: '[data-quantity-plus]',
		quantityReadOnly: 'read-only',
		isDisabled: 'is-disabled',
	};

	class QuantityCounter {
		constructor(holder, inCart = false) {
			this.holder = holder;
			this.quantityUpdateCart = inCart;
		}

		init() {
			// Settings
			this.settings = selectors$1;

			// DOM Elements
			this.quantity = this.holder.querySelector(this.settings.quantityHolder);
			this.field = this.quantity.querySelector(this.settings.quantityField);
			this.buttons = this.quantity.querySelectorAll(this.settings.quantityButton);
			this.increaseButton = this.quantity.querySelector(this.settings.quantityPlusButton);

			// Set value or classes
			this.quantityValue = Number(this.field.value || 0);
			this.cartItemID = this.field.getAttribute('data-id');
			this.maxValue = Number(this.field.getAttribute('max')) > 0 ? Number(this.field.getAttribute('max')) : null;
			this.minValue = Number(this.field.getAttribute('min')) > 0 ? Number(this.field.getAttribute('min')) : 0;
			this.disableIncrease = this.disableIncrease.bind(this);

			// Flags
			this.emptyField = false;

			// Methods
			this.updateQuantity = this.updateQuantity.bind(this);
			this.decrease = this.decrease.bind(this);
			this.increase = this.increase.bind(this);

			this.disableIncrease();

			// Events
			if (!this.quantity.classList.contains(this.settings.quantityReadOnly)) {
				this.changeValueOnClick();
				this.changeValueOnInput();
			}
		}

		/**
		 * Change field value when click on quantity buttons
		 *
		 * @return  {Void}
		 */

		changeValueOnClick() {
			const that = this;

			this.buttons.forEach((element) => {
				element.addEventListener('click', (event) => {
					event.preventDefault();
					const clickedElement = event.target;
					const isDescrease =
						clickedElement.matches(that.settings.quantityMinusButton) || clickedElement.closest(that.settings.quantityMinusButton);
					const isIncrease =
						clickedElement.matches(that.settings.quantityPlusButton) || clickedElement.closest(that.settings.quantityPlusButton);

					if (isDescrease) {
						that.decrease();
					}

					if (isIncrease) {
						that.increase();
					}

					that.updateQuantity();
				});
			});
		}

		/**
		 * Change field value when input new value in a field
		 *
		 * @return  {Void}
		 */

		changeValueOnInput() {
			const that = this;

			this.field.addEventListener(
				'input',
				function () {
					that.quantityValue = this.value;

					if (this.value === '') {
						that.emptyField = true;
					}

					that.updateQuantity();
				},
				this
			);
		}

		/**
		 * Update field value
		 *
		 * @return  {Void}
		 */

		updateQuantity() {
			if (this.maxValue < this.quantityValue && this.maxValue !== null) {
				this.quantityValue = this.maxValue;
			}

			if (this.minValue > this.quantityValue) {
				this.quantityValue = this.minValue;
			}

			this.field.value = this.quantityValue;

			this.disableIncrease();

			if (this.quantityUpdateCart) {
				this.updateCart();
			}
		}

		/**
		 * Decrease value
		 *
		 * @return  {Void}
		 */

		decrease() {
			if (this.quantityValue > this.minValue) {
				this.quantityValue--;

				return;
			}

			this.quantityValue = 0;
		}

		/**
		 * Increase value
		 *
		 * @return  {Void}
		 */

		increase() {
			this.quantityValue++;
		}

		/**
		 * Disable increase
		 *
		 * @return  {[type]}  [return description]
		 */

		disableIncrease() {
			this.increaseButton
				? this.increaseButton.classList.toggle(this.settings.isDisabled, this.quantityValue >= this.maxValue && this.maxValue !== null)
				: '';
		}

		/**
		 * Update cart
		 *
		 * @return  {Void}
		 */

		updateCart() {
			const event = new CustomEvent('update-cart', {
				bubbles: true,
				detail: {
					id: this.cartItemID,
					quantity: this.quantityValue,
					valueIsEmpty: this.emptyField,
				},
			});

			this.holder.dispatchEvent(event);
		}
	}

	const settings = {
		cartDropdownIsEnable: window.theme.cartDropdownIsEnable,
		times: {
			timeoutAddProduct: 1000,
			closeDropdownAfter: 5000,
		},
		classes: {
			hidden: 'is-hidden',
			htmlClasses: 'has-open-cart-dropdown',
			open: 'is-open',
			active: 'is-active',
			visible: 'is-visible',
			loading: 'is-loading',
			disabled: 'is-disabled',
		},
		elements: {
			html: 'html',
			cartDropdown: '#cart-dropdown',
			emptyMessage: '[data-empty-message]',
			buttonHolder: '[data-foot-holder]',
			itemsHolder: '[data-items-holder]',
			item: '[data-item]',
			cartToggleElement: '[data-cart-toggle]',
			cartItemRemove: '[data-item-remove]',
			cartCount: '[data-cart-count]',
			cartCountValue: 'data-cart-count',
			clickedElementForExpanding: '[data-expand-button]',
			cartWidget: '[data-cart-widget]',
			cartTotal: '[data-cart-total]',
			cartMessage: '[data-cart-message]',
			buttonAddToCart: '[data-add-to-cart]',
			formErrorsContainer: '[data-cart-errors-container]',
			cartErrors: '[data-cart-errors]',
			cartCloseError: '[data-cart-error-close]',
			formCloseError: '[data-close-error]',
			quickModal: '[data-quickview-modal-global]',
			cartProgress: '[data-cart-progress]',
			cartOriginalTotal: '[data-cart-original-total]',
			cartOriginaTotalPrice: '[data-cart-original-total-price]',
			cartDiscountsHolder: '[data-cart-discounts-holder]',
		},
		formatMoney: theme.moneyFormat,
		cartTotalDiscountsTemplate: '[data-cart-total-discount]',
	};

	class CartDrawer {
		constructor() {
			this.init();
		}

		init() {
			this.settings = settings;

			// DOM Elements
			this.document = document;
			this.html = this.document.querySelector(this.settings.elements.html);
			this.cartDropdown = this.document.querySelector(this.settings.elements.cartDropdown);
			this.emptyMessage = this.document.querySelector(this.settings.elements.emptyMessage);
			this.buttonHolder = this.document.querySelector(this.settings.elements.buttonHolder);
			this.itemsHolder = this.document.querySelector(this.settings.elements.itemsHolder);
			this.items = this.document.querySelectorAll(this.settings.elements.item);
			this.counterHolders = this.document.querySelectorAll(this.settings.elements.cartCount);
			this.cartTotal = this.document.querySelector(this.settings.elements.cartTotal);
			this.cartMessage = this.document.querySelector(this.settings.elements.cartMessage);
			this.cartOriginalTotal = this.document.querySelector(this.settings.elements.cartOriginalTotal);
			this.cartOriginaTotalPrice = this.document.querySelector(this.settings.elements.cartOriginaTotalPrice);
			this.cartDiscountHolder = this.document.querySelector(this.settings.elements.cartDiscountsHolder);
			this.clickedElementForExpanding = this.document.querySelectorAll(this.settings.elements.clickedElementForExpanding);
			this.cartTotalDiscountTemplate = this.document.querySelector(this.settings.cartTotalDiscountsTemplate).innerHTML;
			this.cartErrorHolder = this.document.querySelector(this.settings.elements.cartErrors);
			this.cartCloseErrorMessage = this.document.querySelector(this.settings.elements.cartCloseError);
			this.accessibility = a11y;

			this.build = this.build.bind(this);

			// AJAX request
			this.addToCart = this.addToCart.bind(this);
			this.updateCart = this.updateCart.bind(this);

			// Cart events
			this.openCartDropdown = this.openCartDropdown.bind(this);
			this.closeCartDropdown = this.closeCartDropdown.bind(this);
			this.toggleCartDropdown = this.toggleCartDropdown.bind(this);

			// Checking
			this.hasItemsInCart = this.hasItemsInCart.bind(this);

			// Set classes
			this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

			// Flags
			this.cartDropdownIsBuilded = this.items.length > 0;
			this.totalItems = this.cartDropdownIsBuilded;
			this.cartDropdownIsOpen = false;
			this.cartDiscounts = 0;
			this.cartDropdownIsEnable = this.settings.cartDropdownIsEnable;
			this.cartLimitErrorIsHidden = true;

			this.html.style.setProperty('--scrollbar-width', `${window.innerWidth - this.html.clientWidth}px`);

			// Cart Events
			this.eventToggleCart();
			this.expandEvents();
			this.cartEvents();
			this.cartEventAdd();

			// Init quantity for fields
			this.initQuantity();

			// Init estimate shipping calculator
			this.estimateShippingCalculator();

			// Attributes
			if (this.cartMessage) {
				this.cartFreeLimitShipping = Number(this.cartMessage.getAttribute('data-limit')) * 100;
				this.subtotal = 0;
				this.progress = this.document.querySelector(this.settings.elements.cartProgress);
				this.circumference = 28 * Math.PI; // radius - stroke * 4 * PI

				this.setProgress(this.progress.getAttribute('data-percent'));
			}
		}

		/**
		 * Init quantity field functionality
		 *
		 * @return  {Void}
		 */

		initQuantity() {
			this.items = this.document.querySelectorAll(this.settings.elements.item);

			this.items.forEach((item) => {
				const initQuantity = new QuantityCounter(item, true);

				initQuantity.init();
				this.customEventsHandle(item);
			});
		}

		/**
		 * Expand blocks and close siblings
		 *
		 * @return  {Void}
		 */

		expandEvents() {
			const that = this;

			this.clickedElementForExpanding.forEach((item) => {
				item.addEventListener('click', function (event) {
					event.preventDefault();

					item.classList.toggle(that.settings.classes.active);
					slideToggle(that.document.querySelector(item.getAttribute('href')), 400);

					const otherCartWidget = getSiblings(item.closest(that.settings.elements.cartWidget)).filter((el) =>
						el.hasAttribute(that.settings.elements.cartWidget.replace('[', '').replace(']', ''))
					)[0];

					if (otherCartWidget) {
						const otherClickedElementForExpanding = otherCartWidget.querySelector(that.settings.elements.clickedElementForExpanding);

						otherClickedElementForExpanding.classList.remove(that.settings.classes.active);
						slideUp(otherClickedElementForExpanding.nextElementSibling, 400);
					}
				});
			});
		}

		/**
		 * Custom event who change the cart
		 *
		 * @return  {Void}
		 */

		customEventsHandle(holder) {
			holder.addEventListener(
				'update-cart',
				debounce((event) => {
					this.updateCart(
						{
							id: event.detail.id,
							quantity: event.detail.quantity,
						},
						holder,
						event.detail.valueIsEmpty
					);
				}, 500)
			);
		}

		/**
		 * Cart events
		 *
		 * @return  {Void}
		 */

		cartEvents() {
			const that = this;
			const cartItemRemove = this.document.querySelectorAll(that.settings.elements.cartItemRemove);

			cartItemRemove.forEach((item) => {
				item.addEventListener('click', function (event) {
					event.preventDefault();

					that.updateCart({
						id: this.getAttribute('data-id'),
						quantity: 0,
					});
				});
			});

			if (this.cartCloseErrorMessage) {
				this.cartCloseErrorMessage.addEventListener('click', (event) => {
					event.preventDefault();

					slideUp(this.cartErrorHolder, 400);
				});
			}
		}

		/**
		 * Cart event add product to cart
		 *
		 * @return  {Void}
		 */

		cartEventAdd() {
			if (!this.cartDropdownIsEnable) {
				return;
			}

			this.document.addEventListener('click', (event) => {
				const clickedElement = event.target;

				if (
					clickedElement.matches(this.settings.elements.buttonAddToCart) ||
					(clickedElement.closest(this.settings.elements.buttonAddToCart) && clickedElement)
				) {
					event.preventDefault();

					if (clickedElement.hasAttribute('disabled') || clickedElement.parentNode.hasAttribute('disabled')) {
						return;
					}

					const form = clickedElement.closest('form');
					const formData = new FormData(form);

					if (form.querySelector('[type="file"]')) {
						return;
					}

					const modal = clickedElement.closest(this.settings.elements.quickModal);

					this.addToCart(JSON.stringify(Object.fromEntries(formData)), modal);

					this.html.dispatchEvent(
						new CustomEvent('cart:add-item', {
							bubbles: true,
							detail: {
								selector:
									modal !== null
										? this.document.querySelector(
												`[data-trigger-quickview="${modal
													.querySelector('[data-product-handle]')
													.getAttribute('data-product-handle')}"]`
										  )
										: clickedElement,
							},
						})
					);
				}
			});
		}

		/**
		 * Estimate shippint calculator
		 *
		 * @return  {Void}
		 */

		estimateShippingCalculator() {
			Shopify.Cart.ShippingCalculator.show({
				submitButton: theme.strings.shippingCalcSubmitButton,
				submitButtonDisabled: theme.strings.shippingCalcSubmitButtonDisabled,
				customerIsLoggedIn: theme.strings.shippingCalcCustomerIsLoggedIn,
				moneyFormat: theme.strings.shippingCalcMoneyFormat,
			});
		}

		/**
		 * Get response from the cart
		 *
		 * @return  {Void}
		 */

		getCart() {
			fetch('/cart.js')
				.then(this.handleErrors)
				.then((response) => response.json())
				.then((response) => {
					this.updateCounter(response.item_count);
					this.newTotalItems = response.items.length;

					this.buildTotalPrice(response);
					this.freeShippingMessageHandle(response.total_price);

					if (this.cartMessage) {
						this.subtotal = response.total_price;
						this.updateProgress();
					}

					return fetch('/cart?view=items');
				})
				.then((response) => response.text())
				.then((response) => {
					this.build(response);
				})
				.catch((error) => console.log(error));
		}

		/**
		 * Add item(s) to the cart and show the added item(s)
		 *
		 * @param   {String}  data
		 * @param   {DOM Element}  modal
		 *
		 * @return  {Void}
		 */

		addToCart(data, modal = null) {
			fetch('/cart/add.js', {
				method: 'POST',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					'Content-Type': 'application/json',
				},
				body: data,
			})
				.then((response) => response.json())
				.then((response) => {
					if (response.status) {
						this.addToCartError(response, modal);

						return;
					}

					if (modal !== null) {
						setTimeout(() => {
							modal.innerHTML = '';
						}, this.settings.times.timeoutAddProduct);
					}

					this.getCart();

					setTimeout(() => {
						this.openCartDropdown();
						this.cartDropdownIsOpen = true;
					}, this.settings.times.timeoutAddProduct);
				})
				.catch((error) => console.log(error));
		}

		/**
		 * Update cart
		 *
		 * @param   {Object}  updateData
		 *
		 * @return  {Void}
		 */

		updateCart(updateData = {}, holder = null, valueIsEmpty = false) {
			let newCount = null;
			let oldCount = null;
			let newItem = null;

			if (holder !== null) {
				holder.closest(this.settings.elements.item).classList.add(this.settings.classes.loading);
			}

			this.items.forEach((item) => {
				item.classList.add(this.settings.classes.disabled);
				item.querySelector('input').blur();
			});

			fetch('/cart.js')
				.then(this.handleErrors)
				.then((response) => response.json())
				.then((response) => {
					const matchKeys = (item) => item.key === updateData.id;
					const index = response.items.findIndex(matchKeys);
					oldCount = response.item_count;
					newItem = response.items[index].title;

					const data = {
						line: `${index + 1}`,
						quantity: updateData.quantity,
					};

					return fetch('/cart/change.js', {
						method: 'post',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(data),
					});
				})
				.then(this.handleErrors)
				.then((response) => {
					return response.json();
				})
				.then((response) => {
					function delay(t, val) {
						return new Promise((resolve) => setTimeout(resolve, t, val));
					}

					const items = response.items;
					const personalizationProducts = items
						.filter((item) => item.handle === 'extra-personalizare')
						.map((el) => el.properties['Writting']);
					const personalizationProductsLong = items
						.filter((item) => item.handle === 'extra-personalizare')
						.map((el) => {
							const object = {};
							object[el.properties['Writting']] = el.quantity;
							return object;
						});

					if (personalizationProducts.length != 0) {
						let arrayOrVariants = [];
						const itemsWithWrittingPropertyExtra = items.filter(
							(item) => item.properties['Writting'] && item.handle != 'extra-personalizare' && item.properties['Extra Personalization']
						);

						if (itemsWithWrittingPropertyExtra.length == 0) {
							const personalizationProductsId = items.filter((item) => item.handle === 'extra-personalizare')[0].variant_id;
							const n = document.createElement('script');
							n.setAttribute('src', `https://nimba.ro/cart/update.js?updates[${personalizationProductsId}]=0`);
							document.body.appendChild(n);
						}

						itemsWithWrittingPropertyExtra.forEach((el) => {
							if (personalizationProducts.includes(el.properties['Writting'])) {
								const personalizationQuant = personalizationProductsLong.find((i) => i[el.properties['Writting']])[
									el.properties['Writting']
								];
								const elQuant = el.quantity;
								if (personalizationQuant != elQuant) {
									const personalizationProductsId = items.filter((item) => item.handle === 'extra-personalizare')[0].variant_id;
									const n = document.createElement('script');
									n.setAttribute('src', `https://nimba.ro/cart/update.js?updates[${personalizationProductsId}]=${elQuant}`);
									document.body.appendChild(n);
								}
							} else {
								const personalizationProductsId = items.filter((item) => item.handle === 'extra-personalizare')[0].variant_id;
								const n = document.createElement('script');
								n.setAttribute(
									'src',
									`https://nimba.ro/cart/add.js?id=[${personalizationProductsId}]&quanity=${elQuant}&properties[Writting]=${el.properties['Writting']}`
								);
								document.body.appendChild(n);
							}
						});
					}

					return delay(1000).then(function () {
						return fetch('/cart.js');
					});
				})
				.then(this.handleErrors)
				.then((response) => {
					return response.json();
				})
				.then((response) => {
					newCount = response.item_count;

					this.updateCounter(newCount);

					// Change the cart total and hide message if missing discounts and the changed product is not deleted
					this.buildTotalPrice(response);
					this.freeShippingMessageHandle(response.total_price);
					this.cartDiscounts = response.total_discount;

					// Build cart again if the quantity of the changed product is 0 or cart discounts are changed
					if (this.cartMessage) {
						this.subtotal = response.total_price;
						this.updateProgress();
					}

					if (valueIsEmpty) {
						newCount = 0;
					}

					this.cartLimitErrorIsHidden = newCount !== oldCount;

					this.toggleLimitError(newItem);

					this.getCart();
				})
				.catch((error) => console.log(error));
		}

		/**
		 * Show/hide limit error
		 *
		 * @param   {String}  itemTitle
		 *
		 * @return  {Void}
		 */

		toggleLimitError(itemTitle) {
			this.cartErrorHolder.querySelector('p').innerText = itemTitle;

			if (this.cartLimitErrorIsHidden) {
				slideUp(this.cartErrorHolder, 400);
			} else {
				slideDown(this.cartErrorHolder, 400);
			}
		}

		/**
		 * Handle errors
		 *
		 * @param   {Object}  response
		 *
		 * @return  {Object}
		 */

		handleErrors(response) {
			if (!response.ok) {
				return response.json().then(function (json) {
					const e = new FetchError({
						status: response.statusText,
						headers: response.headers,
						json: json,
					});
					throw e;
				});
			}
			return response;
		}

		/**
		 * Add to cart error handle
		 *
		 * @param   {Object}  data
		 * @param   {DOM Element/Null} modal
		 *
		 * @return  {Void}
		 */

		addToCartError(data, modal) {
			this.closeCartDropdown();
			let errorContainer = this.document.querySelector(this.settings.elements.formErrorsContainer);

			if (modal !== null) {
				errorContainer = modal.querySelector(this.settings.elements.formErrorsContainer);
			}

			errorContainer.innerHTML = `<div class="errors">${data.message}: ${data.description}<span class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close-thin" viewBox="0 0 27 27"><g stroke="#979797" fill="none" fill-rule="evenodd" stroke-linecap="square"><path d="M.5.5l26 26M26.5.5l-26 26"></path></g></svg></span></div>`;

			errorContainer.classList.add(this.settings.classes.visible);

			this.document.addEventListener('click', (event) => {
				const clickedElement = event.target;

				if (clickedElement.matches(this.settings.elements.formCloseError) || clickedElement.closest(this.settings.elements.formCloseError)) {
					event.preventDefault();

					errorContainer.classList.remove(this.settings.classes.visible);
				}
			});
		}

		/**
		 * Open cart dropdown and add class on body
		 *
		 * @return  {Void}
		 */

		openCartDropdown() {
			this.html.classList.add(this.settings.classes.htmlClasses);
			document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
			this.cartDropdown.classList.add(this.settings.classes.open);

			this.accessibility.removeTrapFocus();
			this.accessibility.trapFocus(this.cartDropdown, {
				elementToFocus: this.cartDropdown.querySelector('a:first-child, input:first-child'),
			});

			this.html.dispatchEvent(
				new CustomEvent('cart:open', {
					bubbles: false,
				})
			);

			if (!this.cartDropdownIsBuilded) {
				this.getCart();
			}
		}

		/**
		 * Close cart dropdown and remove class on body
		 *
		 * @return  {Void}
		 */

		closeCartDropdown() {
			this.accessibility.removeTrapFocus();

			if (this.html.classList.contains('is-focused')) {
				const button = this.document.querySelector(`${this.settings.elements.cartToggleElement}[data-focus-element]`);

				setTimeout(() => {
					button.focus();
				}, 200);
			}

			this.html.classList.remove(this.settings.classes.htmlClasses);
			this.cartDropdown.classList.remove(this.settings.classes.open);
			document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
		}

		/**
		 * Toggle cart dropdown
		 *
		 * @return  {Void}
		 */

		toggleCartDropdown() {
			this.cartDropdownIsOpen = !this.cartDropdownIsOpen;
			this.cartDropdownIsOpen ? this.openCartDropdown() : this.closeCartDropdown();
		}

		/**
		 * Event click to element to open cart dropdown
		 *
		 * @return  {Void}
		 */

		eventToggleCart() {
			this.document.addEventListener('click', (event) => {
				const clickedElement = event.target;
				const isNotCartButton = !(
					clickedElement.matches(this.settings.elements.cartToggleElement) ||
					clickedElement.closest(this.settings.elements.cartToggleElement)
				);
				const isNotCartDropdownOrCartDropdownChild = !(
					clickedElement.matches(this.settings.elements.cartDropdown) || clickedElement.closest(this.settings.elements.cartDropdown)
				);

				if (
					clickedElement.matches(this.settings.elements.cartToggleElement) ||
					clickedElement.closest(this.settings.elements.cartToggleElement)
				) {
					event.preventDefault();

					this.toggleCartDropdown();
				} else if (this.cartDropdownIsOpen && isNotCartButton && isNotCartDropdownOrCartDropdownChild) {
					this.cartDropdownIsOpen = false;
					this.closeCartDropdown();
				}
			});
		}

		/**
		 * Toggle classes on different containers and messages
		 *
		 * @return  {Void}
		 */

		toggleClassesOnContainers() {
			const that = this;

			this.emptyMessage.classList.toggle(that.settings.classes.hidden, that.hasItemsInCart());
			this.buttonHolder.classList.toggle(that.settings.classes.hidden, !that.hasItemsInCart());
			this.itemsHolder.classList.toggle(that.settings.classes.hidden, !that.hasItemsInCart());
		}

		/**
		 * Build cart depends on results
		 *
		 * @param   {Object}  data
		 *
		 * @return  {Void}
		 */

		build(data) {
			if (this.totalItems !== this.newTotalItems) {
				this.totalItems = this.newTotalItems;

				this.toggleClassesOnContainers();
			}

			this.itemsHolder.innerHTML = data;

			this.cartEvents();
			this.initQuantity();
		}

		/**
		 * Update cart count
		 *
		 * @param   {Number}  countItems
		 *
		 * @return  {Void}
		 */

		updateCounter(countItems) {
			if (!this.counterHolders.length) {
				return;
			}

			this.counterHolders.forEach((holder) => {
				holder.innerHTML = countItems;
				holder.setAttribute(settings.elements.cartCountValue, countItems);
			});
		}

		/**
		 * Check for items in the cart
		 *
		 * @return  {Void}
		 */

		hasItemsInCart() {
			return this.totalItems > 0;
		}

		/**
		 * Build total cart total price
		 *
		 * @param   {Object}  data
		 *
		 * @return  {Void}
		 */

		buildTotalPrice(data) {
			if (data.original_total_price > data.total_price && data.cart_level_discount_applications.length > 0) {
				this.cartOriginalTotal.classList.remove(this.settings.classes.hidden);
				this.cartOriginaTotalPrice.innerHTML = themeCurrency.formatMoney(data.original_total_price, this.settings.formatMoney);
			} else {
				this.cartOriginalTotal.classList.add(this.settings.classes.hidden);
			}

			this.cartTotal.innerHTML = themeCurrency.formatMoney(data.total_price, this.settings.formatMoney);

			if (data.cart_level_discount_applications.length > 0) {
				const discountsMarkup = this.buildCartTotalDiscounts(data.cart_level_discount_applications);

				this.cartDiscountHolder.classList.remove(this.settings.classes.hidden);
				this.cartDiscountHolder.innerHTML = discountsMarkup;
			} else {
				this.cartDiscountHolder.classList.add(this.settings.classes.hidden);
			}
		}

		/**
		 * Build cart total discounts
		 *
		 * @param   {Array}  discounts
		 *
		 * @return  {String}
		 */

		buildCartTotalDiscounts(discounts) {
			let discountMarkup = '';

			discounts.forEach((discount) => {
				discountMarkup += Sqrl.render(this.cartTotalDiscountTemplate, {
					discount_title: discount.title,
					discount_total_allocated_amount: themeCurrency.formatMoney(discount.total_allocated_amount, this.settings.formatMoney),
				});
			});

			return discountMarkup;
		}

		/**
		 * Show/hide free shipping message
		 *
		 * @param   {Number}  total
		 *
		 * @return  {Void}
		 */

		freeShippingMessageHandle(total) {
			if (this.cartMessage) {
				this.cartMessage.classList.toggle(this.settings.classes.hidden, total >= this.cartFreeLimitShipping || total === 0);
			}
		}

		/**
		 * Set circle progress
		 *
		 * @param   {Number}  percent
		 *
		 * @return  {Void}
		 */

		setProgress(percent) {
			const offset = this.circumference - ((percent / 100) * this.circumference) / 2;

			this.progress.style.strokeDashoffset = offset;
		}

		/**
		 * Update progress when update cart
		 *
		 * @return  {Void}  [return description]
		 */

		updateProgress() {
			const newPercentValue = (this.subtotal / this.cartFreeLimitShipping) * 100;

			this.setProgress(newPercentValue > 100 ? 100 : newPercentValue);
		}
	}

	window.cart = new CartDrawer();

	const settings$1 = {
		elements: {
			html: 'html',
			body: 'body',
			inPageLink: '[data-skip-content]',
			linkesWithOnlyHash: 'a[href="#"]',
			triggerFocusElement: '[data-focus-element]',
			cartDropdown: '#cart-dropdown',
			modal: '[data-quickview-modal-global]',
			search: '#search-popdown',
			accordionContent: '.accordion-content',
			accordionIcon: '.accordion__icon',
			tabs: '.tabs',
		},
		classes: {
			focus: 'is-focused',
			classRotate: 'rotate-90',
			accordionToggle: 'accordion-toggle',
			tabLink: 'tab-link',
		},
		keysCodes: {
			escapeCode: 27,
			tabCode: 9,
			enterCode: 13,
			spaceCode: 32,
		},
	};

	class Accessibility {
		constructor() {
			this.init();
		}

		init() {
			this.settings = settings$1;
			this.window = window;
			this.document = document;
			this.a11y = a11y;
			this.cart = this.window.cart;

			// DOM Elements
			this.inPageLink = this.document.querySelector(this.settings.elements.inPageLink);
			this.linkesWithOnlyHash = this.document.querySelectorAll(this.settings.elements.linkesWithOnlyHash);
			this.html = this.document.querySelector(this.settings.elements.html);
			this.body = this.document.querySelector(this.settings.elements.body);
			this.modal = this.document.querySelector(this.settings.elements.modal);
			this.cartDropdown = this.document.querySelector(this.settings.elements.cartDropdown);
			this.lastFocused = null;

			// Flags
			this.isFocused = false;

			// A11Y init methods
			this.a11y.focusHash();
			this.a11y.bindInPageLinks();

			// Events
			this.clickEvents();
			this.focusEvents();
			this.focusEventsOff();
			this.closeExpandedElements();
		}

		/**
		 * Clicked events accessibility
		 *
		 * @return  {Void}
		 */

		clickEvents() {
			if (this.inPageLink) {
				this.inPageLink.addEventListener('click', (event) => {
					event.preventDefault();
				});
			}

			if (this.linkesWithOnlyHash) {
				this.linkesWithOnlyHash.forEach((item) => {
					item.addEventListener('click', (event) => {
						event.preventDefault();
					});
				});
			}
		}

		/**
		 * Focus events
		 *
		 * @return  {Void}
		 */

		focusEvents() {
			this.document.addEventListener('keyup', (event) => {
				if (event.keyCode !== this.settings.keysCodes.tabCode) {
					return;
				}

				this.body.classList.add(this.settings.classes.focus);
				this.isFocused = true;
			});

			// Expand modals
			this.document.addEventListener('keyup', (event) => {
				if (!this.isFocused) {
					return;
				}

				const target = event.target;
				const pressEnterOrSpace = event.keyCode === this.settings.keysCodes.enterCode || event.keyCode === this.settings.keysCodes.spaceCode;
				const targetElement =
					target.matches(this.settings.elements.triggerFocusElement) || target.closest(this.settings.elements.triggerFocusElement);
				const isAccordion =
					target.classList.contains(this.settings.classes.accordionToggle) ||
					target.parentNode.classList.contains(this.settings.classes.accordionToggle);
				const isTab =
					target.classList.contains(this.settings.classes.tabLink) || target.parentNode.classList.contains(this.settings.classes.tabLink);

				const isQuickViewModal =
					target.hasAttribute('data-trigger-quickview') ||
					(target.closest(this.settings.elements.triggerFocusElement) &&
						target.closest(this.settings.elements.triggerFocusElement).hasAttribute('data-trigger-quickview'));

				const isSearchModal =
					target.hasAttribute('data-popdown-toggle') ||
					(target.closest(this.settings.elements.triggerFocusElement) &&
						target.closest(this.settings.elements.triggerFocusElement).hasAttribute('data-popdown-toggle'));

				if (pressEnterOrSpace && targetElement) {
					if (this.lastFocused === null) {
						this.lastFocused = target;
					}

					let container = this.document.querySelector(this.settings.elements.cartDropdown);

					if (isQuickViewModal) {
						container = this.document.querySelector(this.settings.elements.modal);

						target.click();
					}

					if (isSearchModal) {
						container = this.document.querySelector(this.settings.elements.search);
					}

					if (isAccordion) {
						container = target.nextElementSibling;
						target.click();
					}

					if (isTab) {
						const selector = `.tab-content-${target.getAttribute('data-tab')}`;

						container = this.document.querySelector(selector);
						target.click();
					}

					if (isQuickViewModal && container.querySelectorAll('a, input')) {
						this.modal.addEventListener('modal-is-loaded', () => {
							this.a11y.trapFocus(container, {
								elementToFocus: container.querySelector('a:first-child, input:first-child'),
							});
						});
					} else if (container.querySelector('a, input')) {
						this.a11y.trapFocus(container, {
							elementToFocus: container.querySelector('a:first-child, input:first-child'),
						});
					}
				}
			});

			// Focus addToCart button or quickview button
			this.html.addEventListener('cart:add-item', (event) => {
				this.lastFocused = event.detail.selector;
			});
		}

		/**
		 * Focus events off
		 *
		 * @return  {Void}
		 */

		focusEventsOff() {
			this.document.addEventListener('mousedown', () => {
				this.body.classList.remove(this.settings.classes.focus);
				this.isFocused = false;
			});
		}

		/**
		 * Close expanded elements with when press escape
		 *
		 * @return  {Void}
		 */

		closeExpandedElements() {
			document.addEventListener('keyup', (event) => {
				if (event.keyCode !== this.settings.keysCodes.escapeCode) {
					return;
				}

				this.a11y.removeTrapFocus();

				if (this.html.classList.contains(this.cart.settings.classes.htmlClasses)) {
					this.cart.toggleCartDropdown();
					this.html.classList.remove(this.cart.settings.classes.htmlClasses);
					this.cartDropdown.classList.remove(this.cart.settings.classes.open);
				}

				if (this.modal.innerHTML !== '') {
					setTimeout(() => {
						this.modal.innerHTML = '';
					}, 1000);
				}

				const accordionContents = document.querySelectorAll(this.settings.elements.accordionContent);

				if (accordionContents.length) {
					for (let i = 0; i < accordionContents.length; i++) {
						if (accordionContents[i].style.display !== 'block') {
							continue;
						}

						const accordionArrow = accordionContents[i].previousElementSibling.querySelector(this.settings.elements.accordionIcon);
						accordionArrow.classList.remove(this.settings.classes.classRotate);

						slideUp(accordionContents[i]);
					}
				}

				if (this.lastFocused !== null) {
					setTimeout(() => {
						this.lastFocused.focus();
						this.lastFocused = null;
					}, 600);
				}
			});
		}
	}

	window.accessibility = new Accessibility();

	window.isYoutubeAPILoaded = false;
	function loadYoutubeAPI() {
		if (window.isYoutubeAPILoaded);
		else {
			// Load Youtube API script
			var tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';
			var firstScriptTag = document.getElementsByTagName('script')[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
		}
	}
	function onYouTubeIframeAPIReady() {
		window.isYoutubeAPILoaded = true;
		document.body.dispatchEvent(new CustomEvent('youtubeAPIReady'));
	}
	window.isPlyrLoaded = false;
	function loadPlyr() {
		if (window.isPlyrLoaded);
		else {
			// Load Youtube API script
			try {
				window.Shopify.loadFeatures(
					[
						{
							name: 'video-ui',
							version: '1.0',
							onLoad: handleShopifyXRLoadError,
						},
					],
					plyrLoaded
				);
			} catch (error) {
				console.log('An error occured while loading the Shopify Video interface');
				throw error;
			}
		}
	}
	function plyrLoaded() {
		window.isPlyrLoaded = true;
		document.body.dispatchEvent(new CustomEvent('plyrReady'));
	}
	function handleShopifyXRLoadError(error) {
		if (error) {
			throw error;
		}
	}
	window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
	window.loadYoutubeAPI = loadYoutubeAPI;
	window.loadPlyr = loadPlyr;

	theme.ProductModel = (function () {
		let modelJsonSections = {};
		let models = {};
		let xrButtons = {};
		const selectors = {
			productMediaWrapper: '[data-product-single-media-wrapper]',
			productSlideshow: '[data-product-slideshow]',
			productXr: '[data-shopify-xr]',
			dataMediaId: 'data-media-id',
			dataModelId: 'data-model-id',
			dataModel3d: 'data-shopify-model3d-id',
			modelViewer: 'model-viewer',
			modelJson: '#ModelJson-',
			classMediaHidden: 'media--hidden',
		};

		function init(modelViewerContainer, sectionId) {
			modelJsonSections[sectionId] = {
				loaded: false,
			};

			const mediaId = modelViewerContainer.getAttribute(selectors.dataMediaId);
			const modelViewerElement = modelViewerContainer.querySelector(selectors.modelViewer);
			const modelId = modelViewerElement.getAttribute(selectors.dataModelId);
			const xrButton = modelViewerContainer.closest(selectors.productSlideshow).parentElement.querySelector(selectors.productXr);
			xrButtons[sectionId] = {
				$element: xrButton,
				defaultId: modelId,
			};

			models[mediaId] = {
				modelId: modelId,
				mediaId: mediaId,
				sectionId: sectionId,
				$container: modelViewerContainer,
				$element: modelViewerElement,
			};

			window.Shopify.loadFeatures([
				{
					name: 'shopify-xr',
					version: '1.0',
					onLoad: setupShopifyXr,
				},
				{
					name: 'model-viewer-ui',
					version: '1.0',
					onLoad: setupModelViewerUi,
				},
			]);
		}

		function setupShopifyXr(errors) {
			if (errors) {
				console.warn(errors);
				return;
			}
			if (!window.ShopifyXR) {
				document.addEventListener('shopify_xr_initialized', function () {
					setupShopifyXr();
				});
				return;
			}

			for (const sectionId in modelJsonSections) {
				if (modelJsonSections.hasOwnProperty(sectionId)) {
					const modelSection = modelJsonSections[sectionId];
					if (modelSection.loaded) continue;

					const modelJson = document.querySelector(`${selectors.modelJson}${sectionId}`);
					if (modelJson) {
						window.ShopifyXR.addModels(JSON.parse(modelJson.innerHTML));
						modelSection.loaded = true;
					}
				}
			}
			window.ShopifyXR.setupXRElements();
		}

		function setupModelViewerUi(errors) {
			if (errors) {
				console.warn(errors);
				return;
			}

			for (const key in models) {
				if (models.hasOwnProperty(key)) {
					const model = models[key];
					if (!model.modelViewerUi) {
						model.modelViewerUi = new Shopify.ModelViewerUI(model.$element);
					}
					setupModelViewerListeners(model);
				}
			}
		}

		function setupModelViewerListeners(model) {
			const xrButton = xrButtons[model.sectionId];

			model.$container.addEventListener('mediaVisible', function () {
				xrButton.$element.setAttribute(selectors.dataModel3d, model.modelId);

				pauseOtherMedia(model.mediaId);

				if (window.theme.touched) return;
				model.modelViewerUi.play();
			});

			model.$container.addEventListener('mediaHidden', function () {
				xrButton.$element.setAttribute(selectors.dataModel3d, xrButton.defaultId);
				model.modelViewerUi.pause();
			});

			model.$container.addEventListener('xrLaunch', function () {
				model.modelViewerUi.pause();
			});

			model.$element.addEventListener('shopify_model_viewer_ui_toggle_play', function () {
				pauseOtherMedia(model.mediaId);
			});
		}

		function pauseOtherMedia(mediaId) {
			const mediaIdString = `[${selectors.dataMediaId}="${mediaId}"]`;
			const currentMedia = document.querySelector(`${selectors.productMediaWrapper}${mediaIdString}`);
			const otherMedia = document.querySelectorAll(`${selectors.productMediaWrapper}:not(${mediaIdString})`);

			currentMedia.classList.remove(selectors.classMediaHidden);
			if (otherMedia.length) {
				otherMedia.forEach((element) => {
					element.dispatchEvent(new CustomEvent('mediaHidden'));
					element.classList.add(selectors.classMediaHidden);
				});
			}
		}

		function removeSectionModels(sectionId) {
			for (const key in models) {
				if (models.hasOwnProperty(key)) {
					const model = models[key];
					if (model.sectionId === sectionId) {
						delete models[key];
					}
				}
			}
			delete modelJsonSections[sectionId];
			delete theme.mediaInstances[sectionId];
		}

		return {
			init: init,
			removeSectionModels: removeSectionModels,
		};
	})();

	const selectors$2 = {
		templateAddresses: '.template-addresses',
		addressNewForm: '#AddressNewForm',
		btnNew: '.address-new-toggle',
		btnEdit: '.address-edit-toggle',
		btnDelete: '.address-delete',
		classHide: 'hide',
		dataFormId: 'data-form-id',
		dataConfirmMessage: 'data-confirm-message',
		defaultConfirmMessage: 'Are you sure you wish to delete this address?',
		editAddress: '#EditAddress',
		addressCountryNew: 'AddressCountryNew',
		addressProvinceNew: 'AddressProvinceNew',
		addressProvinceContainerNew: 'AddressProvinceContainerNew',
		addressCountryOption: '.address-country-option',
		addressCountry: 'AddressCountry',
		addressProvince: 'AddressProvince',
		addressProvinceContainer: 'AddressProvinceContainer',
	};

	class Addresses {
		constructor(section) {
			this.section = section;
			this.addressNewForm = this.section.querySelector(selectors$2.addressNewForm);

			this.init();
		}

		init() {
			if (this.addressNewForm) {
				const section = this.section;
				const newAddressForm = this.addressNewForm;
				this.customerAddresses();

				const newButtons = section.querySelectorAll(selectors$2.btnNew);
				if (newButtons.length) {
					newButtons.forEach((element) => {
						element.addEventListener('click', function () {
							newAddressForm.classList.toggle(selectors$2.classHide);
						});
					});
				}

				const editButtons = section.querySelectorAll(selectors$2.btnEdit);
				if (editButtons.length) {
					editButtons.forEach((element) => {
						element.addEventListener('click', function () {
							const formId = this.getAttribute(selectors$2.dataFormId);
							section.querySelector(`${selectors$2.editAddress}_${formId}`).classList.toggle(selectors$2.classHide);
						});
					});
				}

				const deleteButtons = section.querySelectorAll(selectors$2.btnDelete);
				if (deleteButtons.length) {
					deleteButtons.forEach((element) => {
						element.addEventListener('click', function () {
							const formId = this.getAttribute(selectors$2.dataFormId);
							const confirmMessage = this.getAttribute(selectors$2.dataConfirmMessage);
							if (confirm(confirmMessage || selectors$2.defaultConfirmMessage)) {
								Shopify.postLink('/account/addresses/' + formId, { parameters: { _method: 'delete' } });
							}
						});
					});
				}
			}
		}

		customerAddresses() {
			// Initialize observers on address selectors, defined in shopify_common.js
			if (Shopify.CountryProvinceSelector) {
				new Shopify.CountryProvinceSelector(selectors$2.addressCountryNew, selectors$2.addressProvinceNew, {
					hideElement: selectors$2.addressProvinceContainerNew,
				});
			}

			// Initialize each edit form's country/province selector
			const countryOptions = this.section.querySelectorAll(selectors$2.addressCountryOption);
			countryOptions.forEach((element) => {
				const formId = element.getAttribute(selectors$2.dataFormId);
				const countrySelector = `${selectors$2.addressCountry}_${formId}`;
				const provinceSelector = `${selectors$2.addressProvince}_${formId}`;
				const containerSelector = `${selectors$2.addressProvinceContainer}_${formId}`;

				new Shopify.CountryProvinceSelector(countrySelector, provinceSelector, {
					hideElement: containerSelector,
				});
			});
		}
	}

	const template = document.querySelector(selectors$2.templateAddresses);
	if (template) {
		new Addresses(template);
	}

	const selectors$3 = {
		accountTemplateLogged: '.customer-logged-in',
		account: '.account',
		accountSidebarMobile: '.account-sidebar--mobile',
	};

	class Account {
		constructor(section) {
			this.section = section;

			this.init();
		}

		init() {
			if (this.section.querySelector(selectors$3.account)) {
				this.accountMobileSidebar();
			}
		}

		accountMobileSidebar() {
			this.section.querySelector(selectors$3.accountSidebarMobile).addEventListener('click', function () {
				const nextElem = this.nextElementSibling;

				if (nextElem && nextElem.tagName === 'UL') {
					nextElem.classList.toggle('visible');
				}
			});
		}
	}

	const template$1 = document.querySelector(selectors$3.accountTemplateLogged);
	if (template$1) {
		new Account(template$1);
	}

	const selectors$4 = {
		form: '[data-account-form]',
		showReset: '[data-show-reset]',
		hideReset: '[data-hide-reset]',
		recover: '[data-recover-password]',
		login: '[data-login-form]',
		recoverHash: '#recover',
		hideClass: 'is-hidden',
	};

	class Login {
		constructor(form) {
			this.form = form;
			this.showButton = form.querySelector(selectors$4.showReset);
			this.hideButton = form.querySelector(selectors$4.hideReset);
			this.recover = form.querySelector(selectors$4.recover);
			this.login = form.querySelector(selectors$4.login);
			this.init();
		}

		init() {
			if (window.location.hash == selectors$4.recoverHash) {
				this.showRecoverPasswordForm();
			} else {
				this.hideRecoverPasswordForm();
			}
			this.showButton.addEventListener(
				'click',
				function (e) {
					e.preventDefault();
					this.showRecoverPasswordForm();
				}.bind(this),
				false
			);
			this.hideButton.addEventListener(
				'click',
				function (e) {
					e.preventDefault();
					this.hideRecoverPasswordForm();
				}.bind(this),
				false
			);
		}

		showRecoverPasswordForm() {
			this.login.classList.add(selectors$4.hideClass);
			this.recover.classList.remove(selectors$4.hideClass);
			window.location.hash = selectors$4.recoverHash;
			return false;
		}

		hideRecoverPasswordForm() {
			this.recover.classList.add(selectors$4.hideClass);
			this.login.classList.remove(selectors$4.hideClass);
			window.location.hash = '';
			return false;
		}
	}

	const loginForm = document.querySelector(selectors$4.form);
	if (loginForm) {
		new Login(loginForm);
	}

	/* eslint-disable */

	var SECTION_ID_ATTR = 'data-section-id';

	function Section(container, properties) {
		this.container = validateContainerElement(container);
		this.id = container.getAttribute(SECTION_ID_ATTR);
		this.extensions = [];

		// eslint-disable-next-line es5/no-es6-static-methods
		Object.assign(this, validatePropertiesObject(properties));

		try {
			this.onLoad();
		} catch (e) {
			console.warn(`Error in section: ${this.id}`);
			console.warn(this);
			console.warn(e);
		}
	}

	Section.prototype = {
		onLoad: Function.prototype,
		onUnload: Function.prototype,
		onSelect: Function.prototype,
		onDeselect: Function.prototype,
		onBlockSelect: Function.prototype,
		onBlockDeselect: Function.prototype,

		extend: function extend(extension) {
			this.extensions.push(extension); // Save original extension

			// eslint-disable-next-line es5/no-es6-static-methods
			var extensionClone = Object.assign({}, extension);
			delete extensionClone.init; // Remove init function before assigning extension properties

			// eslint-disable-next-line es5/no-es6-static-methods
			Object.assign(this, extensionClone);

			if (typeof extension.init === 'function') {
				extension.init.apply(this);
			}
		},
	};

	function validateContainerElement(container) {
		if (!(container instanceof Element)) {
			throw new TypeError('Theme Sections: Attempted to load section. The section container provided is not a DOM element.');
		}
		if (container.getAttribute(SECTION_ID_ATTR) === null) {
			throw new Error('Theme Sections: The section container provided does not have an id assigned to the ' + SECTION_ID_ATTR + ' attribute.');
		}

		return container;
	}

	function validatePropertiesObject(value) {
		if ((typeof value !== 'undefined' && typeof value !== 'object') || value === null) {
			throw new TypeError('Theme Sections: The properties object provided is not a valid');
		}

		return value;
	}

	// Object.assign() polyfill from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#Polyfill
	if (typeof Object.assign != 'function') {
		// Must be writable: true, enumerable: false, configurable: true
		Object.defineProperty(Object, 'assign', {
			value: function assign(target) {
				if (target == null) {
					// TypeError if undefined or null
					throw new TypeError('Cannot convert undefined or null to object');
				}

				var to = Object(target);

				for (var index = 1; index < arguments.length; index++) {
					var nextSource = arguments[index];

					if (nextSource != null) {
						// Skip over if undefined or null
						for (var nextKey in nextSource) {
							// Avoid bugs when hasOwnProperty is shadowed
							if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
								to[nextKey] = nextSource[nextKey];
							}
						}
					}
				}
				return to;
			},
			writable: true,
			configurable: true,
		});
	}

	/*
	 * @shopify/theme-sections
	 * -----------------------------------------------------------------------------
	 *
	 * A framework to provide structure to your Shopify sections and a load and unload
	 * lifecycle. The lifecycle is automatically connected to theme editor events so
	 * that your sections load and unload as the editor changes the content and
	 * settings of your sections.
	 */

	var SECTION_TYPE_ATTR = 'data-section-type';
	var SECTION_ID_ATTR$1 = 'data-section-id';

	window.Shopify = window.Shopify || {};
	window.Shopify.theme = window.Shopify.theme || {};
	window.Shopify.theme.sections = window.Shopify.theme.sections || {};

	var registered = (window.Shopify.theme.sections.registered = window.Shopify.theme.sections.registered || {});
	var instances = (window.Shopify.theme.sections.instances = window.Shopify.theme.sections.instances || []);

	function register(type, properties) {
		if (typeof type !== 'string') {
			throw new TypeError(
				'Theme Sections: The first argument for .register must be a string that specifies the type of the section being registered'
			);
		}

		if (typeof registered[type] !== 'undefined') {
			throw new Error(
				'Theme Sections: A section of type "' + type + '" has already been registered. You cannot register the same section type twice'
			);
		}

		if (!Array.isArray(properties)) {
			properties = [properties];
		}

		function TypedSection(container) {
			for (var i = 0; i < properties.length; i++) {
				Section.call(this, container, properties[i]);
			}
		}

		TypedSection.constructor = Section;
		TypedSection.prototype = Object.create(Section.prototype);
		TypedSection.prototype.type = type;

		return (registered[type] = TypedSection);
	}

	function load(types, containers) {
		types = normalizeType(types);

		if (typeof containers === 'undefined') {
			containers = document.querySelectorAll('[' + SECTION_TYPE_ATTR + ']');
		}

		containers = normalizeContainers(containers);

		types.forEach(function (type) {
			var TypedSection = registered[type];

			if (typeof TypedSection === 'undefined') {
				return;
			}

			containers = containers.filter(function (container) {
				// Filter from list of containers because container already has an instance loaded
				if (isInstance(container)) {
					return false;
				}

				// Filter from list of containers because container doesn't have data-section-type attribute
				if (container.getAttribute(SECTION_TYPE_ATTR) === null) {
					return false;
				}

				// Keep in list of containers because current type doesn't match
				if (container.getAttribute(SECTION_TYPE_ATTR) !== type) {
					return true;
				}

				instances.push(new TypedSection(container));

				// Filter from list of containers because container now has an instance loaded
				return false;
			});
		});
	}

	function unload(selector) {
		var instancesToUnload = getInstances(selector);

		instancesToUnload.forEach(function (instance) {
			var index = instances
				.map(function (e) {
					return e.id;
				})
				.indexOf(instance.id);
			instances.splice(index, 1);
			instance.onUnload();
		});
	}

	function getInstances(selector) {
		var filteredInstances = [];

		// Fetch first element if its an array
		if (NodeList.prototype.isPrototypeOf(selector) || Array.isArray(selector)) {
			var firstElement = selector[0];
		}

		// If selector element is DOM element
		if (selector instanceof Element || firstElement instanceof Element) {
			var containers = normalizeContainers(selector);

			containers.forEach(function (container) {
				filteredInstances = filteredInstances.concat(
					instances.filter(function (instance) {
						return instance.container === container;
					})
				);
			});

			// If select is type string
		} else if (typeof selector === 'string' || typeof firstElement === 'string') {
			var types = normalizeType(selector);

			types.forEach(function (type) {
				filteredInstances = filteredInstances.concat(
					instances.filter(function (instance) {
						return instance.type === type;
					})
				);
			});
		}

		return filteredInstances;
	}

	function getInstanceById(id) {
		var instance;

		for (var i = 0; i < instances.length; i++) {
			if (instances[i].id === id) {
				instance = instances[i];
				break;
			}
		}
		return instance;
	}

	function isInstance(selector) {
		return getInstances(selector).length > 0;
	}

	function normalizeType(types) {
		// If '*' then fetch all registered section types
		if (types === '*') {
			types = Object.keys(registered);

			// If a single section type string is passed, put it in an array
		} else if (typeof types === 'string') {
			types = [types];

			// If single section constructor is passed, transform to array with section
			// type string
		} else if (types.constructor === Section) {
			types = [types.prototype.type];

			// If array of typed section constructors is passed, transform the array to
			// type strings
		} else if (Array.isArray(types) && types[0].constructor === Section) {
			types = types.map(function (TypedSection) {
				return TypedSection.prototype.type;
			});
		}

		types = types.map(function (type) {
			return type.toLowerCase();
		});

		return types;
	}

	function normalizeContainers(containers) {
		// Nodelist with entries
		if (NodeList.prototype.isPrototypeOf(containers) && containers.length > 0) {
			containers = Array.prototype.slice.call(containers);

			// Empty Nodelist
		} else if (NodeList.prototype.isPrototypeOf(containers) && containers.length === 0) {
			containers = [];

			// Handle null (document.querySelector() returns null with no match)
		} else if (containers === null) {
			containers = [];

			// Single DOM element
		} else if (!Array.isArray(containers) && containers instanceof Element) {
			containers = [containers];
		}

		return containers;
	}

	if (window.Shopify.designMode) {
		document.addEventListener('shopify:section:load', function (event) {
			var id = event.detail.sectionId;
			var container = event.target.querySelector('[' + SECTION_ID_ATTR$1 + '="' + id + '"]');

			if (container !== null) {
				load(container.getAttribute(SECTION_TYPE_ATTR), container);
			}
		});

		document.addEventListener('shopify:section:unload', function (event) {
			var id = event.detail.sectionId;
			var container = event.target.querySelector('[' + SECTION_ID_ATTR$1 + '="' + id + '"]');
			var instance = getInstances(container)[0];

			if (typeof instance === 'object') {
				unload(container);
			}
		});

		document.addEventListener('shopify:section:select', function (event) {
			var instance = getInstanceById(event.detail.sectionId);

			if (typeof instance === 'object') {
				instance.onSelect(event);
			}
		});

		document.addEventListener('shopify:section:deselect', function (event) {
			var instance = getInstanceById(event.detail.sectionId);

			if (typeof instance === 'object') {
				instance.onDeselect(event);
			}
		});

		document.addEventListener('shopify:block:select', function (event) {
			var instance = getInstanceById(event.detail.sectionId);

			if (typeof instance === 'object') {
				instance.onBlockSelect(event);
			}
		});

		document.addEventListener('shopify:block:deselect', function (event) {
			var instance = getInstanceById(event.detail.sectionId);

			if (typeof instance === 'object') {
				instance.onBlockDeselect(event);
			}
		});
	}

	const selectors$5 = {
		sidebar: '.sidebar',
		widgetCategories: '.widget--categories',
		widgetCategoriesLinks: '.widget--categories .widget__links',
		widgetLinksEl: '.widget__links',
		widgetLinks: '.widget__links .has-sub-nav > a',
		widgetLinksSub: '.widget__links .submenu > li > a',
		listEl: 'li',
		linkEl: 'a',
		sliderEnabled: 'flickity-enabled',
		articleSingle: '.article--single',
		sidebarContents: '.sidebar__contents',
		hasSubNav: '.has-sub-nav',
		classOpen: 'open',
		classActive: 'active',
		classSubmenu: 'submenu',
	};

	const sections = {};

	class Article {
		constructor(section) {
			this.container = section.container;
			this.sidebar = this.container.querySelector(selectors$5.sidebar);
			this.widgetCategories = this.container.querySelector(selectors$5.widgetCategories);
			this.flkty = null;

			this.init();
		}

		init() {
			if (this.sidebar) {
				this.sidebarNav();
			}
		}

		sidebarNav() {
			const self = this;

			this.navStates();

			// Dropdown Menus
			document.addEventListener('click', function (e) {
				const checkLinkTag = e.target.tagName.toLowerCase() === selectors$5.linkEl;
				const checkLinkParent = e.target.closest(`${selectors$5.listEl}${selectors$5.hasSubNav}`);
				const checkLinkClosest = e.target.closest(selectors$5.widgetLinksEl);
				const checkLink = checkLinkTag && checkLinkParent && checkLinkClosest;
				const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
				const isMobile = windowWidth < 750;

				if (!isMobile && checkLink) {
					const submenu = e.target.nextElementSibling;

					submenu.parentElement.classList.toggle(selectors$5.classActive);
					submenu.classList.toggle(selectors$5.classOpen);
					slideToggle(submenu);

					self.checkAriaStatus(submenu);

					e.preventDefault();
				}
			});

			if (this.widgetCategories) {
				this.categories();

				window.addEventListener('resize', debounce(this.categories, 250));
			}
		}

		checkAriaStatus(submenu) {
			const state = submenu.classList.contains(selectors$5.classOpen);

			if (state) {
				submenu.setAttribute('aria-expanded', true);
			} else {
				submenu.setAttribute('aria-expanded', false);
			}
		}

		navStates() {
			// Nav Active States
			const links = this.container.querySelectorAll(selectors$5.widgetLinks);
			const subLinks = this.container.querySelectorAll(selectors$5.widgetLinksSub);

			links.forEach((element) => {
				const href = element.getAttribute('href');
				const location = window.location.pathname;

				if (href === location) {
					const elementClosest = element.closest(selectors$5.hasSubNav);
					elementClosest.classList.add(selectors$5.classActive);
					elementClosest.querySelector(`.${selectors$5.classSubmenu}`).style.display = 'block';
				}
			});

			subLinks.forEach((element) => {
				const href = element.getAttribute('href');
				const location = window.location.pathname;

				if (href === location) {
					const elementClosest = element.closest(selectors$5.hasSubNav);
					element.closest('li').classList.add(selectors$5.classActive);
					elementClosest.classList.add(selectors$5.classActive);
					elementClosest.querySelector(`.${selectors$5.classSubmenu}`).style.display = 'block';
				}
			});
		}

		categories() {
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const isMobile = windowWidth < 750;
			const menuCategories = document.querySelector(selectors$5.widgetCategoriesLinks);
			const activeLink = menuCategories.querySelector(`${selectors$5.listEl}.${selectors$5.classActive}`);

			let activeLinkIdx = null;
			if (activeLink) {
				activeLinkIdx = Array.from(activeLink.parentElement.children).indexOf(activeLink);
			}

			const widgetCategories = document.querySelector(selectors$5.widgetCategories);

			this.navStates;

			this.flkty = new Flickity(menuCategories, {
				initialIndex: activeLinkIdx,
				autoPlay: false,
				prevNextButtons: true,
				contain: true,
				pageDots: false,
				wrapAround: true,
				watchCSS: true,
				arrowShape: {
					x0: 10,
					x1: 60,
					y1: 50,
					x2: 60,
					y2: 40,
					x3: 20,
				},
			});

			if (isMobile) {
				document.querySelector(selectors$5.articleSingle).prepend(widgetCategories);
			} else {
				document.querySelector(selectors$5.sidebarContents).prepend(widgetCategories);
			}
		}
	}

	const articleSection = {
		onLoad() {
			sections[this.id] = new Article(this);
		},
	};

	register('article', articleSection);

	const selectors$6 = {
		slider: '.editorial__slider',
		slide: '.editorial__slide',
		prevArrow: '[data-prev-arrow]',
		nextArrow: '[data-next-arrow]',
		classIsHidden: 'is-hidden',
		dataSlideIndex: 'data-slide-index',
	};

	const sections$1 = {};

	class Editorial {
		constructor(section) {
			this.container = section.container;
			this.slideshow = this.container.querySelector(`${selectors$6.slider}--${section.id}`);
			this.sliderPrev = this.container.querySelector(selectors$6.prevArrow);
			this.sliderNext = this.container.querySelector(selectors$6.nextArrow);
			this.flkty = null;

			this.init();
		}
		init() {
			const self = this;

			if (this.slideshow) {
				this.flkty = new Flickity(this.slideshow, {
					autoPlay: false,
					prevNextButtons: false,
					contain: true,
					pageDots: false,
					adaptiveHeight: true,
					wrapAround: true,
					on: {
						ready: function () {
							const slidesNumber = this.slides.length;
							if (slidesNumber > 1) {
								self.sliderPrev.classList.remove(selectors$6.classIsHidden);
								self.sliderNext.classList.remove(selectors$6.classIsHidden);
							} else {
								self.sliderPrev.classList.add(selectors$6.classIsHidden);
								self.sliderNext.classList.add(selectors$6.classIsHidden);
							}
						},
					},
				});

				this.sliderPrev.addEventListener('click', function (e) {
					e.preventDefault();

					self.flkty.previous(true);
				});

				this.sliderNext.addEventListener('click', function (e) {
					e.preventDefault();

					self.flkty.next(true);
				});
			}
		}
		onBlockSelect(evt) {
			// Ignore the cloned version
			const slide = this.slideshow.querySelector(`${selectors$6.slide}--${evt.detail.blockId}`);
			const slideIndex = parseInt(slide.getAttribute(selectors$6.dataSlideIndex));

			// Go to selected slide, pause autoplay
			this.flkty.select(slideIndex);
			this.flkty.stopPlayer();
		}
	}

	const editorialSection = {
		onLoad() {
			sections$1[this.id] = new Editorial(this);
		},
		onBlockSelect(e) {
			sections$1[this.id].onBlockSelect(e);
		},
	};

	const selectors$7 = {
		dataSectionId: 'data-section-id',
		blogListing: '.blog-listing',
		flickityEnabled: 'flickity-enabled',
	};

	const sections$2 = {};

	class Blog {
		constructor(section) {
			this.container = section.container;
			this.id = this.container.getAttribute(selectors$7.dataSectionId);
			this.slideshow = this.container.querySelector(selectors$7.blogListing);
			this.flkty = null;

			this.init();
		}

		init() {
			this.flkty = new Flickity(this.slideshow, {
				autoPlay: false,
				prevNextButtons: true,
				contain: true,
				pageDots: false,
				wrapAround: true,
				watchCSS: true,
				arrowShape: {
					x0: 10,
					x1: 60,
					y1: 50,
					x2: 60,
					y2: 40,
					x3: 20,
				},
			});
		}

		setHeights(container) {
			const content = container.querySelectorAll('.featured-blog__content');
			let flag = false;

			if (content.length > 0) {
				for (const element of content) {
					if (element.classList.contains('onboarding-blog-content')) {
						flag = true;
						break;
					}
				}

				if (flag) {
					let tallestContent = 0;

					for (const element of content) {
						const title = element.querySelector('.h5');
						const titleHeight = parseInt(getComputedStyle(title, null).height.replace('px', ''));
						const dateEl = element.querySelectorAll('.featured-blog__date');
						const dateHeight = parseInt(getComputedStyle(dateEl, null).height.replace('px', ''));
						const excerptEl = element.querySelector('.excerpt');
						const excerptHeight = parseInt(getComputedStyle(excerptEl, null).height.replace('px', ''));
						const currentContent = titleHeight + dateHeight + excerptHeight;
						if (currentContent > tallestContent) {
							tallestContent = currentContent;
						}
					}

					tallestContent += 75;
					for (const element of content) {
						element.style.height = `${tallestContent}px`;
					}
				}
			}
		}

		onBlockSelect(evt) {}

		onUnload(evt) {
			if (this.slideshow.classList.contains(selectors$7.flickityEnabled)) {
				this.flkty.destroy();
			}
		}
	}

	const blogSection = {
		onLoad() {
			sections$2[this.id] = new Blog(this);
		},
		onUnload(e) {
			sections$2[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$2[this.id].onBlockSelect(e);
		},
	};

	register('blog-template', [blogSection, editorialSection]);
	register('blog', blogSection);

	var sections$3 = {};

	const parallaxHero = {
		onLoad() {
			sections$3[this.id] = [];
			const frames = this.container.querySelectorAll('[data-parallax-wrapper]');
			frames.forEach((frame) => {
				const inner = frame.querySelector('[data-parallax-img]');
				sections$3[this.id].push(
					new Rellax(inner, {
						center: true,
						round: true,
						frame: frame,
					})
				);
			});
		},
		onUnload: function () {
			sections$3[this.id].forEach((image) => {
				if (typeof image.destroy === 'function') {
					image.destroy();
				}
			});
		},
	};

	register('hero', parallaxHero);

	const selectors$8 = {
		popoutWrappper: '[data-popout]',
		popoutList: '[data-popout-list]',
		popoutToggle: '[data-popout-toggle]',
		popoutInput: '[data-popout-input]',
		popoutOptions: '[data-popout-option]',
		dataValue: 'data-value',
		ariaExpanded: 'aria-expanded',
	};

	const classes$1 = {
		listVisible: 'popout-list--visible',
	};

	let sections$4 = {};

	const popupToggleClick = function (evt) {
		const ariaExpanded = evt.currentTarget.getAttribute(selectors$8.ariaExpanded) === 'true';
		evt.currentTarget.setAttribute(selectors$8.ariaExpanded, !ariaExpanded);
		this.popoutList.classList.toggle(classes$1.listVisible);
	};

	const popupToggleFocusout = function (evt) {
		const popoutLostFocus = this.container.contains(evt.relatedTarget);

		if (!popoutLostFocus) {
			this._hideList();
		}
	};

	const popupListFocusout = function (evt) {
		const childInFocus = evt.currentTarget.contains(evt.relatedTarget);
		const isVisible = this.popoutList.classList.contains(classes$1.listVisible);

		if (isVisible && !childInFocus) {
			this._hideList();
		}
	};

	const popupOptionsClick = function (evt) {
		const link = evt.target.closest(selectors$8.popoutOptions);
		if (link.attributes.href.value === '#') {
			evt.preventDefault();
			let attrValue = '';
			if (evt.currentTarget.getAttribute(selectors$8.dataValue)) {
				attrValue = evt.currentTarget.getAttribute(selectors$8.dataValue);
			}
			this._submitForm(attrValue);
		}
	};

	const containerKeyup = function (evt) {
		if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
			return;
		}
		this._hideList();
		this.popoutToggle.focus();
	};

	const bodyClick = function (evt) {
		const isOption = this.container.contains(evt.target);
		const isVisible = this.popoutList.classList.contains(classes$1.listVisible);

		if (isVisible && !isOption) {
			this._hideList();
		}
	};

	class Popout {
		constructor(popout) {
			this.container = popout;
			this.popoutList = this.container.querySelector(selectors$8.popoutList);
			this.popoutToggle = this.container.querySelector(selectors$8.popoutToggle);
			this.popoutInput = this.container.querySelector(selectors$8.popoutInput);
			this.popoutOptions = this.container.querySelectorAll(selectors$8.popoutOptions);

			this._connectOptions();
			this._connectToggle();
			this._onFocusOut();
		}

		unload() {
			if (this.popoutOptions.length) {
				this.popoutOptions.forEach((element) => {
					element.removeEventListener('click', popupOptionsClick);
				});
			}

			this.popoutToggle.removeEventListener('click', popupToggleClick);

			this.popoutToggle.removeEventListener('focusout', popupToggleFocusout);

			this.popoutList.removeEventListener('focusout', popupListFocusout);

			this.container.removeEventListener('keyup', containerKeyup);
		}

		_connectToggle() {
			this.popoutToggle.addEventListener('click', popupToggleClick.bind(this));
		}

		_connectOptions() {
			if (this.popoutOptions.length) {
				this.popoutOptions.forEach((element) => {
					element.addEventListener('click', popupOptionsClick.bind(this));
				});
			}
		}

		_onFocusOut() {
			this.popoutToggle.addEventListener('focusout', popupToggleFocusout.bind(this));

			this.popoutList.addEventListener('focusout', popupListFocusout.bind(this));

			this.container.addEventListener('keyup', containerKeyup.bind(this));

			document.body.addEventListener('click', bodyClick.bind(this));
		}

		_submitForm(value) {
			const form = this.container.closest('form');
			this.popoutInput.value = value;
			if (form) {
				form.submit();
			}
		}

		_hideList() {
			this.popoutList.classList.remove(classes$1.listVisible);
			this.popoutToggle.setAttribute(selectors$8.ariaExpanded, false);
		}
	}

	const popoutSection = {
		onLoad() {
			sections$4[this.id] = [];
			const wrappers = this.container.querySelectorAll(selectors$8.popoutWrappper);
			wrappers.forEach((wrapper) => {
				sections$4[this.id].push(new Popout(wrapper));
			});
		},
		onUnload: function () {
			sections$4[this.id].forEach((popout) => {
				if (typeof popout.unload === 'function') {
					popout.unload();
				}
			});
		},
	};

	const footerSection = {
		onLoad() {
			// Lighthouse fires security warning for the Shopify link.
			var shopifyLink = document.querySelector('[data-powered-link] a');
			if (shopifyLink) {
				shopifyLink.relList.add('noopener');
			}
		},
	};

	register('footer', [popoutSection, footerSection, parallaxHero]);

	const selectors$9 = {
		reviews: 'data-reviews',
	};

	class ProductGridReviews {
		constructor(section) {
			this.container = section.container;
			this.showReviews = this.container.getAttribute(selectors$9.reviews) !== 'false';
			this.reviewsAppInstalled = typeof window.SPR === 'function';

			this.init();
		}

		init() {
			if (this.showReviews && typeof yotpo !== 'undefined') {
				yotpo.initialized ? yotpo.refreshWidgets() : yotpo.initWidgets();
			} else if (this.showReviews && this.reviewsAppInstalled) {
				window.SPR.initDomEls();
				window.SPR.loadBadges();
			}
		}
	}
	const productGridReviews = {
		onLoad() {
			new ProductGridReviews(this);
		},
	};

	const selectors$a = {
		productSlideshow: '[data-product-slideshow]',
		productThumbs: '[data-product-thumbs]',
		dataTallLayout: 'data-tall-layout',
		dataType: 'data-type',
		dataMediaId: 'data-media-id',
		dataThumb: 'data-thumb',
		dataNewThumb: 'data-new-thumb',
		dataThumbIndex: 'data-thumb-index',
		ariaLabel: 'aria-label',
		dataThumbnail: '[data-thumbnail]',
		productSlideThumb: '.js-product-slide-thumb',
		classSelected: 'is-selected',
		classMediaHidden: 'media--hidden',
		sliderEnabled: 'flickity-enabled',
		focusEnabled: 'focus-enabled',
	};

	const thumbIcons = {
		model: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-media-model" viewBox="0 0 26 26"><path d="M1 25h24V1H1z"/><path class="icon-media-model-outline" d="M.5 25v.5h25V.5H.5z" fill="none"/><path class="icon-media-model-element" d="M19.13 8.28L14 5.32a2 2 0 0 0-2 0l-5.12 3a2 2 0 0 0-1 1.76V16a2 2 0 0 0 1 1.76l5.12 3a2 2 0 0 0 2 0l5.12-3a2 2 0 0 0 1-1.76v-6a2 2 0 0 0-.99-1.72zm-6.4 11.1l-5.12-3a.53.53 0 0 1-.26-.38v-6a.53.53 0 0 1 .27-.46l5.12-3a.53.53 0 0 1 .53 0l5.12 3-4.72 2.68a1.33 1.33 0 0 0-.67 1.2v6a.53.53 0 0 1-.26 0z" opacity=".6" style="isolation:isolate"/></svg>',
		video: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-media-video" viewBox="0 0 26 26"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 25h24V1H1v24z"/><path class="icon-media-video-outline" d="M.5 25v.5h25V.5H.5V25z"/><path class="icon-media-video-element" fill-rule="evenodd" clip-rule="evenodd" d="M9.718 6.72a1 1 0 0 0-1.518.855v10.736a1 1 0 0 0 1.562.827l8.35-5.677a1 1 0 0 0-.044-1.682l-8.35-5.06z" opacity=".6"/></svg>',
	};

	class InitSlider {
		constructor(section) {
			this.container = section.container;
			this.tallLayout = this.container.getAttribute(selectors$a.dataTallLayout) === 'true';
			this.slideshow = this.container.querySelector(selectors$a.productSlideshow);
			this.thumbs = this.container.querySelector(selectors$a.productThumbs);
			this.flkty = null;
			this.init();
		}

		init() {
			const instance = this;
			if (this.tallLayout) {
				instance.initSliderMobile();

				document.addEventListener('theme:resize', () => {
					instance.initSliderMobile();
				});
			} else {
				this.createSlider();
			}
		}

		initSliderMobile() {
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const isMobile = windowWidth < theme.variables.mediaQuerySmall;
			if (isMobile) {
				this.createSlider();
			} else {
				this.destroySlider();
			}
		}

		destroySlider() {
			const instance = this;
			const isSliderInitialized = instance.slideshow.classList.contains(selectors$a.sliderEnabled);

			if (isSliderInitialized) {
				instance.flkty.destroy();
			}

			if (this.thumbs) {
				this.thumbs.innerHTML = '';
			}
		}

		createSlider() {
			if (!this.slideshow) {
				return;
			}

			const instance = this;
			const firstSlide = this.slideshow.querySelectorAll(`[${selectors$a.dataType}]`)[0];

			let flickityOptions = {
				autoPlay: false,
				prevNextButtons: false,
				contain: true,
				pageDots: false,
				adaptiveHeight: true,
				wrapAround: true,
				fade: true,
				on: {
					ready: function () {
						instance.sliderThumbs(this);
					},
				},
			};

			this.flkty = new FlickityFade(this.slideshow, flickityOptions);
			this.flkty.resize();

			if (firstSlide) {
				const firstType = firstSlide.getAttribute(selectors$a.dataType);

				if (firstType === 'model' || firstType === 'video' || firstType === 'external_video') {
					this.flkty.options.draggable = false;
					this.flkty.updateDraggable();
				}
			}

			this.flkty.on('change', function (index) {
				let lastSLideIdx = index;

				if (instance.thumbs) {
					const selectedElem = instance.thumbs.querySelector(`.${selectors$a.classSelected}`);
					const currentSlide = instance.thumbs.querySelector(`li [${selectors$a.dataThumbIndex}="${index}"]`);

					if (selectedElem) {
						lastSLideIdx = Array.from(selectedElem.parentElement.children).indexOf(selectedElem);
						selectedElem.classList.remove(selectors$a.classSelected);
					}

					if (currentSlide) {
						currentSlide.parentElement.classList.add(selectors$a.classSelected);
					}
				}

				const currentMedia = this.cells[lastSLideIdx].element;
				const newMedia = this.selectedElement;

				currentMedia.dispatchEvent(new CustomEvent('mediaHidden'));
				newMedia.classList.remove(selectors$a.classMediaHidden);
			});

			this.flkty.on('settle', function (index) {
				const currentMedia = this.selectedElement;
				const otherMedia = Array.prototype.filter.call(currentMedia.parentNode.children, function (child) {
					return child !== currentMedia;
				});
				const mediaType = currentMedia.getAttribute(selectors$a.dataType);
				const isFocusEnabled = document.body.classList.contains(selectors$a.focusEnabled);

				if (mediaType === 'model' || mediaType === 'video' || mediaType === 'external_video') {
					// fisrt boolean sets value, second option false to prevent refresh
					instance.flkty.options.draggable = false;
					instance.flkty.updateDraggable();
				} else {
					instance.flkty.options.draggable = true;
					instance.flkty.updateDraggable();
				}

				if (isFocusEnabled) currentMedia.dispatchEvent(new Event('focus'));

				if (otherMedia.length) {
					otherMedia.forEach((element) => {
						element.classList.add(selectors$a.classMediaHidden);
					});
				}

				currentMedia.dispatchEvent(new CustomEvent('mediaVisible'));
			});

			instance.container.addEventListener('click', function (e) {
				const target = e.target;
				if (target.matches(selectors$a.productSlideThumb) || target.closest(selectors$a.productSlideThumb)) {
					e.preventDefault();

					let slideIdx = 0;
					let selector;
					if (target.matches(selectors$a.productSlideThumb)) {
						selector = target;
					} else {
						selector = target.closest(selectors$a.productSlideThumb);
					}

					slideIdx = parseInt(selector.getAttribute(selectors$a.dataThumbIndex));

					/* Laura fix */
					const allSlides = document.querySelectorAll('.product__photo.product__slide');
					allSlides.forEach((element, index) => {
						if (+index === +slideIdx) {
							element.querySelector('img[data-product-image]').src = e.target.closest('.thumb--image').dataset.newMedia;
						}
					});
					instance.flkty.select(slideIdx);
				}
			});
		}

		sliderThumbs(thisEl) {
			const slides = thisEl.slides;

			if (this.thumbs && slides.length) {
				let slidesHtml = '';
				slides.forEach((element, i) => {
					const slide = element.cells[0].element;
					const type = slide.getAttribute(selectors$a.dataType);
					const mediaId = slide.getAttribute(selectors$a.dataMediaId);
					const thumb = slide.getAttribute(selectors$a.dataThumb);
					const newThumb = slide.getAttribute(selectors$a.dataNewThumb);
					let thumbAlt = '';
					const thumbIcon = thumbIcons[type] ? thumbIcons[type] : '';
					let selected = '';

					if (slide.querySelector(`[${selectors$a.ariaLabel}]`)) {
						thumbAlt = slide.querySelector(`[${selectors$a.ariaLabel}]`).getAttribute(selectors$a.ariaLabel);
					}

					if (thumbAlt === '' && slide.hasAttribute(selectors$a.ariaLabel)) {
						thumbAlt = slide.getAttribute(selectors$a.ariaLabel);
					}

					slide.setAttribute('tabindex', '-1');

					if (slide.classList.contains(selectors$a.classSelected)) {
						selected = selectors$a.classSelected;
					}

					slidesHtml += `<li class="${selected}"><a href="${thumb}" class="thumb--${type} js-product-slide-thumb" data-thumb-index="${i}" data-thumbnail data-media-id="${mediaId}" data-new-media="${newThumb}"><img alt="${thumbAlt}" src="${thumb}">${thumbIcon}</a></li>`;
				});

				if (slidesHtml !== '') {
					slidesHtml = `<ul>${slidesHtml}</ul>`;
					this.thumbs.innerHTML = slidesHtml;
				}
			}

			const productThumbImages = this.container.querySelectorAll(selectors$a.dataThumbnail);
			if (productThumbImages.length) {
				productThumbImages.forEach((element) => {
					element.addEventListener('click', function (e) {
						e.preventDefault();
					});

					element.addEventListener('keyup', function (e) {
						// On keypress Enter move the focus to the first focusable element in the related slide
						if (e.keyCode === 13) {
							const mediaId = this.getAttribute(selectors$a.dataMediaId);
							const mediaElem = thisEl.element
								.querySelector(`[${selectors$a.dataMediaId}="${mediaId}"]`)
								.querySelectorAll('model-viewer, video, iframe, button, [href], input, [tabindex]:not([tabindex="-1"])')[0];
							if (mediaElem) {
								mediaElem.dispatchEvent(new Event('focus'));
								mediaElem.dispatchEvent(new Event('select'));
							}
						}
					});
				});
			}
		}
	}

	const selectors$b = {
		zoomWrapper: '[data-zoom-wrapper]',
		dataImageSrc: 'data-image-src',
		dataImageWidth: 'data-image-width',
		dataImageHeight: 'data-image-height',
		dataImageZoomEnable: 'data-image-zoom-enable',
		thumbs: '.pswp__thumbs',
		caption: '[data-zoom-caption]',
	};

	const classes$2 = {
		variantSoldOut: 'variant--soldout',
		variantUnavailable: 'variant--unavailabe',
		popupThumb: 'pswp__thumb',
		popupClass: 'pswp-zoom-gallery',
		popupClassNoThumbs: 'pswp-zoom-gallery--single',
	};

	class Zoom {
		constructor(section) {
			this.container = section.container;
			this.zoomWrappers = this.container.querySelectorAll(selectors$b.zoomWrapper);
			this.thumbsContainer = document.querySelector(selectors$b.thumbs);
			this.zoomCaptionElem = this.container.querySelector(selectors$b.caption);
			this.zoomEnable = this.container.getAttribute(selectors$b.dataImageZoomEnable) === 'true';

			if (this.zoomEnable) {
				this.init();
			}
		}

		init() {
			const self = this;

			if (this.zoomWrappers.length) {
				this.zoomWrappers.forEach((element, i) => {
					element.addEventListener('click', function (e) {
						e.preventDefault();

						self.createZoom(i);
					});

					element.addEventListener('keyup', function (e) {
						// On keypress Enter move the focus to the first focusable element in the related slide
						if (e.keyCode === 13) {
							e.preventDefault();

							self.createZoom(i);
						}
					});
				});
			}
		}

		createZoom(indexImage) {
			const self = this;
			let items = [];
			let counter = 0;
			let thumbs = '';
			this.zoomWrappers.forEach((elementImage) => {
				const imgSrc = elementImage.getAttribute(selectors$b.dataImageSrc);
				const imgWidth = parseInt(elementImage.getAttribute(selectors$b.dataImageWidth));
				const imgHeight = parseInt(elementImage.getAttribute(selectors$b.dataImageHeight));

				items.push({
					src: imgSrc,
					w: imgWidth,
					h: imgHeight,
					msrc: imgSrc,
				});

				thumbs += `<a href="#" class="${classes$2.popupThumb}" style="background-image: url('${imgSrc}')"></a>`;

				counter += 1;
				if (self.zoomWrappers.length === counter) {
					let popupClass = `${classes$2.popupClass}`;
					if (counter === 1) {
						popupClass = `${classes$2.popupClass} ${classes$2.popupClassNoThumbs}`;
					}
					const options = {
						history: false,
						focus: false,
						index: indexImage,
						mainClass: popupClass,
						showHideOpacity: true,
						howAnimationDuration: 150,
						hideAnimationDuration: 250,
						closeOnScroll: false,
						closeOnVerticalDrag: false,
						captionEl: true,
						closeEl: true,
						closeElClasses: ['caption-close'],
						tapToClose: false,
						clickToCloseNonZoomable: false,
						maxSpreadZoom: 2,
						loop: true,
						spacing: 0,
						allowPanToNext: true,
						pinchToClose: false,
						addCaptionHTMLFn: function (item, captionEl, isFake) {
							self.zoomCaption(item, captionEl, isFake);
						},
						getThumbBoundsFn: function getThumbBoundsFn() {
							const imageLocation = self.zoomWrappers[indexImage];
							const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
							const rect = imageLocation.getBoundingClientRect();
							return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
						},
					};

					openPhotoSwipe(items, options);

					if (self.thumbsContainer && thumbs !== '') {
						self.thumbsContainer.innerHTML = thumbs;
					}
				}
			});
		}

		zoomCaption(item, captionEl, isFake) {
			let captionHtml = '';
			const targetContainer = captionEl.children[0];
			if (this.zoomCaptionElem) {
				captionHtml = this.zoomCaptionElem.innerHTML;

				if (this.zoomCaptionElem.closest(`.${classes$2.variantSoldOut}`)) {
					targetContainer.classList.add(classes$2.variantSoldOut);
				} else {
					targetContainer.classList.remove(classes$2.variantSoldOut);
				}

				if (this.zoomCaptionElem.closest(`.${classes$2.variantUnavailable}`)) {
					targetContainer.classList.add(classes$2.variantUnavailable);
				} else {
					targetContainer.classList.remove(classes$2.variantUnavailable);
				}
			}

			targetContainer.innerHTML = captionHtml;
			return false;
		}
	}

	const selectors$c = {
		videoPlayer: '[data-video]',
		modelViewer: '[data-model]',
		dataType: 'data-type',
		dataMediaId: 'data-media-id',
		dataVideoLooping: 'data-video-looping',
		dataYoutubeId: 'data-youtube-id',
		productMediaWrapper: '[data-product-single-media-wrapper]',
		productMediaContainer: '[data-product-single-media-group]',
		classMediaHidden: 'media--hidden',
	};
	theme.mediaInstances = {};
	class Video {
		constructor(section) {
			this.section = section;
			this.container = section.container;
			this.id = section.id;
			this.players = {};
			this.init();
		}

		init() {
			const playerElements = this.container.querySelectorAll(selectors$c.videoPlayer);
			let hasYouTube = false;
			let hasNative = false;
			for (let i = 0; i < playerElements.length; i++) {
				const newPlayerID = playerElements[i].getAttribute('id');
				const newPlayerType = playerElements[i].getAttribute(selectors$c.dataType);
				const newPlayerMediaId = playerElements[i].getAttribute(selectors$c.dataMediaId);
				this.players[newPlayerID] = {};
				this.players[newPlayerID].id = newPlayerID;
				this.players[newPlayerID].type = newPlayerType;
				this.players[newPlayerID].mediaId = newPlayerMediaId;
				this.players[newPlayerID].container = playerElements[i];
				this.players[newPlayerID].element = playerElements[i].querySelector('iframe, video');
				if (!this.players[newPlayerID].element) return;
				if (newPlayerType === 'external_video') {
					const youtubeID = playerElements[i].getAttribute(selectors$c.dataYoutubeId);
					this.players[newPlayerID].externalID = youtubeID;
					hasYouTube = true;
				} else if (newPlayerType === 'video') {
					hasNative = true;
				}
			}
			if (hasNative) {
				if (window.isPlyrLoaded) {
					this.nativeInitCallback();
				} else {
					// Load Plyr
					const videoLoad = {
						name: 'video-ui',
						version: '1.0',
					};
					loadScript(videoLoad).then(() => {
						this.nativeInitCallback();
					});
				}
			}

			if (hasYouTube) {
				if (window.isYoutubeAPILoaded) {
					this.youTubeInitCallback();
				} else {
					// Load Youtube API
					window.loadYoutubeAPI();
					document.body.addEventListener('youtubeAPIReady', this.youTubeInitCallback.bind(this));
				}
			}
		}

		youTubeInitCallback() {
			const self = this;
			for (const currentPlayerID in this.players) {
				if (this.players[currentPlayerID].type === 'external_video') {
					const currentPlayer = this.players[currentPlayerID];
					const enableLooping =
						this.players[currentPlayerID].container
							.closest(`[${selectors$c.dataVideoLooping}]`)
							.getAttribute(selectors$c.dataVideoLooping) === 'true';
					this.players[currentPlayerID].player = new YT.Player(currentPlayer.element, {
						videoId: currentPlayer.externalID,
						playerVars: {
							cc_load_policy: 0,
							iv_load_policy: 3,
							modestbranding: 1,
							playsinline: 1,
							autohide: 0,
							controls: 1,
							branding: 0,
							showinfo: 0,
							rel: 0,
							fs: 0,
							wmode: 'opaque',
						},
						events: {
							onStateChange: function (e) {
								if (e.data === 0 && enableLooping) e.target.seekTo(0);
								const currentMediaElem = e.target.getIframe().closest(`[${selectors$c.dataMediaId}]`);
								let currentMediaId = '';
								if (currentMediaElem) {
									currentMediaId = currentMediaElem.getAttribute(selectors$c.dataMediaId);
								}
								if (e.data === 1) self.pauseOtherMedia(currentMediaId);
							},
						},
					});
					this.players[currentPlayerID].container.addEventListener('mediaHidden', function (event) {
						self.onHidden(event, true);
					});
					this.players[currentPlayerID].container.addEventListener('xrLaunch', function (event) {
						self.onHidden(event, true);
					});
					this.players[currentPlayerID].container.addEventListener('mediaVisible', function (event) {
						self.onVisible(event, true);
					});
				}
			}
		}

		nativeInitCallback() {
			const self = this;
			for (const currentPlayerID in this.players) {
				if (this.players[currentPlayerID].type === 'video') {
					const currentPlayer = this.players[currentPlayerID];
					const enableLooping =
							this.players[currentPlayerID].container
								.closest(`[${selectors$c.dataVideoLooping}]`)
								.getAttribute(selectors$c.dataVideoLooping) === 'true',
						plyrOptions = {
							loop: { active: enableLooping },
							focusOnPlay: true,
						};
					this.players[currentPlayerID].player = new Shopify.Plyr(currentPlayer.element, plyrOptions);
					currentPlayer.element.addEventListener('play', (event) => {
						const currentMediaElem = event.target.closest(`[${selectors$c.dataMediaId}]`);
						let currentMediaId = '';
						if (currentMediaElem) {
							currentMediaId = currentMediaElem.getAttribute(selectors$c.dataMediaId);
						}
						self.pauseOtherMedia(currentMediaId);
					});
					this.players[currentPlayerID].container.addEventListener('mediaHidden', function (event) {
						self.onHidden(event, false);
					});
					this.players[currentPlayerID].container.addEventListener('xrLaunch', function (event) {
						self.onHidden(event, false);
					});
					this.players[currentPlayerID].container.addEventListener('mediaVisible', function (event) {
						self.onVisible(event, false);
					});
				}
			}
		}

		onHidden(event, playerYT = false) {
			if (typeof event.target.dataset.playerId !== 'undefined') {
				const newID = event.target.dataset.playerId;
				const newPlayer = this.players[newID];
				if (playerYT === true && newPlayer.player && newPlayer.player.pauseVideo) {
					newPlayer.player.pauseVideo();
				} else if (newPlayer.player && newPlayer.player.pause) {
					newPlayer.player.pause();
				}
			}
		}

		onVisible(event, playerYT = false) {
			if (window.theme.touched) return;
			if (typeof event.target.dataset.playerId !== 'undefined') {
				const newID = event.target.dataset.playerId;
				const newPlayer = this.players[newID];
				if (playerYT === true && newPlayer.player && newPlayer.player.playVideo) {
					newPlayer.player.playVideo();
				} else if (newPlayer.player && newPlayer.player.play) {
					newPlayer.player.play();
				}
			}
		}

		pauseOtherMedia(mediaId) {
			const mediaIdString = `[${selectors$c.dataMediaId}="${mediaId}"]`;
			const currentMedia = document.querySelector(`${selectors$c.productMediaWrapper}${mediaIdString}`);
			const otherMedia = document.querySelectorAll(`${selectors$c.productMediaWrapper}:not(${mediaIdString})`);
			currentMedia.classList.remove(selectors$c.classMediaHidden);
			if (otherMedia.length) {
				otherMedia.forEach((element) => {
					element.dispatchEvent(new CustomEvent('mediaHidden'));
					element.classList.add(selectors$c.classMediaHidden);
				});
			}
		}
	}

	theme.mediaInstances = {};

	const selectors$d = {
		videoPlayer: '[data-video]',
		modelViewer: '[data-model]',
		sliderEnabled: 'flickity-enabled',
		classMediaHidden: 'media--hidden',
	};

	class Media {
		constructor(section) {
			this.section = section;
			this.id = section.id;
			this.container = section.container;
		}

		init() {
			new Video(this.section);

			this.detect3d();
			this.launch3d();

			new Zoom(this.section);
			new InitSlider(this.section);
		}

		detect3d() {
			const modelViewerElements = this.container.querySelectorAll(selectors$d.modelViewer);
			if (modelViewerElements.length) {
				modelViewerElements.forEach((element) => {
					theme.ProductModel.init(element, this.id);
				});
			}
		}

		launch3d() {
			const instance = this;

			document.addEventListener('shopify_xr_launch', function () {
				const currentMedia = instance.container.querySelector(`${instance.selectors.modelViewer}:not(.${selectors$d.classMediaHidden})`);
				currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
			});
		}
	}

	const fadeOut = (el, callback = null) => {
		el.style.opacity = 1;

		(function fade() {
			if ((el.style.opacity -= 0.1) < 0) {
				el.style.display = 'none';
			} else {
				requestAnimationFrame(fade);
			}

			if (parseFloat(el.style.opacity) === 0 && typeof callback === 'function') {
				callback();
			}
		})();
	};

	const selectors$e = {
		modalWrapper: 'modal__wrapper',
	};

	function showModal(element, onInit, onClose) {
		const thisModal = element;

		if (typeof onInit === 'function') {
			fadeIn(thisModal, 'block', onInit);
		} else {
			fadeIn(thisModal);
		}

		thisModal.addEventListener('click', function (e) {
			const target = e.target;

			if (target.classList.contains('close') || target.classList.contains(selectors$e.modalWrapper)) {
				if (typeof onClose === 'function') {
					fadeOut(thisModal, onClose);
				} else {
					fadeOut(thisModal);
				}
				e.preventDefault();

				removeTrapFocus();
				return false;
			}
		});

		thisModal.addEventListener('keyup', (e) => {
			if (e.which !== window.theme.keyboardKeys.ESCAPE) {
				return;
			}
			if (typeof onClose === 'function') {
				fadeOut(thisModal, onClose);
			} else {
				fadeOut(thisModal);
			}

			removeTrapFocus();
			return false;
		});

		thisModal.querySelectorAll(window.theme.focusable)[0].focus();
	}

	function Listeners() {
		this.entries = [];
	}

	Listeners.prototype.add = function (element, event, fn) {
		this.entries.push({ element: element, event: event, fn: fn });
		if (element) {
			element.addEventListener(event, fn);
		}
	};

	Listeners.prototype.removeAll = function () {
		this.entries = this.entries.filter(function (listener) {
			listener.element.removeEventListener(listener.event, listener.fn);
			return false;
		});
	};

	/**
	 * Find a match in the project JSON (using a ID number) and return the variant (as an Object)
	 * @param {Object} product Product JSON object
	 * @param {Number} value Accepts Number (e.g. 6908023078973)
	 * @returns {Object} The variant object once a match has been successful. Otherwise null will be return
	 */

	/**
	 * Convert the Object (with 'name' and 'value' keys) into an Array of values, then find a match & return the variant (as an Object)
	 * @param {Object} product Product JSON object
	 * @param {Object} collection Object with 'name' and 'value' keys (e.g. [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }])
	 * @returns {Object || null} The variant object once a match has been successful. Otherwise null will be returned
	 */
	function getVariantFromSerializedArray(product, collection) {
		_validateProductStructure(product);

		// If value is an array of options
		var optionArray = _createOptionArrayFromOptionCollection(product, collection);
		return getVariantFromOptionArray(product, optionArray);
	}

	/**
	 * Find a match in the project JSON (using Array with option values) and return the variant (as an Object)
	 * @param {Object} product Product JSON object
	 * @param {Array} options List of submitted values (e.g. ['36', 'Black'])
	 * @returns {Object || null} The variant object once a match has been successful. Otherwise null will be returned
	 */
	function getVariantFromOptionArray(product, options) {
		_validateProductStructure(product);
		_validateOptionsArray(options);

		var result = product.variants.filter(function (variant) {
			return options.every(function (option, index) {
				return variant.options[index] === option;
			});
		});

		return result[0] || null;
	}

	/**
	 * Creates an array of selected options from the object
	 * Loops through the project.options and check if the "option name" exist (product.options.name) and matches the target
	 * @param {Object} product Product JSON object
	 * @param {Array} collection Array of object (e.g. [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }])
	 * @returns {Array} The result of the matched values. (e.g. ['36', 'Black'])
	 */
	function _createOptionArrayFromOptionCollection(product, collection) {
		_validateProductStructure(product);
		_validateSerializedArray(collection);

		var optionArray = [];

		collection.forEach(function (option) {
			for (var i = 0; i < product.options.length; i++) {
				var name = product.options[i].name || product.options[i];
				if (name.toLowerCase() === option.name.toLowerCase()) {
					optionArray[i] = option.value;
					break;
				}
			}
		});

		return optionArray;
	}

	/**
	 * Check if the product data is a valid JS object
	 * Error will be thrown if type is invalid
	 * @param {object} product Product JSON object
	 */
	function _validateProductStructure(product) {
		if (typeof product !== 'object') {
			throw new TypeError(product + ' is not an object.');
		}

		if (Object.keys(product).length === 0 && product.constructor === Object) {
			throw new Error(product + ' is empty.');
		}
	}

	/**
	 * Validate the structure of the array
	 * It must be formatted like jQuery's serializeArray()
	 * @param {Array} collection Array of object [{ name: "Size", value: "36" }, { name: "Color", value: "Black" }]
	 */
	function _validateSerializedArray(collection) {
		if (!Array.isArray(collection)) {
			throw new TypeError(collection + ' is not an array.');
		}

		if (collection.length === 0) {
			throw new Error(collection + ' is empty.');
		}

		if (collection[0].hasOwnProperty('name')) {
			if (typeof collection[0].name !== 'string') {
				throw new TypeError('Invalid value type passed for name of option ' + collection[0].name + '. Value should be string.');
			}
		} else {
			throw new Error(collection[0] + 'does not contain name key.');
		}
	}

	/**
	 * Validate the structure of the array
	 * It must be formatted as list of values
	 * @param {Array} collection Array of object (e.g. ['36', 'Black'])
	 */
	function _validateOptionsArray(options) {
		if (Array.isArray(options) && typeof options[0] === 'object') {
			throw new Error(options + 'is not a valid array of options.');
		}
	}

	var selectors$f = {
		idInput: '[name="id"]',
		optionInput: '[name^="options"]',
		quantityInput: '[name="quantity"]',
		propertyInput: '[name^="properties"]',
	};

	/**
	 * Constructor class that creates a new instance of a product form controller.
	 *
	 * @param {Element} element - DOM element which is equal to the <form> node wrapping product form inputs
	 * @param {Object} product - A product object
	 * @param {Object} options - Optional options object
	 * @param {Function} options.onOptionChange - Callback for whenever an option input changes
	 * @param {Function} options.onQuantityChange - Callback for whenever an quantity input changes
	 * @param {Function} options.onPropertyChange - Callback for whenever a property input changes
	 * @param {Function} options.onFormSubmit - Callback for whenever the product form is submitted
	 */
	function ProductForm(element, product, options) {
		this.element = element;
		this.product = _validateProductObject(product);

		options = options || {};

		this._listeners = new Listeners();
		this._listeners.add(this.element, 'submit', this._onSubmit.bind(this, options));

		this.optionInputs = this._initInputs(selectors$f.optionInput, options.onOptionChange);

		this.quantityInputs = this._initInputs(selectors.quantityInput, options.onQuantityChange);

		console.log('pfl');
		this.propertyInputs = this._initInputs(selectors$f.propertyInput, options.onPropertyChange);
	}

	/**
	 * Cleans up all event handlers that were assigned when the Product Form was constructed.
	 * Useful for use when a section needs to be reloaded in the theme editor.
	 */
	ProductForm.prototype.destroy = function () {
		this._listeners.removeAll();
	};

	/**
	 * Getter method which returns the array of currently selected option values
	 *
	 * @returns {Array} An array of option values
	 */
	ProductForm.prototype.options = function () {
		return _serializeInputValues(this.optionInputs, function (item) {
			var regex = /(?:^(options\[))(.*?)(?:\])/;
			item.name = regex.exec(item.name)[2]; // Use just the value between 'options[' and ']'
			return item;
		});
	};

	/**
	 * Getter method which returns the currently selected variant, or `null` if variant
	 * doesn't exist.
	 *
	 * @returns {Object|null} Variant object
	 */
	ProductForm.prototype.variant = function () {
		return getVariantFromSerializedArray(this.product, this.options());
	};

	/**
	 * Getter method which returns a collection of objects containing name and values
	 * of property inputs
	 *
	 * @returns {Array} Collection of objects with name and value keys
	 */
	ProductForm.prototype.properties = function () {
		return _serializeInputValues(this.propertyInputs, function (item) {
			var regex = /(?:^(properties\[))(.*?)(?:\])/;
			item.name = regex.exec(item.name)[2]; // Use just the value between 'properties[' and ']'
			return item;
		});
	};

	/**
	 * Getter method which returns the current quantity or 1 if no quantity input is
	 * included in the form
	 *
	 * @returns {Array} Collection of objects with name and value keys
	 */
	ProductForm.prototype.quantity = function () {
		return this.quantityInputs[0] ? Number.parseInt(this.quantityInputs[0].value, 10) : 1;
	};

	// Private Methods
	// -----------------------------------------------------------------------------
	ProductForm.prototype._setIdInputValue = function (value) {
		var idInputElement = this.element.querySelector(selectors$f.idInput);

		if (!idInputElement) {
			idInputElement = document.createElement('input');
			idInputElement.type = 'hidden';
			idInputElement.name = 'id';
			this.element.appendChild(idInputElement);
		}

		idInputElement.value = value.toString();
	};

	ProductForm.prototype._onSubmit = function (options, event) {
		event.dataset = this._getProductFormEventData();

		this._setIdInputValue(event.dataset.variant.id);

		if (options.onFormSubmit) {
			options.onFormSubmit(event);
		}
	};

	ProductForm.prototype._onFormEvent = function (cb) {
		if (typeof cb === 'undefined') {
			return Function.prototype;
		}

		return function (event) {
			event.dataset = this._getProductFormEventData();
			cb(event);
		}.bind(this);
	};

	ProductForm.prototype._initInputs = function (selector, cb) {
		if (!this.element || !this.element.querySelector(selector)) return;

		var elements = Array.prototype.slice.call(this.element.querySelectorAll(selector));

		return elements.map(
			function (element) {
				this._listeners.add(element, 'change', this._onFormEvent(cb));
				return element;
			}.bind(this)
		);
	};

	ProductForm.prototype._getProductFormEventData = function () {
		return {
			options: this.options(),
			variant: this.variant(),
			properties: this.properties(),
			//quantity: this.quantity(),
		};
	};

	function _serializeInputValues(inputs, transform) {
		if (!inputs) return [];
		return inputs.reduce(function (options, input) {
			if (
				input.checked || // If input is a checked (means type radio or checkbox)
				(input.type !== 'radio' && input.type !== 'checkbox') // Or if its any other type of input
			) {
				options.push(transform({ name: input.name, value: input.value }));
			}

			return options;
		}, []);
	}

	function _validateProductObject(product) {
		if (typeof product !== 'object') {
			throw new TypeError(product + ' is not an object.');
		}

		if (typeof product.variants[0].options === 'undefined') {
			throw new TypeError(
				'Product object is invalid. Make sure you use the product object that is output from {{ product | json }} or from the http://[your-product-url].js route'
			);
		}

		return product;
	}

	const selectors$g = {
		product: '[data-product]',
		productForm: '[data-product-form]',
		addToCart: '[data-add-to-cart]',
		addToCartText: '[data-add-to-cart-text]',
		comparePrice: '[data-compare-price]',
		comparePriceText: '[data-compare-text]',
		formWrapper: '[data-form-wrapper]',
		originalSelectorId: '[data-product-select]',
		priceWrapper: '[data-price-wrapper]',
		productSlideshow: '[data-product-slideshow]',
		productImage: '[data-product-image]',
		productJson: '[data-product-json]',
		productPrice: '[data-product-price]',
		unitPrice: '[data-product-unit-price]',
		unitBase: '[data-product-base]',
		unitWrapper: '[data-product-unit]',
		preOrderTag: '_preorder',
		sliderEnabled: 'flickity-enabled',
		productSlide: '.product__slide',
		dataCartBar: 'data-cart-bar',
		dataTallLayout: 'data-tall-layout',
		dataEnableHistoryState: 'data-enable-history-state',
		dataImageId: 'data-image-id',
		cartBar: '#cart-bar',
	};

	const classes$3 = {
		hide: 'hide',
		variantSoldOut: 'variant--soldout',
		variantUnavailable: 'variant--unavailabe',
		productPriceSale: 'product__price--sale',
	};

	class ProductAddForm {
		constructor(section) {
			this.section = section;
			this.container = section.container;
			this.tallLayout = this.container.getAttribute(selectors$g.dataTallLayout) === 'true';
			this.product = this.container.querySelector(selectors$g.product);

			// Stop parsing if we don't have the product
			if (!this.product) {
				return;
			}

			this.productForm = this.container.querySelector(selectors$g.productForm);
			this.enableHistoryState = this.container.getAttribute(selectors$g.dataEnableHistoryState) === 'true';

			if (this.container.getAttribute(selectors$g.dataCartBar) === 'true') {
				this.cartBar = this.container.querySelector(selectors$g.cartBar);
				this.cartBarProductForm = this.cartBar.querySelector(selectors$g.productForm);
			}

			const counter = new QuantityCounter(this.container);
			counter.init();

			this.init();

			this.hasUnitPricing = this.container.querySelector(selectors$g.unitWrapper);
		}

		init() {
			let productJSON = null;
			const productElemJSON = this.container.querySelector(selectors$g.productJson);
			if (productElemJSON) {
				productJSON = productElemJSON.innerHTML;
			}
			if (productJSON) {
				this.productJSON = JSON.parse(productJSON);
				this.linkForm();
			} else {
				console.error('Missing product JSON');
			}
		}

		destroy() {
			this.productForm.destroy();

			if (this.container.getAttribute(selectors$g.dataCartBar) === 'true') {
				this.cartBarProductForm.destroy();
			}
		}

		linkForm() {
			this.productForm = new ProductForm(this.productForm, this.productJSON, {
				onOptionChange: this.onOptionChange.bind(this),
			});

			if (this.container.getAttribute(selectors$g.dataCartBar) === 'true') {
				this.cartBarProductForm = new ProductForm(this.cartBarProductForm, this.productJSON, {
					onOptionChange: this.onOptionChange.bind(this),
				});
			}
		}

		onOptionChange(evt) {
			this.updateAddToCartState(evt);
			this.updateProductImage(evt);
			this.updateProductPrices(evt);

			if (this.enableHistoryState) {
				this.updateHistoryState(evt);
			}
		}

		updateAddToCartState(evt) {
			const variant = evt.dataset.variant;
			let addText = theme.strings.addToCart;
			const priceWrapper = this.container.querySelectorAll(selectors$g.priceWrapper);
			const addToCart = this.container.querySelectorAll(selectors$g.addToCart);
			const addToCartText = this.container.querySelectorAll(selectors$g.addToCartText);
			const formWrapper = this.container.querySelectorAll(selectors$g.formWrapper);

			if (this.productJSON.tags.includes(selectors$g.preOrderTag)) {
				addText = theme.strings.preOrder;
			}

			if (priceWrapper.length && variant) {
				priceWrapper.forEach((element) => {
					element.classList.remove(classes$3.hide);
				});
			}

			if (addToCart.length) {
				addToCart.forEach((element) => {
					if (variant) {
						if (variant.available) {
							element.disabled = false;
						} else {
							element.disabled = true;
						}
					} else {
						element.disabled = true;
					}
				});
			}

			if (addToCartText.length) {
				addToCartText.forEach((element) => {
					if (variant) {
						if (variant.available) {
							element.innerHTML = addText;
						} else {
							element.innerHTML = theme.strings.soldOut;
						}
					} else {
						element.innerHTML = theme.strings.unavailable;
					}
				});
			}

			if (formWrapper.length) {
				formWrapper.forEach((element) => {
					if (variant) {
						if (variant.available) {
							element.classList.remove(classes$3.variantSoldOut, classes$3.variantUnavailable);
						} else {
							element.classList.add(classes$3.variantSoldOut);
							element.classList.remove(classes$3.variantUnavailable);
						}
						const formSelect = element.querySelector(selectors$g.originalSelectorId);
						if (formSelect) {
							formSelect.value = variant.id;
						}
					} else {
						element.classList.add(classes$3.variantUnavailable);
						element.classList.remove(classes$3.variantSoldOut);
					}
				});
			}
		}

		updateHistoryState(evt) {
			const variant = evt.dataset.variant;
			const location = window.location.href;
			if (variant && location.includes('/product')) {
				const url = new window.URL(location);
				const params = url.searchParams;
				params.set('variant', variant.id);
				url.search = params.toString();
				const urlString = url.toString();
				window.history.replaceState({ path: urlString }, '', urlString);
			}
		}

		getBaseUnit(variant) {
			return variant.unit_price_measurement.reference_value === 1
				? variant.unit_price_measurement.reference_unit
				: variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
		}

		updateProductPrices(evt) {
			const variant = evt.dataset.variant;
			const priceWrappers = this.container.querySelectorAll(selectors$g.priceWrapper);

			priceWrappers.forEach((wrap) => {
				const comparePrice = wrap.querySelector(selectors$g.comparePrice);
				const productPrice = wrap.querySelector(selectors$g.productPrice);
				const comparePriceText = wrap.querySelector(selectors$g.comparePriceText);

				if (variant) {
					productPrice.innerHTML = themeCurrency.formatMoney(variant.price, theme.moneyFormat);
				}

				if (variant && variant.compare_at_price > variant.price) {
					comparePrice.innerHTML = themeCurrency.formatMoney(variant.compare_at_price, theme.moneyFormat);
					comparePrice.classList.remove(classes$3.hide);
					comparePriceText.classList.remove(classes$3.hide);
					productPrice.classList.add(classes$3.productPriceSale);
				} else if (comparePrice) {
					comparePrice.innerHTML = '';
					comparePrice.classList.add(classes$3.hide);
					comparePriceText.classList.add(classes$3.hide);
					productPrice.classList.remove(classes$3.productPriceSale);
				}
			});

			if (this.hasUnitPricing) {
				this.updateProductUnits(evt);
			}
		}

		updateProductUnits(evt) {
			const variant = evt.dataset.variant;

			if (typeof variant.unit_price !== 'undefined') {
				const price = themeCurrency.formatMoney(variant.unit_price, theme.moneyFormat);
				const base = this.getBaseUnit(variant);
				this.container.querySelector(selectors$g.unitPrice).innerHTML = price;
				this.container.querySelector(selectors$g.unitBase).innerHTML = base;
				showElement(this.container.querySelector(selectors$g.unitWrapper));
			} else {
				hideElement(this.container.querySelector(selectors$g.unitWrapper));
			}
		}

		updateProductImage(evt) {
			const variant = evt.dataset.variant;

			if (variant) {
				// Update variant image, if one is set
				if (variant.featured_media) {
					const newImg = this.container.querySelector(
						`${selectors$g.productImage}[${selectors$g.dataImageId}="${variant.featured_media.id}"]`
					);
					const newImageParent = newImg.closest(selectors$g.productSlide);
					// If we have a mobile breakpoint or the tall layout is disabled,
					// just switch the slideshow.
					if (newImageParent) {
						const newImagePos = Array.from(newImageParent.parentElement.children).indexOf(newImageParent);
						const slider = this.container.querySelector(selectors$g.productSlideshow);

						if (slider && slider.classList.contains(selectors$g.sliderEnabled)) {
							const flkty = FlickityFade.data(slider).select(newImagePos);
						}

						if (!theme.variables.bpSmall && this.tallLayout) {
							// We know its a tall layout, if it's sticky
							// scroll to the images
							// Scroll to/reorder image unless it's the first photo on load

							if (newImagePos !== 0) {
								// Scroll to variant image
								window.scrollTo({
									top: newImg.getBoundingClientRect().top + window.scrollY,
									left: 0,
									behavior: 'smooth',
								});
							}
						}
					}
				}
			}
		}
	}

	const productFormSection = {
		onLoad() {
			this.section = new ProductAddForm(this);
		},
		onUnload: function () {
			if (typeof this.section.unload === 'function') {
				this.section.unload();
			}
		},
	};

	const selectors$h = {
		body: 'body',
		globalWrapper: '[data-quickview-modal-global]',
		modal: '[data-quickview-modal]',
		outer: '[data-product-grid-item]',
		content: '[data-quickview-content]',
		handle: 'data-trigger-quickview',
		productSlides: '.product__slides',
		sliderEnabled: 'flickity-enabled',
		reviews: 'data-reviews',
	};
	const defaultHTML = '<div class="loader loader--quickview"><div class="loader-indeterminate"></div></div>';

	/**
	 * Button constructor, triggered for each button by Quickview
	 */
	class QuickviewButton {
		constructor(button, id) {
			this.button = button;
			this.outerSectionId = id;
			this.handle = this.button.getAttribute(selectors$h.handle);
			this.unique = this.handle + '_' + this.outerSectionId;
			this.outer = this.button.closest(selectors$h.outer);
			this.modal = this.outer.querySelector(selectors$h.modal);
			this.globalWrapper = document.querySelectorAll(selectors$h.globalWrapper)[0];
			this.content = this.modal.querySelector(selectors$h.content);
			this.clone = this.modal;
			this.cloneContent = this.content;
			this.body = document.querySelector(selectors$h.body);
			this.accessibility = a11y;
			this.form = {};
			this.init();
		}

		init() {
			this.initButton();
			this.initFetch();
		}

		initFetch() {
			const self = this;
			this.modal.addEventListener(
				'quickview',
				function () {
					self.renderProduct();
				},
				false
			);
		}

		renderProduct() {
			const self = this;
			this.clone = this.modal.cloneNode(true);
			this.globalWrapper.append(this.clone);
			this.cloneContent = this.clone.querySelector(selectors$h.content);

			showModal(self.clone, self.onOpen.bind(self), self.onClose.bind(self));

			const root = window.theme.routes.root === '/' ? '' : window.theme.routes.root;

			fetch(root + '/products/' + self.handle + '?view=ajax_quickview')
				.then(function (response) {
					return response.text();
				})
				.then(function (data) {
					const regex = /CORGIPOMSKIMELONFELLOW/gi;
					const newData = data.replace(regex, self.unique);
					self.cloneContent.innerHTML = newData;
					self.onLoaded();
				})
				.catch(function () {
					console.log('error');
				});
		}

		initButton() {
			const self = this;
			const initButtons = this.outer.querySelectorAll(`[${selectors$h.handle}='${this.handle}']`);
			if (initButtons.length) {
				initButtons.forEach((element) => {
					element.addEventListener('click', function (e) {
						e.preventDefault();

						const eventQuickview = new CustomEvent('quickview', {
							handle: self.handle,
						});

						self.modal.dispatchEvent(eventQuickview);
					});
				});
			}
		}

		onLoaded() {
			const slider = this.cloneContent.querySelector(selectors$h.productSlides);
			const hasSlider = slider.classList.contains(selectors$h.sliderEnabled);
			const showReviews = this.cloneContent.getAttribute(selectors$h.reviews) !== 'false';
			const reviewsAppInstalled = typeof window.SPR === 'function';
			const section = {
				id: this.unique,
				container: this.cloneContent,
				type: 'quickview',
			};

			if (hasSlider) {
				window.dispatchEvent(new Event('resize'));
			} else if (typeof window.theme.mediaInstances[this.id] === 'undefined') {
				window.theme.mediaInstances[this.unique] = new Media(section);
				window.theme.mediaInstances[this.unique].init();
			} else {
				window.theme.mediaInstances[this.unique].initSlider();
			}

			this.form = new ProductAddForm(section);
			const counter = new QuantityCounter(section.container);
			counter.init();

			if (showReviews && reviewsAppInstalled) {
				window.SPR.initDomEls();
				window.SPR.loadProducts();
				window.SPR.loadBadges();
			}

			const event = new CustomEvent('modal-is-loaded', {
				bubbles: true,
			});

			this.globalWrapper.dispatchEvent(event);

			this.accessibility.trapFocus(this.clone, {
				elementToFocus: this.clone.querySelector('a:first-child, input:first-child'),
			});

			if (
				typeof window.Shopify !== 'undefined' &&
				typeof window.Shopify.PaymentButton !== 'undefined' &&
				typeof window.Shopify.PaymentButton.init !== 'undefined'
			) {
				window.Shopify.PaymentButton.init();
			}
		}

		onOpen() {
			// unused for now
		}

		onClose() {
			if (Object.keys(this.form).length !== 0 && this.form.constructor === Object) {
				this.form.destroy();
			}
			this.content.innerHTML = defaultHTML;

			this.accessibility.removeTrapFocus();

			if (this.body.classList.contains('is-focused')) {
				const button = document.querySelector(`[data-trigger-quickview="${this.handle}"]`);

				setTimeout(() => {
					button.focus();
				}, 200);
			}

			while (this.globalWrapper.firstChild) this.globalWrapper.removeChild(this.globalWrapper.firstChild);
		}
	}

	var selectors$i = {
		trigger: '[data-trigger-quickview]',
		dataSectionId: 'data-section-id',
	};
	var sections$5 = {};

	class Quickview {
		constructor(section) {
			this.container = section.container;
			this.id = this.container.getAttribute(selectors$i.dataSectionId);

			this.init();
		}

		init() {
			sections$5[this.id] = [];
			const self = this;
			const buttons = this.container.querySelectorAll(selectors$i.trigger);

			if (buttons.length) {
				buttons.forEach((element) => {
					const buttonClass = new QuickviewButton(element, self.id);
					sections$5[self.id].push(buttonClass);
				});
			}
		}

		onUnload() {
			const buttonsArray = sections$5[this.id];
			for (let i = 0, len = buttonsArray.length; i < len; i++) {
				const button = buttonsArray[i];
				if (typeof button.unload === 'function') {
					button.unload();
				}
			}
		}
	}

	const quickviewSection = {
		onLoad() {
			this.section = new Quickview(this);
		},
		onUnload() {
			if (typeof this.section.unload === 'function') {
				this.section.unload();
			}
		},
	};

	var selectors$j = {
		sort: '[data-sort-enabled]',
		sortLinks: '[data-sort-link]',
		sortValue: 'data-value',
		collectionNavGrouped: '.collection-nav--grouped',
		collectionSidebarHeading: '.collection__sidebar__heading',
		linkAdd: '.link--add',
		linkRemove: '.link--remove',
	};

	class Collection {
		constructor(section) {
			this.container = section.container;
			this.sort = this.container.querySelector(selectors$j.sort);
			this.sortLinks = this.container.querySelectorAll(selectors$j.sortLinks);
			this.init();
		}

		init() {
			if (this.sort) {
				this.initClick();
			}
			this.removeUnusableFilters();
		}

		onClick(e) {
			const sort = e.currentTarget.getAttribute(selectors$j.sortValue);
			const url = new window.URL(window.location.href);
			const params = url.searchParams;
			params.set('sort_by', sort);
			url.search = params.toString();
			window.location.replace(url.toString());
		}

		initClick() {
			this.sortLinks.forEach((link) => {
				link.addEventListener(
					'click',
					function (e) {
						this.onClick(e);
					}.bind(this)
				);
			});
		}

		removeUnusableFilters() {
			const collectionNavGrouped = this.container.querySelectorAll(selectors$j.collectionNavGrouped);
			if (collectionNavGrouped.length > 0) {
				collectionNavGrouped.forEach((element) => {
					const linkAdd = element.querySelector(selectors$j.linkAdd);
					const linkRemove = element.querySelector(selectors$j.linkRemove);

					if (!linkAdd && !linkRemove) {
						hideElement(element);
						hideElement(element.parentElement.querySelector(selectors$j.collectionSidebarHeading));
					}
				});
			}
		}
	}

	const collectionSection = {
		onLoad() {
			this.collection = new Collection(this);
		},
		onUnload: function () {
			if (typeof this.collection.unload === 'function') {
				this.collection.unload();
			}
		},
	};

	register('collection', [parallaxHero, productGridReviews, quickviewSection, collectionSection, popoutSection]);

	const selectors$k = {
		frame: '[data-ticker-frame]',
		scale: '[data-ticker-scale]',
		text: '[data-ticker-text]',
		clone: 'data-clone',
		animationClass: 'ticker--animated',
		unloadedClass: 'ticker--unloaded',
		comparitorClass: 'ticker__comparitor',
	};

	const sections$6 = {};

	class Ticker {
		constructor(el) {
			this.frame = el;
			this.scale = this.frame.querySelector(selectors$k.scale);
			this.text = this.frame.querySelector(selectors$k.text);

			this.comparitor = this.text.cloneNode(true);
			this.comparitor.classList.add(selectors$k.comparitorClass);
			this.frame.appendChild(this.comparitor);
			this.scale.classList.remove(selectors$k.unloadedClass);
			this.listen();
		}
		unload() {
			document.removeEventListener('theme:resize', this.checkWidth);
		}
		listen() {
			document.addEventListener('theme:resize', this.checkWidth.bind(this));
			this.checkWidth();
		}
		checkWidth() {
			if (this.frame.clientWidth < this.comparitor.clientWidth) {
				this.text.classList.add(selectors$k.animationClass);
				if (this.scale.childElementCount === 1) {
					this.clone = this.text.cloneNode(true);
					this.clone.setAttribute('aria-hidden', true);
					this.clone.setAttribute(selectors$k.clone, '');
					this.scale.appendChild(this.clone);
				}
			} else {
				let clone = this.scale.querySelector(`[${selectors$k.clone}]`);
				if (clone) {
					this.scale.removeChild(clone);
				}
				this.text.classList.remove(selectors$k.animationClass);
			}
		}
	}

	const ticker = {
		onLoad() {
			sections$6[this.id] = [];
			const el = this.container.querySelectorAll(selectors$k.frame);
			el.forEach((el) => {
				sections$6[this.id].push(new Ticker(el));
			});
		},
		onUnload: function () {
			sections$6[this.id].forEach((el) => {
				if (typeof el.unload === 'function') {
					el.unload();
				}
			});
		},
	};

	register('announcement', ticker);

	var selectors$l = {
		body: 'body',
		html: 'html',
		drawerWrappper: '[data-drawer]',
		underlay: '[data-drawer-underlay]',
		stagger: '[data-stagger-animation]',
		wrapper: '[data-header-transparent]',
		transparent: 'data-header-transparent',
		drawerToggle: 'data-drawer-toggle',
		focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
	};

	var classes$4 = {
		isVisible: 'drawer--visible',
	};

	var sections$7 = {};

	class Drawer {
		constructor(el) {
			this.drawer = el;
			this.underlay = this.drawer.querySelector(selectors$l.underlay);
			this.wrapper = this.drawer.closest(selectors$l.wrapper);
			this.transparent = this.wrapper.getAttribute(selectors$l.transparent) !== 'false';
			this.key = this.drawer.dataset.drawer;
			const btnSelector = `[${selectors$l.drawerToggle}='${this.key}']`;
			this.buttons = document.querySelectorAll(btnSelector);
			this.staggers = this.drawer.querySelectorAll(selectors$l.stagger);
			this.body = document.querySelector(selectors$l.body);
			this.html = document.querySelector(selectors$l.html);

			this.connectToggle();
			this.connectDrawer();
			this.closers();
			this.staggerChildAnimations();
		}

		connectToggle() {
			this.buttons.forEach((btn) => {
				btn.addEventListener(
					'click',
					function () {
						this.drawer.dispatchEvent(
							new CustomEvent('theme:menu:toggle', {
								bubbles: false,
							})
						);
					}.bind(this)
				);
			});
		}

		connectDrawer() {
			this.drawer.addEventListener(
				'theme:menu:toggle',
				function () {
					if (this.drawer.classList.contains(classes$4.isVisible)) {
						this.hideDrawer();
					} else {
						this.showDrawer();
					}
				}.bind(this)
			);

			this.html.addEventListener('cart:open', () => {
				this.hideDrawer();
			});
		}

		staggerChildAnimations() {
			this.staggers.forEach((el) => {
				const children = el.querySelectorAll(':scope > * > [data-animates]');
				children.forEach((child, index) => {
					child.style.transitionDelay = `${index * 50 + 10}ms`;
				});
			});
		}

		watchFocus(evt) {
			let drawerInFocus = this.wrapper.contains(evt.target);
			if (!drawerInFocus) {
				this.hideDrawer();
			}
		}

		closers() {
			this.drawer.addEventListener(
				'keyup',
				function (evt) {
					if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
						return;
					}
					this.hideDrawer();
					this.buttons[0].focus();
				}.bind(this)
			);

			this.underlay.addEventListener(
				'click',
				function () {
					this.hideDrawer();
				}.bind(this)
			);
		}

		showDrawer() {
			this.transparent = this.wrapper.getAttribute(selectors$l.transparent) !== 'false';
			this.buttons.forEach((el) => el.setAttribute('aria-expanded', true));
			this.buttons.forEach((el) => el.classList.add(classes$4.isVisible));
			this.drawer.classList.add(classes$4.isVisible);
			this.drawer.querySelector(selectors$l.focusable).focus();
			this.wrapper.setAttribute(selectors$l.transparent, false);
			document.addEventListener('focusin', this.watchFocus.bind(this));

			setTimeout(() => {
				document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
			}, 20);
		}

		hideDrawer() {
			this.buttons.forEach((el) => el.setAttribute('aria-expanded', true));
			this.buttons.forEach((el) => el.classList.remove(classes$4.isVisible));
			this.drawer.classList.remove(classes$4.isVisible);
			this.wrapper.setAttribute(selectors$l.transparent, this.transparent);
			document.removeEventListener('focusin', this.watchFocus);

			document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
		}
	}

	const drawer = {
		onLoad() {
			sections$7[this.id] = [];
			const els = this.container.querySelectorAll(selectors$l.drawerWrappper);
			els.forEach((el) => {
				sections$7[this.id].push(new Drawer(el));
			});
		},
		onUnload: function () {
			sections$7[this.id].forEach((el) => {
				if (typeof el.unload === 'function') {
					el.unload();
				}
			});
		},
	};

	const selectors$m = {
		announcement: '[data-announcement-wrapper]',
		transparent: 'data-header-transparent',
		header: '[data-header-wrapper] header',
		headerIsNotFixed: '[data-header-sticky="static"]',
	};

	const classes$5 = {
		stuck: 'js__header__stuck',
		stuckAnimated: 'js__header__stuck--animated',
		triggerAnimation: 'js__header__stuck--trigger-animation',
		stuckBackdrop: 'js__header__stuck__backdrop',
		headerIsNotVisible: 'is-not-visible',
	};

	let sections$8 = {};

	class Sticky {
		constructor(el) {
			this.wrapper = el;
			this.type = this.wrapper.dataset.headerSticky;
			this.transparent = this.wrapper.dataset.headerTransparent;
			this.sticks = this.type === 'sticky';
			this.static = this.type === 'static';
			this.win = window;
			this.animated = this.type === 'directional';
			this.currentlyStuck = false;
			this.cls = this.wrapper.classList;
			const announcementEl = document.querySelector(selectors$m.announcement);
			const announcementHeight = announcementEl ? announcementEl.clientHeight : 0;
			this.headerHeight = document.querySelector(selectors$m.header).clientHeight;
			this.blur = this.headerHeight + announcementHeight;
			this.stickDown = this.headerHeight + announcementHeight;
			this.stickUp = announcementHeight;
			if (this.wrapper.getAttribute(selectors$m.transparent) !== 'false') {
				this.blur = announcementHeight;
			}
			if (this.sticks) {
				this.stickDown = announcementHeight;
				this.scrollDownInit();
			}

			if (this.static) {
				this.checkIsVisible();
			}
			this.listen();
		}

		unload() {
			document.removeEventListener('theme:scroll', this.listen);
			document.removeEventListener('theme:scroll:up', this.scrollUpDirectional);
			document.removeEventListener('theme:scroll:down', this.scrollDownDirectional);
		}

		listen() {
			if (this.sticks || this.animated) {
				document.addEventListener('theme:scroll', (e) => {
					if (e.detail.down) {
						if (!this.currentlyStuck && e.detail.position > this.stickDown) {
							this.stickSimple();
						}
						if (!this.currentlyBlurred && e.detail.position > this.blur) {
							this.addBlur();
						}
					} else {
						if (e.detail.position <= this.stickUp) {
							this.unstickSimple();
						}
						if (e.detail.position <= this.blur) {
							this.removeBlur();
						}
					}
				});
			}
			if (this.animated) {
				document.addEventListener('theme:scroll:up', this.scrollUpDirectional.bind(this));
				document.addEventListener('theme:scroll:down', this.scrollDownDirectional.bind(this));
			}
		}

		stickSimple() {
			if (this.animated) {
				this.cls.add(classes$5.stuckAnimated);
			}
			this.cls.add(classes$5.stuck);
			this.wrapper.setAttribute(selectors$m.transparent, false);
			this.currentlyStuck = true;
		}

		unstickSimple() {
			if (!document.documentElement.hasAttribute('data-scroll-locked')) {
				// check for scroll lock
				this.cls.remove(classes$5.stuck);
				this.wrapper.setAttribute(selectors$m.transparent, this.transparent);
				if (this.animated) {
					this.cls.remove(classes$5.stuckAnimated);
				}
				this.currentlyStuck = false;
			}
		}

		scrollDownInit() {
			if (window.scrollY > this.stickDown) {
				this.stickSimple();
			}
			if (window.scrollY > this.blur) {
				this.addBlur();
			}
		}

		stickDirectional() {
			this.cls.add(classes$5.triggerAnimation);
		}

		unstickDirectional() {
			this.cls.remove(classes$5.triggerAnimation);
		}

		scrollDownDirectional() {
			this.unstickDirectional();
		}

		scrollUpDirectional() {
			if (window.scrollY <= this.stickDown) {
				this.unstickDirectional();
			} else {
				this.stickDirectional();
			}
		}

		addBlur() {
			this.cls.add(classes$5.stuckBackdrop);
			this.currentlyBlurred = true;
		}

		removeBlur() {
			this.cls.remove(classes$5.stuckBackdrop);
			this.currentlyBlurred = false;
		}

		checkIsVisible() {
			const self = this;
			const header = document.querySelector(selectors$m.headerIsNotFixed);

			document.addEventListener('theme:scroll', () => {
				const currentScroll = self.win.pageYOffset;

				header.classList.toggle(classes$5.headerIsNotVisible, currentScroll >= self.headerHeight);
			});
		}
	}

	const stickyHeader = {
		onLoad() {
			sections$8 = new Sticky(this.container);
		},
		onUnload: function () {
			if (typeof sections$8.unload === 'function') {
				sections$8.unload();
			}
		},
	};

	const selectors$n = {
		disclosureToggle: 'data-hover-disclosure-toggle',
		disclosureWrappper: '[data-hover-disclosure]',
		link: '[data-top-link]',
		meganavVisible: 'meganav--visible',
		wrapper: '[data-header-wrapper]',
		stagger: '[data-stagger]',
		staggerPair: '[data-stagger-first]',
		staggerAfter: '[data-stagger-second]',
		staggerImage: '[data-grid-item], [data-header-image]',
		focusable: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
	};

	const classes$6 = {
		isVisible: 'is-visible',
	};

	let sections$9 = {};
	let disclosures = {};

	class HoverDisclosure {
		constructor(el) {
			this.disclosure = el;
			this.wrapper = el.closest(selectors$n.wrapper);
			this.key = this.disclosure.id;
			const btnSelector = `[${selectors$n.disclosureToggle}='${this.key}']`;
			this.trigger = document.querySelector(btnSelector);
			this.link = this.trigger.querySelector(selectors$n.link);
			this.grandparent = this.trigger.classList.contains('grandparent');

			this.trigger.setAttribute('aria-haspopup', true);
			this.trigger.setAttribute('aria-expanded', false);
			this.trigger.setAttribute('aria-controls', this.key);

			this.connectHoverToggle();
			this.handleTablets();
			this.staggerChildAnimations();
		}

		onBlockSelect(evt) {
			if (this.disclosure.contains(evt.target)) {
				this.showDisclosure();
			}
		}

		onBlockDeselect(evt) {
			if (this.disclosure.contains(evt.target)) {
				this.hideDisclosure();
			}
		}

		showDisclosure() {
			if (this.grandparent) {
				this.wrapper.classList.add(selectors$n.meganavVisible);
			} else {
				this.wrapper.classList.remove(selectors$n.meganavVisible);
			}
			this.trigger.setAttribute('aria-expanded', true);
			this.trigger.classList.add(classes$6.isVisible);
			this.disclosure.classList.add(classes$6.isVisible);
		}

		hideDisclosure() {
			this.disclosure.classList.remove(classes$6.isVisible);
			this.trigger.classList.remove(classes$6.isVisible);
			this.trigger.setAttribute('aria-expanded', false);
			this.wrapper.classList.remove(selectors$n.meganavVisible);
		}

		staggerChildAnimations() {
			const simple = this.disclosure.querySelectorAll(selectors$n.stagger);
			simple.forEach((el, index) => {
				el.style.transitionDelay = `${index * 50 + 10}ms`;
			});

			const pairs = this.disclosure.querySelectorAll(selectors$n.staggerPair);
			pairs.forEach((child, i) => {
				const d1 = i * 150;
				child.style.transitionDelay = `${d1}ms`;
				child.parentElement.querySelectorAll(selectors$n.staggerAfter).forEach((grandchild, i2) => {
					const di1 = i2 + 1;
					const d2 = di1 * 20;
					grandchild.style.transitionDelay = `${d1 + d2}ms`;
				});
			});

			const images = this.disclosure.querySelectorAll(selectors$n.staggerImage);
			images.forEach((el, index) => {
				el.style.transitionDelay = `${(index + 1) * 80}ms`;
			});
		}

		handleTablets() {
			// first click opens the popup, second click opens the link
			this.trigger.addEventListener(
				'touchstart',
				function (e) {
					const isOpen = this.disclosure.classList.contains(classes$6.isVisible);
					if (!isOpen) {
						e.preventDefault();
						this.showDisclosure();
					}
				}.bind(this),
				{ passive: true }
			);
		}

		connectHoverToggle() {
			this.trigger.addEventListener('mouseenter', debounce(this.showDisclosure.bind(this), 100));
			this.link.addEventListener('focus', debounce(this.showDisclosure.bind(this), 100));

			this.trigger.addEventListener('mouseleave', debounce(this.hideDisclosure.bind(this), 100));
			this.trigger.addEventListener(
				'focusout',
				debounce(
					function (e) {
						const inMenu = this.trigger.contains(e.relatedTarget);
						if (!inMenu) {
							this.hideDisclosure();
						}
					}.bind(this)
				),
				100
			);
			this.disclosure.addEventListener(
				'keyup',
				function (evt) {
					if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
						return;
					}
					this.hideDisclosure();
				}.bind(this)
			);
		}
	}

	const hoverDisclosure = {
		onLoad() {
			sections$9[this.id] = [];
			disclosures = this.container.querySelectorAll(selectors$n.disclosureWrappper);
			disclosures.forEach((el) => {
				sections$9[this.id].push(new HoverDisclosure(el));
			});
		},
		onBlockSelect(evt) {
			sections$9[this.id].forEach((el) => {
				if (typeof el.onBlockSelect === 'function') {
					el.onBlockSelect(evt);
				}
			});
		},
		onBlockDeselect(evt) {
			sections$9[this.id].forEach((el) => {
				if (typeof el.onBlockDeselect === 'function') {
					el.onBlockDeselect(evt);
				}
			});
		},
		onUnload: function () {
			sections$9[this.id].forEach((el) => {
				if (typeof el.unload === 'function') {
					el.unload();
				}
			});
		},
	};

	const selectors$o = {
		count: 'data-cart-count',
	};

	class Totals {
		constructor(el) {
			this.section = el;
			this.counts = this.section.querySelectorAll(`[${selectors$o.count}]`);
			this.cart = null;
			this.listen();
		}

		listen() {
			document.addEventListener(
				'theme:cart:change',
				function (event) {
					this.cart = event.detail.cart;
					this.update();
				}.bind(this)
			);
		}

		update() {
			if (this.cart) {
				this.counts.forEach((count) => {
					count.setAttribute(selectors$o.count, this.cart.item_count);
					count.innerHTML = `${this.cart.item_count}`;
				});
			}
		}
	}
	const headerTotals = {
		onLoad() {
			new Totals(this.container);
		},
	};

	function fetchProduct(handle) {
		let root = window.theme.routes.root_url || '';
		if (root[root.length - 1] !== '/') {
			root = `${root}/`;
		}
		const requestRoute = `${root}products/${handle}.js`;
		return window
			.fetch(requestRoute)
			.then((response) => {
				return response.json();
			})
			.catch((e) => {
				console.error(e);
			});
	}

	const selectors$p = {
		saleClass: 'sale',
		soldClass: 'sold-out',
		doubleImage: 'double__image',
	};

	function formatPrices(product) {
		// Apprend classes for on sale and sold out
		const on_sale = product.price <= product.compare_at_price_min;
		let classes = on_sale ? selectors$p.saleClass : '';
		classes += product.available ? '' : selectors$p.soldClass;
		// Add 'from' before min prive if price varies
		product.price = themeCurrency.formatMoney(product.price, theme.moneyFormat);
		product.price_with_from = product.price;
		if (product.prive_varies) {
			let min = themeCurrency.formatMoney(product.price_min, theme.moneyFormat);
			product.price_with_from = `<small>${window.theme.strings.from}</small> ${min}`;
		}

		// add a class if there's more than one media
		let double_class = '';
		if (product.media !== undefined) {
			if (product.media.length > 1) {
				double_class += selectors$p.doubleImage;
			}
		}

		const formatted = {
			...product,
			classes,
			on_sale,
			double_class,
			sold_out: !product.available,
			sold_out_translation: window.theme.strings.soldOut,
			compare_at_price: themeCurrency.formatMoney(product.compare_at_price, theme.moneyFormat),
			compare_at_price_max: themeCurrency.formatMoney(product.compare_at_price_max, theme.moneyFormat),
			compare_at_price_min: themeCurrency.formatMoney(product.compare_at_price_min, theme.moneyFormat),
			price_max: themeCurrency.formatMoney(product.price_max, theme.moneyFormat),
			price_min: themeCurrency.formatMoney(product.price_min, theme.moneyFormat),
		};
		return formatted;
	}

	const selectors$q = {
		append: '[data-predictive-search-append]',
		input: 'data-predictive-search-input',
		productTemplate: '[product-grid-item-template]',
		productWrapper: '[data-product-wrap]',
		productWrapperOuter: '[data-product-wrap-outer]',
		titleTemplate: '[data-predictive-search-title-template]',
		titleWrapper: '[data-search-title-wrap]',
		dirtyClass: 'dirty',
		loadingClass: 'is-loading',
		searchPopdown: 'search-popdown',
	};

	class SearchPredictive {
		constructor(input) {
			this.input = input;
			this.key = this.input.getAttribute(selectors$q.input);
			const appendSelector = `[id='${this.key}']`;
			this.append = document.querySelector(appendSelector);
			this.productTemplate = document.querySelector(selectors$q.productTemplate).innerHTML;
			this.titleTemplate = document.querySelector(selectors$q.titleTemplate).innerHTML;
			this.titleWrapper = document.querySelector(selectors$q.titleWrapper);
			this.productWrapper = this.append.querySelector(selectors$q.productWrapper);
			this.productWrapperOuter = this.append.querySelector(selectors$q.productWrapperOuter);
			this.popdown = document.getElementById(selectors$q.searchPopdown);
			this.result = null;
			this.accessibility = a11y;
			this.initSearch();
		}

		initSearch() {
			this.input.addEventListener(
				'input',
				debounce(
					function (event) {
						const val = event.target.value;
						if (val && val.length > 1) {
							this.productWrapperOuter.classList.add(selectors$q.loadingClass);
							this.render(val);
						} else {
							this.reset();
							this.append.classList.remove(selectors$q.dirtyClass);
						}
					}.bind(this),
					300
				)
			);
			this.input.addEventListener('clear', this.reset.bind(this));
		}

		render(terms) {
			fetch(
				`/search/suggest.json?q=${encodeURIComponent(
					terms
				)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`
			)
				.then(this.handleErrors)
				.then((response) => response.json())
				.then((response) => {
					this.result = response.resources.results;
					return this.fetchProducts(response.resources.results.products);
				})
				.then((response) => {
					this.injectTitle(terms);

					setTimeout(() => {
						this.reset(false);
						this.productWrapperOuter.classList.remove(selectors$q.loadingClass);
						this.injectProduct(response);
						this.append.classList.add(selectors$q.dirtyClass);

						this.accessibility.trapFocus(this.popdown);
						this.input.focus();
					}, 1000);
				})
				.catch((e) => {
					console.error(e);
				});
		}

		reset(clearTerms = true) {
			this.productWrapper.innerHTML = '';
			this.append.classList.remove(selectors$q.dirtyClass);
			this.input.val = '';
			this.accessibility.removeTrapFocus();

			if (clearTerms) {
				this.titleWrapper.innerHTML = '';
				this.popdown.classList.remove('results-visible');
			}
		}

		injectTitle(terms) {
			let title = window.theme.strings.noResultsFor;
			let count = '';
			if (this.result && this.result.products.length > 0) {
				count = this.result.products.length;
				title = window.theme.strings.resultsFor;
			}
			this.popdown.classList.add('results-visible');
			this.titleWrapper.innerHTML = Sqrl.render(this.titleTemplate, {
				count: count,
				title: title,
				query: terms,
			});
		}

		injectProduct(productHTML) {
			this.productWrapper.innerHTML += productHTML;
		}

		fetchProducts(products) {
			const promises = [];
			products.forEach((product) => {
				// because of a translation bug in the predictive search API
				// we need to fetch the product JSON from the handle
				promises.push(
					fetchProduct(product.handle).then((productJSON) => {
						const formatted = formatPrices(productJSON);
						return this.renderProduct(formatted);
					})
				);
			});

			return Promise.all(promises).then((result) => {
				let str = '';
				result.forEach((render) => {
					str += render;
				});
				return str;
			});
		}

		renderProduct(product) {
			let media = null;
			let mediaHover = null;
			let image = '';
			let image_2 = '';

			if (product.media !== undefined) {
				media = product.media[0];
				mediaHover = product.media[1];
			}

			if (media) {
				image = {
					thumb: themeImages.getSizedImageUrl(media.preview_image.src, '800x800'),
					alt: media.preview_image.src,
				};
			}
			if (mediaHover) {
				image_2 = {
					thumb: themeImages.getSizedImageUrl(mediaHover.preview_image.src, '800x800'),
					alt: mediaHover.preview_image.src,
				};
			}
			const withImage = {
				...product,
				image,
				image_2,
			};
			return Sqrl.render(this.productTemplate, { product: withImage });
		}

		handleErrors(response) {
			if (!response.ok) {
				return response.json().then(function (json) {
					const e = new FetchError({
						status: response.statusText,
						headers: response.headers,
						json: json,
					});
					throw e;
				});
			}
			return response;
		}
	}

	const selectors$r = {
		body: 'body',
		popdownTrigger: 'data-popdown-toggle',
		close: '[data-close-popdown]',
		input: '[data-predictive-search-input]',
	};

	const classes$7 = {
		isVisible: 'is-visible',
	};

	let sections$a = {};

	class SearchPopdownTriggers {
		constructor(trigger) {
			this.trigger = trigger;
			this.key = this.trigger.getAttribute(selectors$r.popdownTrigger);
			this.search = null;

			const popdownSelector = `[id='${this.key}']`;
			this.document = document;
			this.popdown = document.querySelector(popdownSelector);
			this.input = this.popdown.querySelector(selectors$r.input);
			this.close = this.popdown.querySelector(selectors$r.close);
			this.body = document.querySelector(selectors$r.body);
			this.accessibility = a11y;

			this.initTriggerEvents();

			// Initialized once for every search trigger
			this.initPopdownEvents();
		}

		initTriggerEvents() {
			this.trigger.setAttribute('aria-haspopup', true);
			this.trigger.setAttribute('aria-expanded', false);
			this.trigger.setAttribute('aria-controls', this.key);

			this.trigger.addEventListener('click', (evt) => {
				evt.preventDefault();

				if (!this.body.classList.contains('is-focused')) {
					this.showPopdown();
				}
			});

			this.trigger.addEventListener('keyup', (evt) => {
				if (
					(evt.which === window.theme.keyboardKeys.SPACE || evt.which === window.theme.keyboardKeys.ENTER) &&
					this.body.classList.contains('is-focused')
				) {
					this.showPopdown();
				}
			});
		}

		initPopdownEvents() {
			this.search = new SearchPredictive(this.input);
			this.popdown.addEventListener(
				'keyup',
				function (evt) {
					if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
						return;
					}
					this.hidePopdown();
				}.bind(this)
			);

			this.close.addEventListener(
				'click',
				function () {
					this.hidePopdown();
				}.bind(this)
			);

			this.document.addEventListener('click', (event) => {
				const clickedElement = event.target;
				const isNotSearchExpandButton = !(
					clickedElement.matches(`[${selectors$r.popdownTrigger}]`) || clickedElement.closest(`[${selectors$r.popdownTrigger}]`)
				);
				const isNotSearchPopdownChild = !(clickedElement.matches(`#${this.key}`) || clickedElement.closest(`#${this.key}`));

				if (isNotSearchExpandButton && isNotSearchPopdownChild && this.popdown.classList.contains(classes$7.isVisible)) {
					this.hidePopdown();
				}
			});
		}

		hidePopdown() {
			this.popdown.classList.remove(classes$7.isVisible);
			this.input.form.reset();
			this.input.dispatchEvent(new CustomEvent('clear', { bubbles: false }));
			this.accessibility.removeTrapFocus();
			document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

			if (this.body.classList.contains('is-focused')) {
				setTimeout(() => {
					this.trigger.focus();
				}, 200);
			}
		}

		showPopdown() {
			document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
			this.popdown.classList.add(classes$7.isVisible);
			const val = this.input.value;
			this.input.value = '';
			this.input.value = val;

			this.accessibility.trapFocus(this.popdown);
			this.input.focus();
		}
	}

	const searchPopdown = {
		onLoad() {
			sections$a[this.id] = [];
			const triggers = this.container.querySelectorAll(`[${selectors$r.popdownTrigger}]`);
			triggers.forEach((el) => {
				sections$a[this.id].push(new SearchPopdownTriggers(el));
			});
		},
		onUnload: function () {
			sections$a[this.id].forEach((el) => {
				if (typeof el.unload === 'function') {
					el.unload();
				}
			});
		},
	};

	const selectors$s = {
		slideruleOpen: 'data-sliderule-open',
		slideruleClose: 'data-sliderule-close',
		sliderulePane: 'data-sliderule-pane',
		slideruleWrappper: '[data-sliderule]',
		focusable: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		children: `:scope > [data-animates], 
             :scope > * > [data-animates], 
             :scope > * > * >[data-animates],
             :scope > * > .sliderule-grid  > *`,
	};

	const classes$8 = {
		isVisible: 'is-visible',
	};

	let sections$b = {};

	class HeaderMobileSliderule {
		constructor(el) {
			this.sliderule = el;
			this.wrapper = el.closest(selectors$s.wrapper);
			this.key = this.sliderule.id;
			const btnSelector = `[${selectors$s.slideruleOpen}='${this.key}']`;
			const exitSelector = `[${selectors$s.slideruleClose}='${this.key}']`;
			this.trigger = document.querySelector(btnSelector);
			this.exit = document.querySelector(exitSelector);
			this.pane = document.querySelector(`[${selectors$s.sliderulePane}]`);
			this.children = this.sliderule.querySelectorAll(selectors$s.children);

			this.trigger.setAttribute('aria-haspopup', true);
			this.trigger.setAttribute('aria-expanded', false);
			this.trigger.setAttribute('aria-controls', this.key);

			this.clickEvents();
			this.staggerChildAnimations();
		}

		clickEvents() {
			this.trigger.addEventListener(
				'click',
				function () {
					this.showSliderule();
				}.bind(this)
			);
			this.exit.addEventListener(
				'click',
				function () {
					this.hideSliderule();
				}.bind(this)
			);
		}

		keyboardEvents() {
			this.trigger.addEventListener(
				'keyup',
				function (evt) {
					if (evt.which !== window.theme.keyboardKeys.SPACE) {
						return;
					}
					this.showSliderule();
				}.bind(this)
			);
			this.sliderule.addEventListener(
				'keyup',
				function (evt) {
					if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
						return;
					}
					this.hideSliderule();
					this.buttons[0].focus();
				}.bind(this)
			);
		}

		staggerChildAnimations() {
			this.children.forEach((child, index) => {
				child.style.transitionDelay = `${index * 50 + 10}ms`;
			});
		}

		hideSliderule() {
			this.sliderule.classList.remove(classes$8.isVisible);
			this.children.forEach((el) => {
				el.classList.remove(classes$8.isVisible);
			});
			const newPosition = parseInt(this.pane.dataset.sliderulePane, 10) - 1;
			this.pane.setAttribute(selectors$s.sliderulePane, newPosition);
		}

		showSliderule() {
			this.sliderule.classList.add(classes$8.isVisible);
			this.children.forEach((el) => {
				el.classList.add(classes$8.isVisible);
			});
			const newPosition = parseInt(this.pane.dataset.sliderulePane, 10) + 1;
			this.pane.setAttribute(selectors$s.sliderulePane, newPosition);
		}
	}

	const headerMobileSliderule = {
		onLoad() {
			sections$b[this.id] = [];
			const els = this.container.querySelectorAll(selectors$s.slideruleWrappper);
			els.forEach((el) => {
				sections$b[this.id].push(new HeaderMobileSliderule(el));
			});
		},
		onUnload: function () {
			sections$b[this.id].forEach((el) => {
				if (typeof el.unload === 'function') {
					el.unload();
				}
			});
		},
	};

	const selectors$t = {
		wrapper: '[data-header-wrapper]',
		style: 'data-header-style',
		widthContentWrapper: '[data-takes-space-wrapper]',
		widthContent: '[data-child-takes-space]',
		desktop: '[data-header-desktop]',
		cloneClass: 'js__header__clone',
		showMobileClass: 'js__show__mobile',
		backfill: '[data-header-backfill]',
		transparent: 'data-header-transparent',
		overrideBorder: 'header-override-border',
		firstSectionHasImage: '.main-content > .shopify-section:first-child [data-overlay-header]',
		deadLink: '.navlink[href="#"]',
	};

	let sections$c = {};

	class Header {
		constructor(el) {
			this.wrapper = el;
			this.style = this.wrapper.dataset.style;
			this.desktop = this.wrapper.querySelector(selectors$t.desktop);
			this.transparent = this.wrapper.getAttribute(selectors$t.transparent) !== 'false';
			this.overlayedImages = document.querySelectorAll(selectors$t.firstSectionHasImage);
			this.deadLinks = document.querySelectorAll(selectors$t.deadLink);

			this.killDeadLinks();
			if (this.style !== 'drawer' && this.desktop) {
				this.minWidth = this.getMinWidth();
				this.listenWidth();
			}
			this.checkForImage();

			window.dispatchEvent(new Event('resize'));
		}

		unload() {
			document.removeEventListener('theme:resize', this.checkWidth);
		}

		checkForImage() {
			if (this.overlayedImages.length > 0 && this.transparent) {
				// is transparent and has image, overlay the image
				document.querySelector(selectors$t.backfill).style.display = 'none';
				this.listenOverlay();
			} else {
				this.wrapper.setAttribute(selectors$t.transparent, false);
			}

			if (this.overlayedImages.length > 0 && !this.transparent) {
				// Have image but not transparent, remove border bottom
				this.wrapper.classList.add(selectors$t.overrideBorder);
				this.subtractHeaderHeight();
			}
		}

		listenOverlay() {
			document.addEventListener('theme:resize', this.subtractAnnouncementHeight.bind(this));
			this.subtractAnnouncementHeight();
		}

		listenWidth() {
			document.addEventListener('theme:resize', this.checkWidth.bind(this));
			this.checkWidth();
		}

		killDeadLinks() {
			this.deadLinks.forEach((el) => {
				el.onclick = (e) => {
					e.preventDefault();
				};
			});
		}

		subtractAnnouncementHeight() {
			const { windowHeight, announcementHeight, headerHeight } = readHeights();
			this.overlayedImages.forEach((el) => {
				el.style.setProperty('--full-screen', `${windowHeight - announcementHeight}px`);
				el.style.setProperty('--header-padding', `${headerHeight}px`);
				el.classList.add('has-overlay');
			});
		}

		subtractHeaderHeight() {
			const { windowHeight, headerHeight } = readHeights();
			this.overlayedImages.forEach((el) => {
				el.style.setProperty('--full-screen', `${windowHeight - headerHeight}px`);
			});
		}

		checkWidth() {
			if (document.body.clientWidth < this.minWidth) {
				this.wrapper.classList.add(selectors$t.showMobileClass);
			} else {
				this.wrapper.classList.remove(selectors$t.showMobileClass);
			}
		}

		getMinWidth() {
			const comparitor = this.wrapper.cloneNode(true);
			comparitor.classList.add(selectors$t.cloneClass);
			document.body.appendChild(comparitor);
			const widthWrappers = comparitor.querySelectorAll(selectors$t.widthContentWrapper);
			let minWidth = 0;
			let spaced = 0;

			widthWrappers.forEach((context) => {
				const wideElements = context.querySelectorAll(selectors$t.widthContent);
				let thisWidth = 0;
				if (wideElements.length === 3) {
					thisWidth = _sumSplitWidths(wideElements);
				} else {
					thisWidth = _sumWidths(wideElements);
				}
				if (thisWidth > minWidth) {
					minWidth = thisWidth;
					spaced = wideElements.length * 20;
				}
			});

			document.body.removeChild(comparitor);
			return minWidth + spaced;
		}
	}

	function _sumSplitWidths(nodes) {
		let arr = [];
		nodes.forEach((el) => {
			if (el.firstElementChild) {
				arr.push(el.firstElementChild.clientWidth);
			}
		});
		if (arr[0] > arr[2]) {
			arr[2] = arr[0];
		} else {
			arr[0] = arr[2];
		}
		const width = arr.reduce((a, b) => a + b);
		return width;
	}
	function _sumWidths(nodes) {
		let width = 0;
		nodes.forEach((el) => {
			width += el.clientWidth;
		});
		return width;
	}

	const header = {
		onLoad() {
			sections$c = new Header(this.container);
		},
		onUnload: function () {
			if (typeof sections$c.unload === 'function') {
				sections$c.unload();
			}
		},
	};

	register('header', [header, drawer, popoutSection, headerMobileSliderule, stickyHeader, hoverDisclosure, headerTotals, searchPopdown]);

	const selectors$u = {
		slider: '.look__slider',
		slide: '.look__slide',
		prevArrow: '[data-prev-arrow]',
		nextArrow: '[data-next-arrow]',
		classIsHidden: 'is-hidden',
		dataSlideIndex: 'data-slide-index',
	};

	const sections$d = {};

	class Look {
		constructor(section) {
			this.container = section.container;
			this.slideshow = this.container.querySelector(selectors$u.slider);
			this.sliderPrev = this.container.querySelector(selectors$u.prevArrow);
			this.sliderNext = this.container.querySelector(selectors$u.nextArrow);
			this.flkty = null;

			this.init();
		}

		init() {
			const self = this;
			this.flkty = new Flickity(this.slideshow, {
				autoPlay: false,
				prevNextButtons: false,
				contain: true,
				pageDots: false,
				adaptiveHeight: true,
				wrapAround: true,
				on: {
					ready: function () {
						const slidesNumber = this.slides.length;
						if (slidesNumber > 1) {
							self.sliderPrev.classList.remove(selectors$u.classIsHidden);
							self.sliderNext.classList.remove(selectors$u.classIsHidden);
						} else {
							self.sliderPrev.classList.add(selectors$u.classIsHidden);
							self.sliderNext.classList.add(selectors$u.classIsHidden);
						}
					},
				},
			});

			this.sliderPrev.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.previous(true);
			});

			this.sliderNext.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.next(true);
			});
		}

		onBlockSelect(evt) {
			// Ignore the cloned version
			const slide = this.slideshow.querySelector(`${selectors$u.slide}--${evt.detail.blockId}`);
			const slideIndex = parseInt(slide.getAttribute(selectors$u.dataSlideIndex));

			// Go to selected slide, pause autoplay
			this.flkty.select(slideIndex);
			this.flkty.stopPlayer();
		}
	}

	const lookSection = {
		onLoad() {
			sections$d[this.id] = new Look(this);
		},
		onBlockSelect(e) {
			sections$d[this.id].onBlockSelect(e);
		},
	};

	register('look', [lookSection, productGridReviews, quickviewSection]);

	const selectors$v = {
		mobileSlider: '.product-grid--slider',
		flickityInitialized: 'flickity-enabled',
	};

	class GridSlider {
		constructor(container) {
			this.container = container;
			this.mobileSlider = this.container.querySelector(selectors$v.mobileSlider);
			this.flkty = null;

			this.init();
		}

		init() {
			this.mobileCheck();
			document.addEventListener('theme:resize', this.mobileCheck.bind(this));
		}

		initMobileSlider() {
			if (this.mobileSlider && !this.mobileSlider.classList.contains(selectors$v.flickityInitialized)) {
				this.flkty = new Flickity(this.mobileSlider, {
					contain: true,
					pageDots: false,
					arrowShape:
						'M4.068 18.649l15.466 16.105c.31.283.466.629.466 1.039 0 .41-.155.77-.466 1.08a1.412 1.412 0 0 1-1.038.424c-.41 0-.756-.141-1.038-.424L.466 19.708A1.373 1.373 0 0 1 0 18.648c0-.423.155-.776.466-1.059L17.458.424A1.412 1.412 0 0 1 18.496 0c.41 0 .755.141 1.038.424.31.282.466.636.466 1.06 0 .423-.155.776-.466 1.059L4.068 18.649z',
				});
			}
		}

		destroyMobileSlider() {
			if (this.flkty && typeof this.flkty.destroy === 'function') {
				this.flkty.destroy();
			}
		}

		mobileCheck() {
			const isMobile = window.innerWidth < window.theme.sizes.small;
			if (isMobile) {
				this.initMobileSlider();
			} else {
				this.destroyMobileSlider();
			}
		}
	}
	const gridSlider = {
		onLoad() {
			new GridSlider(this.container);
		},
	};

	register('product-grid', [productGridReviews, gridSlider, quickviewSection]);

	const selectors$w = {
		quantityAdjust: '.js-qty__adjust',
		quantityAdjustPlus: 'js-qty__adjust--plus',
		quantityAdjustMinus: 'js-qty__adjust--minus',
		quantityNum: '.js-qty__num',
	};

	const quantity = (e, element) => {
		if (e.type === 'keyup' && e.keyCode !== 13) return;

		const el = element;
		const qtySelector = el.parentElement.querySelector(selectors$w.quantityNum);
		let qty = parseInt(qtySelector.value.replace(/\D/g, ''));

		// Make sure we have a valid integer
		if (parseFloat(qty) == parseInt(qty) && !isNaN(qty));
		else {
			// Not a number. Default to 1.
			qty = 1;
		}
		// Add or subtract from the current quantity
		if (el.classList.contains(selectors$w.quantityAdjustPlus)) {
			qty += 1;
		} else {
			qty -= 1;
			if (qty <= 1) qty = 1;
		}
		// Update the input's number
		qtySelector.value = qty;
		qtySelector.dispatchEvent(new Event('click'));
	};

	function quantitySelectors() {
		// Setup listeners to add/subtract from the input
		const quantityAdjust = document.querySelectorAll(selectors$w.quantityAdjust);
		quantityAdjust.forEach((element) => {
			element.addEventListener('click', function (e) {
				quantity(e, element);
			});
			element.addEventListener('keyup', function (e) {
				quantity(e, element);
			});
		});

		// All quantity input value change on up/down arrows key press
		const quantityNum = document.querySelectorAll(selectors$w.quantityNum);
		quantityNum.forEach((element) => {
			element.addEventListener('keyup', function (e) {
				// On arrow up key press (38)
				if (e.keyCode == 38) {
					this.parentElement.querySelector(`.${selectors$w.quantityAdjustPlus}`).dispatchEvent(new Event('click'));
				}
				// On arrow down key press (40)
				if (e.keyCode == 40) {
					this.parentElement.querySelector(`.${selectors$w.quantityAdjustMinus}`).dispatchEvent(new Event('click'));
				}
			});
		});
	}

	window.theme.variables = {
		productPageSticky: false,
		bpSmall: false,
		mediaQuerySmall: 750,
	};

	const selectors$x = {
		addToCart: '[data-add-to-cart]',
		priceWrapper: '[data-price-wrapper]',
		slideshow: '[data-product-slideshow]',
		productImage: '[data-product-image]',
		productJson: '[data-product-json]',
		form: '[data-product-form]',
		dropdown: '[data-single-option-selector]',
		thumbs: '[data-product-thumbs]',
		dataSectionId: 'data-section-id',
		dataTallLayout: 'data-tall-layout',
		dataStickyEnabled: 'data-sticky-enabled',
		dataCartBar: 'data-cart-bar',
		dataReviews: 'data-reviews',
		productPage: '.product__page',
		formWrapper: '.form__wrapper',
		accordionToggle: '.accordion-toggle',
		accordionIcon: '.accordion__icon',
		tabs: '.tabs',
		productTabsWrapper: '.productTabsWrapper',
		productAccordion: '.product-accordion',
		productTabs: '.product-tabs',
		tabsLi: 'ul.tabs > li',
		tabLink: '.tab-link',
		tabContent: '.tab-content',
		dataTab: 'data-tab',
		cartBar: '#cart-bar',
		selectTag: 'select',
		dataPosition: 'data-position',
		productSubmitAdd: '.product__submit__add',
		dataIndex: 'data-index',
		templateProduct: '#template-product',
		siteFooterWrapper: '.site-footer-wrapper',
		shopifyProductReviews: '#shopify-product-reviews',
		classCurrent: 'current',
		classSticky: 'is-sticky',
		classStickyHeader: 'with-sticky-header',
		classRotate: 'rotate-90',
		classCartBarVisible: 'cart-bar--is-visible',
		classSiteFooterPush: 'site-footer--push',
		headerSticky: '[data-header-sticky="sticky"]',
	};

	/**
	 * Product section constructor.
	 * @param {string} container - selector for the section container DOM element
	 */
	class Product {
		constructor(section) {
			this.section = section;
			this.container = section.container;
			this.id = this.container.getAttribute(selectors$x.dataSectionId);
			this.tallLayout = this.container.getAttribute(selectors$x.dataTallLayout) === 'true';
			this.stickyEnabled = this.container.getAttribute(selectors$x.dataStickyEnabled) === 'true';
			this.headerSticky = document.querySelector(selectors$x.headerSticky) !== null;
			this.showReviews = this.container.getAttribute(selectors$x.dataReviews) === 'true';
			this.thumbs = this.container.querySelector(selectors$x.thumbs);

			// Stop parsing if we don't have the product json script tag when loading
			// section in the Theme Editor
			const productJson = this.container.querySelector(selectors$x.productJson);
			if ((productJson && !productJson.innerHTML) || !productJson) {
				return;
			}

			this.form = this.container.querySelector(selectors$x.form);

			this.init();
			this.tabs();
			this.tabsSpaceCheck();
			quantitySelectors();

			this.accordion();
			if (this.stickyEnabled) {
				this.stickyScroll();
			}
			if (this.container.getAttribute(selectors$x.dataCartBar) === 'true') {
				this.initCartBar();
			}
			const currentScope = this;
			window.addEventListener('resize', currentScope.tabsSpaceCheck);

			const reviewsAppInstalled = typeof window.SPR == 'function';

			if (this.showReviews && reviewsAppInstalled) {
				window.SPR.initDomEls();
				window.SPR.loadBadges();
			}
		}

		init() {
			theme.mediaInstances[this.id] = new Media(this.section);
			theme.mediaInstances[this.id].init();
		}

		stickyScroll() {
			this.stickyScrollCheck();

			document.addEventListener('theme:resize', () => this.stickyScrollCheck());
		}

		stickyScrollCheck() {
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const isDesktop = windowWidth >= theme.variables.mediaQuerySmall;
			const form = this.container.querySelector(selectors$x.formWrapper);
			const targetFormWrapper = this.container.querySelector(`${selectors$x.productPage} ${selectors$x.formWrapper}`);

			let noReviews = true;
			if (this.showReviews) {
				noReviews = false;
			}

			if (isDesktop) {
				const slideshow = this.container.querySelector(selectors$x.slideshow);
				if (!form || !slideshow) return;
				const productCopyHeight = form.offsetHeight;
				const productImagesHeight = slideshow.offsetHeight;

				// Is the product description and form taller than window space
				// Is is also shorter than the window and images
				if (productCopyHeight < productImagesHeight && productCopyHeight < window.innerHeight && noReviews) {
					theme.variables.productPageSticky = true;

					targetFormWrapper.classList.add(selectors$x.classSticky);
					if (this.headerSticky) {
						targetFormWrapper.classList.add(selectors$x.classStickyHeader);
					}
				} else {
					theme.variables.productPageSticky = false;
					targetFormWrapper.classList.remove(selectors$x.classSticky);
				}
			} else {
				targetFormWrapper.classList.remove(selectors$x.classSticky);
			}
		}

		tabs() {
			const ctx = this.container;
			const tabsNavList = ctx.querySelectorAll(selectors$x.tabsLi);
			const firstTabLink = ctx.querySelector(`${selectors$x.tabLink}-0`);
			const firstTabContent = ctx.querySelector(`${selectors$x.tabContent}-0`);

			if (firstTabContent) {
				firstTabContent.classList.add(selectors$x.classCurrent);
			}

			if (firstTabLink) {
				firstTabLink.classList.add(selectors$x.classCurrent);
			}

			if (tabsNavList.length) {
				const tabFunctionality = (element) => {
					const tabId = parseInt(element.getAttribute(selectors$x.dataTab));
					ctx.querySelector(`${selectors$x.tabsLi}.${selectors$x.classCurrent}`).classList.remove(selectors$x.classCurrent);
					ctx.querySelector(`${selectors$x.tabContent}.${selectors$x.classCurrent}`).classList.remove(selectors$x.classCurrent);

					element.classList.add(selectors$x.classCurrent);
					ctx.querySelector(`${selectors$x.tabContent}-${tabId}`).classList.add(selectors$x.classCurrent);
				};

				tabsNavList.forEach((element) => {
					element.addEventListener('click', function (e) {
						tabFunctionality(element);
					});

					element.addEventListener('keyup', function (e) {
						if (e.type === 'keyup' && e.keyCode !== 13) return;
						tabFunctionality(element);
					});
				});
			}
		}

		accordion() {
			// mobile accordion for tabs content
			const accordionToggles = this.container.querySelectorAll(selectors$x.accordionToggle);
			if (accordionToggles.length) {
				const self = this;

				accordionToggles.forEach((element) => {
					element.addEventListener('click', function (e) {
						const elemSibling = this.nextElementSibling;
						const checkNextDiv = elemSibling && elemSibling.tagName === 'DIV';
						if (checkNextDiv && window.getComputedStyle(elemSibling).display !== 'none') {
							slideUp(elemSibling);
						} else if (checkNextDiv) {
							slideDown(elemSibling);
							self.stickyScrollCheck;
						}

						const elemIcon = this.querySelector(selectors$x.accordionIcon);

						if (elemIcon) {
							elemIcon.classList.toggle(selectors$x.classRotate);
						}
					});
				});
			}
		}

		tabsSpaceCheck() {
			if (this.container && this.container.querySelector(selectors$x.tabs) && this.container.querySelector(selectors$x.productTabsWrapper)) {
				const tabsWidth = this.container.querySelector(selectors$x.tabs).clientWidth;
				const tabsWrapper = this.container.querySelector(selectors$x.productTabsWrapper).clientWidth - 31;
				const productAccordion = this.container.querySelector(selectors$x.productAccordion);
				const productTabs = this.container.querySelector(selectors$x.productTabs);
				if (tabsWidth >= tabsWrapper) {
					if (productTabs) {
						productTabs.style.position = 'absolute';
						productTabs.style.left = '-9999px';
						productTabs.style.display = 'table';
					}

					if (productAccordion) {
						showElement(productAccordion);
					}
				} else {
					if (productTabs) {
						productTabs.removeAttribute('style');
					}

					if (productAccordion) {
						hideElement(productAccordion);
					}
				}
			}
		}

		initCartBar() {
			const instance = this;
			const cartBar = document.querySelector(selectors$x.cartBar);
			const cartBarSelectors = cartBar.querySelectorAll(selectors$x.selectTag);
			const formSelectors = this.form.querySelectorAll(selectors$x.dropdown);
			const cartBarPosition = cartBar.getAttribute(selectors$x.dataPosition);

			if (cartBarSelectors.length) {
				cartBarSelectors.forEach((element) => {
					// Update product form on cart bar variant change
					element.addEventListener('change', function () {
						const index = this.getAttribute(selectors$x.dataIndex);
						const optionSelected = this.value;
						const targets = instance.form.querySelectorAll(`${selectors$x.dropdown}[${selectors$x.dataIndex}="${index}"]`);

						if (targets[0].tagName === 'INPUT') {
							for (const target of targets) {
								const targetIndex = target.getAttribute(selectors$x.dataIndex);
								const targetValue = target.value;
								if (targetIndex === index && targetValue === optionSelected) {
									target.checked = true;
									target.dispatchEvent(new Event('change'));
									break;
								}
							}
						} else {
							const select = targets[0];
							select.value = optionSelected;

							select.dispatchEvent(new Event('change'));
						}
					});
				});
			}

			// Update cart bar on product form variant change
			if (formSelectors.length) {
				formSelectors.forEach((element) => {
					element.addEventListener('change', function () {
						const index = this.getAttribute(selectors$x.dataIndex);
						const optionSelected = this.value;

						cartBar.querySelector(`[${selectors$x.dataIndex}="${index}"]`).value = optionSelected;
					});
				});
			}

			this.cartBar = cartBar;

			document.addEventListener('theme:scroll', this.scrollTop.bind(this));

			if (cartBarPosition === 'top') {
				this.setCartBarPosition();
				document.addEventListener('theme:scroll', this.setCartBarPosition.bind(this));
			}
		}

		scrollTop() {
			const scrolled = window.pageYOffset;
			const siteFooter = document.querySelector(selectors$x.siteFooterWrapper);

			if (this.form && this.cartBar) {
				const formOffset = this.form.offsetTop;
				const formHeight = this.form.offsetHeight;

				if (scrolled > formOffset + formHeight) {
					this.cartBar.classList.add(selectors$x.classCartBarVisible);
					siteFooter.classList.add(selectors$x.classSiteFooterPush);
				} else {
					this.cartBar.classList.remove(selectors$x.classCartBarVisible);
					siteFooter.classList.remove(selectors$x.classSiteFooterPush);
				}
			}
		}

		setCartBarPosition() {
			if (this.headerSticky) {
				this.cartBar.style.top = `var(--menu-height)`;
			} else {
				this.cartBar.style.top = '0';
			}
		}
	}

	const productSection = {
		onLoad() {
			this.section = new Product(this);
		},
		onUnload: function () {
			if (typeof this.section.unload === 'function') {
				this.section.unload();
			}
		},
	};

	register('product', [productFormSection, productSection]);

	register('list-collections', gridSlider);

	const selectors$y = {
		body: 'body',
		dataRelatedSectionElem: '[data-related-section]',
		dataRelatedProduct: '[data-product-grid-item]',
		dataProductId: 'data-product-id',
		dataLimit: 'data-limit',
		datTab: 'data-tab',
		dataTabIndex: 'data-tab-index',
		tabsLi: 'ul.tabs > li',
		tabLink: '.tab-link',
		tabLinkRecent: '.tab-link__recent',
		tabContent: '.tab-content',
		recentlyViewed: '#RecentlyViewed',
		recentlyProduct: '#recently-viewed-products .product-item',
	};

	const classes$9 = {
		classCurrent: 'current',
		classHide: 'hide',
		classAlt: 'alt',
	};

	class Related {
		constructor(section) {
			this.section = section;
			this.container = section.container;
			this.body = document.querySelector(selectors$y.body);
			this.accessibility = window.accessibility;

			this.init();
			this.tabs();
			this.recent();
		}

		init() {
			const relatedSection = this.container.querySelector(selectors$y.dataRelatedSectionElem);

			if (!relatedSection) {
				return;
			}

			const self = this;
			const productId = relatedSection.getAttribute(selectors$y.dataProductId);
			const limit = relatedSection.getAttribute(selectors$y.dataLimit);
			const requestUrl = `${window.theme.routes.product_recommendations_url}?section_id=related&limit=${limit}&product_id=${productId}`;
			const section = this.section;

			fetch(requestUrl)
				.then(function (response) {
					return response.text();
				})
				.then(function (data) {
					const createdElement = document.createElement('div');
					createdElement.innerHTML = data;
					const inner = createdElement.querySelector(selectors$y.dataRelatedSectionElem);

					if (inner.querySelector(selectors$y.dataRelatedProduct)) {
						const innerHtml = inner.innerHTML;
						hideElement(relatedSection);
						relatedSection.innerHTML = innerHtml;
						slideDown(relatedSection);

						if (!Shopify.Products.hasProducts || !document.querySelector(selectors$y.recentlyViewed)) {
							new Quickview(section);
						}
					} else {
						self.hideRelatedTab();
					}
				})
				.catch(function () {
					self.hideRelatedTab();
				});
		}

		hideRelatedTab() {
			const relatedSection = this.container.querySelector(selectors$y.dataRelatedSectionElem);
			if (!relatedSection) {
				return;
			}

			const parentTabContent = relatedSection.closest(`${selectors$y.tabContent}.${classes$9.classCurrent}`);
			if (!parentTabContent) {
				return;
			}
			const parentTabContentIdx = parseInt(parentTabContent.getAttribute(selectors$y.dataTabIndex));
			const tabsNavList = this.container.querySelectorAll(selectors$y.tabsLi);

			if (tabsNavList.length > parentTabContentIdx) {
				const nextTabsNavLink = tabsNavList[parentTabContentIdx].nextSibling;

				if (nextTabsNavLink) {
					tabsNavList[parentTabContentIdx].classList.add(classes$9.classHide);
					nextTabsNavLink.dispatchEvent(new Event('click'));
				}
			}
		}

		tabs() {
			const ctx = this.container;
			const tabsNavList = ctx.querySelectorAll(selectors$y.tabsLi);
			const firstTabLink = ctx.querySelector(`${selectors$y.tabLink}-0`);
			const firstTabContent = ctx.querySelector(`${selectors$y.tabContent}-0`);

			if (firstTabContent) {
				firstTabContent.classList.add(classes$9.classCurrent);
			}

			if (firstTabLink) {
				firstTabLink.classList.add(classes$9.classCurrent);
			}

			this.checkVisibleTabLinks();

			if (tabsNavList.length) {
				tabsNavList.forEach((element) => {
					const tabId = parseInt(element.getAttribute(selectors$y.datTab));
					const tab = ctx.querySelector(`${selectors$y.tabContent}-${tabId}`);

					element.addEventListener('click', () => {
						this.tabChange(element, ctx, tab);
					});

					element.addEventListener('keyup', (event) => {
						if (
							(event.which === window.theme.keyboardKeys.SPACE || event.which === window.theme.keyboardKeys.ENTER) &&
							this.body.classList.contains('is-focused')
						) {
							this.tabChange(element, ctx, tab);

							if (tab.querySelector('a, input')) {
								this.accessibility.lastFocused = element;

								this.accessibility.a11y.trapFocus(tab, {
									elementToFocus: tab.querySelector('a:first-child, input:first-child'),
								});
							}
						}
					});
				});
			}
		}

		tabChange(element, ctx, tab) {
			ctx.querySelector(`${selectors$y.tabsLi}.${classes$9.classCurrent}`).classList.remove(classes$9.classCurrent);
			ctx.querySelector(`${selectors$y.tabContent}.${classes$9.classCurrent}`).classList.remove(classes$9.classCurrent);

			element.classList.add(classes$9.classCurrent);
			tab.classList.add(classes$9.classCurrent);

			if (element.classList.contains(classes$9.classHide)) {
				tab.classList.add(classes$9.classHide);
			}

			this.checkVisibleTabLinks();

			this.accessibility.a11y.removeTrapFocus();
		}

		checkVisibleTabLinks() {
			const tabsNavList = this.container.querySelectorAll(selectors$y.tabsLi);
			const tabsNavListHided = this.container.querySelectorAll(`${selectors$y.tabLink}.${classes$9.classHide}`);
			const difference = tabsNavList.length - tabsNavListHided.length;

			if (difference < 2) {
				this.container.classList.add(classes$9.classAlt);
			} else {
				this.container.classList.remove(classes$9.classAlt);
			}
		}

		recent() {
			const self = this;
			const section = this.section;
			const recentlyViewed = document.querySelector(selectors$y.recentlyViewed);
			let howManyToshow = 4;

			if (recentlyViewed) {
				howManyToshow = parseInt(recentlyViewed.getAttribute(selectors$y.dataLimit));
				Shopify.Products.recordRecentlyViewed();
			}

			Shopify.Products.showRecentlyViewed({
				howManyToShow: howManyToshow,
				onComplete: function () {
					const recentProductsCount = document.querySelectorAll(selectors$y.recentlyProduct).length;

					if (recentProductsCount > 0) {
						fadeIn(recentlyViewed);

						const tabLink = document.querySelector(selectors$y.tabLinkRecent);

						if (tabLink) {
							tabLink.classList.remove(classes$9.classHide);
							const tabLinkIdx = parseInt(tabLink.getAttribute(selectors$y.datTab));
							const tabContent = document.querySelector(`${selectors$y.tabContent}[${selectors$y.dataTabIndex}="${tabLinkIdx}"]`);
							if (tabContent) {
								tabContent.classList.remove(classes$9.classHide);
							}

							self.checkVisibleTabLinks();
						}

						new Quickview(section);
					}
				},
			});
		}
	}

	const relatedSection = {
		onLoad() {
			this.section = new Related(this);
			new Quickview(this);
		},
		onUnload: function () {
			if (typeof this.section.unload === 'function') {
				this.section.unload();
			}
		},
	};

	register('related', [relatedSection, popoutSection]);

	const sections$e = {};

	const selectors$z = {
		logo: '[data-slider-logo]',
		text: '[data-slider-text]',
		slide: '[data-slide]',
		slideData: 'data-slide',
		asNavFor: '#nav-for-',
		prevArrow: '[data-prev-arrow]',
		nextArrow: '[data-next-arrow]',
		slideIndex: 'data-slide-index',
		flickityEnabled: 'flickity-enabled',
		classIsSelected: 'is-selected',
	};

	class LogoList {
		constructor(section) {
			this.container = section.container;
			this.slideshowText = this.container.querySelector(selectors$z.text);
			this.slideshowNav = this.container.querySelector(selectors$z.logo);
			this.slideshowPrev = this.container.querySelector(selectors$z.prevArrow);
			this.slideshowNext = this.container.querySelector(selectors$z.nextArrow);
			this.logoSlides = this.slideshowNav.querySelectorAll(selectors$z.slide);
			this.textSlides = this.slideshowText.querySelectorAll(selectors$z.slide);
			this.flkty = null;
			this.flktyNav = null;

			this.init();
		}

		init() {
			this.flkty = new Flickity(this.slideshowText, {
				autoPlay: false,
				prevNextButtons: false,
				contain: true,
				pageDots: false,
				wrapAround: false,
				selectedAttraction: 0.3,
				friction: 0.8,
				draggable: false,
			});

			this.logoSlides.forEach((element) => {
				element.addEventListener(
					'click',
					function (e) {
						const index = parseInt(e.currentTarget.getAttribute(selectors$z.slideIndex));
						const hasSlider = this.slideshowNav.classList.contains(selectors$z.flickityEnabled);

						this.flkty.select(index);
						if (hasSlider) {
							this.flktyNav.select(index);
						} else {
							this.logoSlides.forEach((logo) => {
								logo.classList.remove(selectors$z.classIsSelected);
							});
							e.currentTarget.classList.add(selectors$z.classIsSelected);
						}
					}.bind(this)
				);
			});

			let maxHeight = -1;
			if (this.textSlides) {
				this.textSlides.forEach((element) => {
					const elementHeight = parseFloat(getComputedStyle(element, null).height.replace('px', ''));

					if (elementHeight > maxHeight) {
						maxHeight = elementHeight;
					}
				});

				this.textSlides.forEach((element) => {
					const elementHeight = parseFloat(getComputedStyle(element, null).height.replace('px', ''));

					if (elementHeight < maxHeight) {
						const calculateMargin = Math.ceil((maxHeight - elementHeight) / 2);
						element.style.margin = `${calculateMargin}px 0`;
					}
				});
			}

			this.initSlideshowNav();
		}

		onUnload() {
			const sliderInitialized = this.slideshowNav.classList.contains(selectors$z.flickityEnabled);
			if (sliderInitialized) {
				this.flktyNav.destroy();
			}

			this.flkty.destroy();
		}

		onBlockSelect(evt) {
			const slide = this.slideshowText.querySelector(`[${selectors$z.slideData}="${evt.detail.blockId}"]`);
			const slideIndex = parseInt(slide.getAttribute(selectors$z.slideIndex));
			const sliderInitialized = this.slideshowNav.classList.contains(selectors$z.flickityEnabled);

			if (sliderInitialized) {
				this.flktyNav.select(slideIndex);
				this.flktyNav.stopPlayer();
			}

			this.flkty.select(slideIndex);
			this.flkty.stopPlayer();
		}

		onBlockDeselect() {
			const sliderInitialized = this.slideshowNav.classList.contains(selectors$z.flickityEnabled);
			if (sliderInitialized) {
				this.flktyNav.playPlayer();
			}
		}

		goToPrevSlide(e) {
			e.preventDefault();

			this.flktyNav.previous(true);
			this.flkty.previous(true);
		}

		goToNextSlide(e) {
			e.preventDefault();

			this.flktyNav.next(true);
			this.flkty.next(true);
		}

		initSlideshowNav() {
			this.slideshowPrev.addEventListener('click', this.goToPrevSlide.bind(this));

			this.slideshowNext.addEventListener('click', this.goToNextSlide.bind(this));

			const setSlideshowNavState = () => {
				const slidesCount = this.slideshowNav.querySelectorAll(selectors$z.slide).length;
				const slideWidth = 200;
				const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
				const slidesWidth = slidesCount * slideWidth;
				const sliderInitialized = this.slideshowNav.classList.contains(selectors$z.flickityEnabled);

				if (slidesWidth > windowWidth) {
					if (!sliderInitialized) {
						this.logoSlides.forEach((logo) => {
							logo.classList.remove(selectors$z.classIsSelected);
						});

						this.flktyNav = new Flickity(this.slideshowNav, {
							autoPlay: false,
							prevNextButtons: false,
							contain: true,
							pageDots: false,
							selectedAttraction: 0.05,
							friction: 0.8,
						});
					}
				} else if (sliderInitialized) {
					this.flktyNav.destroy();
					this.logoSlides[0].classList.add(selectors$z.classIsSelected);
				}
			};

			setSlideshowNavState();

			window.addEventListener('resize', debounce(setSlideshowNavState.bind(this), 200));
		}
	}

	const LogoListSection = {
		onLoad() {
			sections$e[this.id] = new LogoList(this);
		},
		onUnload(e) {
			sections$e[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$e[this.id].onBlockSelect(e);
		},
	};

	register('logos', LogoListSection);

	/**
	 * Reviews Script
	 * ------------------------------------------------------------------------------
	 * A file that contains scripts highly couple code to the Reviews section.
	 *
	 * @namespace reviews
	 */

	const sections$f = {};

	const selectors$A = {
		reviewWithImage: 'reviews-with-image',
		reviewSlider: '.reviews__slider--',
		reviewSlide: '.review__slide',
		reviewSliderArrow: '.reviews__slider-arrow',
		prevArrow: '[data-prev-arrow]',
		nextArrow: '[data-next-arrow]',
		dataSlides: 'data-slides',
		dataSlideIndex: 'data-slide-index',
		classIsHidden: 'is-hidden',
		classIsSelected: 'is-selected',
		classSliderInitialized: 'reviews__slider--initialized',
		blockquote: 'blockquote',
	};

	class Reviews {
		constructor(section) {
			this.container = section.container;
			const sectionId = section.id;
			this.slideshow = this.container.querySelector(selectors$A.reviewSlider + sectionId);
			this.hasImage = this.container.classList.contains(selectors$A.reviewWithImage);
			this.slides = this.container.querySelectorAll(selectors$A.reviewSlide);
			this.slidesNumber = this.slides.length;
			this.sliderPrev = this.container.querySelector(selectors$A.prevArrow);
			this.sliderNext = this.container.querySelector(selectors$A.nextArrow);
			this.flkty = null;

			this.init();
		}

		init() {
			const self = this;

			this.flkty = new Flickity(this.slideshow, {
				autoPlay: false,
				prevNextButtons: false,
				contain: true,
				pageDots: false,
				adaptiveHeight: true,
				wrapAround: true,
				percentPosition: true,
				cellAlign: 'left',
				draggable: false,
				on: {
					ready: function () {
						self.showArrows();
					},
				},
			});

			window.addEventListener(
				'resize',
				debounce(function () {
					self.showArrows();
					self.flkty.resize();
					if (!self.slideshow.classList.contains(selectors$A.classSliderInitialized)) {
						self.flkty.select(0);
					}
				}, 100)
			);

			this.sliderPrev.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.previous(true);
			});

			this.sliderNext.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.next(true);
			});
		}

		showArrows() {
			const slidesNumberCustom = parseInt(this.slideshow.getAttribute(selectors$A.dataSlides));
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			let flag = false;

			if (!this.hasImage) {
				const largeDesktopCheck = windowWidth > 1339 && this.slidesNumber > slidesNumberCustom;
				const desktopCheck = windowWidth <= 1339 && windowWidth > 1023 && this.slidesNumber > 3;
				const tabletCheck = windowWidth <= 1023 && windowWidth > 767 && this.slidesNumber > 2;
				const mobileCheck = windowWidth <= 767 && this.slidesNumber > 1;

				if (largeDesktopCheck || desktopCheck || tabletCheck || mobileCheck) {
					flag = true;
				}
			} else if (this.hasImage && this.slidesNumber > 1) {
				flag = true;
			}

			if (flag) {
				this.sliderPrev.classList.remove(selectors$A.classIsHidden);
				this.sliderNext.classList.remove(selectors$A.classIsHidden);
				this.slideshow.classList.add(selectors$A.classSliderInitialized);
			} else {
				this.sliderPrev.classList.add(selectors$A.classIsHidden);
				this.sliderNext.classList.add(selectors$A.classIsHidden);
				this.slideshow.classList.remove(selectors$A.classSliderInitialized);
			}
		}

		onBlockSelect(evt) {
			const slide = this.slideshow.querySelector(`${selectors$A.reviewSlide}--${evt.detail.blockId}`);
			const slideIndex = parseInt(slide.getAttribute(selectors$A.dataSlideIndex));

			if (this.slideshow.classList.contains(selectors$A.classSliderInitialized)) {
				this.flkty.select(slideIndex);
			} else {
				this.flkty.select(0);
			}
		}
	}

	const ReviewsSection = {
		onLoad() {
			sections$f[this.id] = new Reviews(this);
		},
		onUnload(e) {
			sections$f[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$f[this.id].onBlockSelect(e);
		},
	};

	register('reviews', ReviewsSection);

	/**
	 * Testimonials Script
	 * ------------------------------------------------------------------------------
	 * A file that contains scripts highly couple code to the Testimonials section.
	 *
	 * @namespace testimonials
	 */

	const sections$g = {};

	const selectors$B = {
		testimonialWithImage: 'testimonials-with-image',
		testimonialSlider: '.testimonials__slider--',
		testimonialSlide: '.testimonial__slide',
		testimonialSliderArrow: '.testimonials__slider-arrow',
		prevArrow: '[data-prev-arrow]',
		nextArrow: '[data-next-arrow]',
		dataSlides: 'data-slides',
		dataSlideIndex: 'data-slide-index',
		classIsHidden: 'is-hidden',
		classIsSelected: 'is-selected',
		blockquote: 'blockquote',
	};

	class Testimonials {
		constructor(section) {
			this.container = section.container;
			const sectionId = section.id;
			this.slideshow = this.container.querySelector(selectors$B.testimonialSlider + sectionId);
			this.hasImage = this.container.classList.contains(selectors$B.testimonialWithImage);
			this.slides = this.container.querySelectorAll(selectors$B.testimonialSlide);
			this.sliderPrev = this.container.querySelector(selectors$B.prevArrow);
			this.sliderNext = this.container.querySelector(selectors$B.nextArrow);
			this.flkty = null;

			this.init();
		}

		init() {
			const self = this;

			const showArrows = () => {
				const slidesNumber = self.slides.length;
				const slidesNumberCustom = parseInt(self.slideshow.getAttribute(selectors$B.dataSlides));
				const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
				let flag = false;

				if (!self.hasImage) {
					const largeDesktopCheck = windowWidth > 1339 && slidesNumber > slidesNumberCustom;
					const desktopCheck = windowWidth <= 1339 && windowWidth > 1023 && slidesNumber > 3;
					const tabletCheck = windowWidth <= 1023 && windowWidth > 767 && slidesNumber > 2;
					const mobileCheck = windowWidth <= 767 && slidesNumber > 1;

					if (largeDesktopCheck || desktopCheck || tabletCheck || mobileCheck) {
						flag = true;
					}
				} else if (self.hasImage && slidesNumber > 1) {
					flag = true;
				}

				if (flag) {
					self.sliderPrev.classList.remove(selectors$B.classIsHidden);
					self.sliderNext.classList.remove(selectors$B.classIsHidden);
				} else {
					self.sliderPrev.classList.add(selectors$B.classIsHidden);
					self.sliderNext.classList.add(selectors$B.classIsHidden);
				}
			};

			this.flkty = new Flickity(this.slideshow, {
				autoPlay: false,
				prevNextButtons: false,
				contain: true,
				pageDots: false,
				adaptiveHeight: true,
				wrapAround: true,
				percentPosition: true,
				cellAlign: 'left',
				draggable: false,
				on: {
					ready: function () {
						showArrows();
					},
				},
			});

			window.addEventListener(
				'resize',
				debounce(function () {
					self.flkty.resize();
					showArrows();
				}, 100)
			);

			this.sliderPrev.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.previous(true);
			});

			this.sliderNext.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.next(true);
			});
		}

		onBlockSelect(evt) {
			// Ignore the cloned version
			const slide = this.slideshow.querySelector(`${selectors$B.testimonialSlide}--${evt.detail.blockId}`);
			const slideIndex = parseInt(slide.getAttribute(selectors$B.dataSlideIndex));

			// Go to selected slide, pause autoplay
			this.flkty.select(slideIndex);
			this.flkty.stopPlayer();
		}
	}

	const TestimonialsSection = {
		onLoad() {
			sections$g[this.id] = new Testimonials(this);
		},
		onUnload(e) {
			sections$g[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$g[this.id].onBlockSelect(e);
		},
	};

	register('testimonials', TestimonialsSection);

	/**
	 * FeaturedVideo Template Script
	 * ------------------------------------------------------------------------------
	 * A file that contains scripts highly couple code to the FeaturedVideo template.
	 *
	 * @namespace FeaturedVideo
	 */

	const sections$h = {};

	const selectors$C = {
		shopifySection: '.shopify-section',
		headerContent: '.header__content',
		heroContent: '.hero__content',
		heroContentWrapper: '.hero__content__wrapper',
		videoPlay: '.js-video__play',
		dataHtml: 'data-html',
	};

	class FeaturedVideo {
		/**
		 * FeaturedVideo section constructor. Runs on page load as well as Theme Editor
		 * `section:load` events.
		 * @param {string} container - selector for the section container DOM element
		 */

		constructor(section) {
			this.container = section.container;
			this.parentContainer = this.container.closest(selectors$C.shopifySection);
			this.parentContainerIdx = Array.from(this.parentContainer.parentElement.children).indexOf(this.parentContainer);
			this.heroContent = this.container.querySelector(selectors$C.heroContent);
			this.heroContentWrapper = this.container.querySelector(selectors$C.heroContentWrapper);
			this.videoPlay = this.container.querySelector(selectors$C.videoPlay);
			const self = this;

			this.setMinHeight();

			window.addEventListener('resize', debounce(self.setMinHeight(), 100));

			this.videoPlay.addEventListener('click', function (e) {
				if (this.hasAttribute(selectors$C.dataHtml) && this.getAttribute(selectors$C.dataHtml).trim() !== '') {
					e.preventDefault();

					const items = [
						{
							html: this.getAttribute(selectors$C.dataHtml),
						},
					];

					openPhotoSwipe(items);
				}
			});
		}

		setMinHeight() {
			const isFirstSection = this.parentContainerIdx == 0;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const headerContent = document.querySelector(selectors$C.headerContent);
			let headerContentHeight = 0;
			if (headerContent) {
				headerContentHeight = parseFloat(getComputedStyle(headerContent, null).height.replace('px', ''));
			}

			let headerHeight = windowWidth >= 750 ? headerContentHeight : 0;
			if (!isFirstSection) headerHeight = 0;
			const contentHeight = this.heroContent.offsetHeight;
			const minHeight = headerHeight + contentHeight;
			this.container.style.minHeight = `${minHeight}px`;
			this.heroContentWrapper.style.paddingTop = `${headerHeight}px`;
		}

		onReorder() {
			this.setMinHeight();
		}
	}

	const FeaturedVideoSection = {
		onLoad() {
			sections$h[this.id] = new FeaturedVideo(this);
		},
		onUnload(e) {
			sections$h[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$h[this.id].onBlockSelect(e);
		},
	};

	register('featured-video', FeaturedVideoSection);

	const throttle = (fn, wait) => {
		let prev, next;
		return function invokeFn(...args) {
			const now = Date.now();
			next = clearTimeout(next);
			if (!prev || now - prev >= wait) {
				// eslint-disable-next-line prefer-spread
				fn.apply(null, args);
				prev = now;
			} else {
				next = setTimeout(invokeFn.bind(null, ...args), wait - (now - prev));
			}
		};
	};

	/**
	 * FeaturedBackgroundVideo Template Script
	 * ------------------------------------------------------------------------------
	 * A file that contains scripts highly couple code to the FeaturedBackgroundVideo template.
	 *
	 * @namespace FeaturedBackgroundVideo
	 */

	const sections$i = {};

	const selectors$D = {
		shopifySection: '.shopify-section',
		headerContent: '.header__content',
		heroContent: '.hero__content',
		heroContentWrapper: '.hero__content__wrapper',
		checkTrueString: 'true',
		dataSectionId: 'data-section-id',
		dataEnableSound: 'data-enable-sound',
		dataVideoId: 'data-video-id',
		dataVideoType: 'data-video-type',
		attrId: 'id',
		loaded: 'loaded',
		videoIframe: '.video__iframe',
		videoWrapper: '.video-wrapper',
		youtubeWrapper: '[data-youtube-wrapper]',
	};

	const players$1 = [];
	let isYoutubeAPIReady = false;
	const defaultYoutubeOptions = {
		height: '720',
		width: '1280',
		playerVars: {
			cc_load_policy: 0,
			iv_load_policy: 3,
			modestbranding: 1,
			playsinline: 1,
			autohide: 0,
			controls: 0,
			branding: 0,
			showinfo: 0,
			rel: 0,
			fs: 0,
			wmode: 'opaque',
		},
		events: {
			onReady: onPlayerReady,
			onStateChange: onPlayerStateChange,
		},
	};

	function onPlayerReady(event) {
		const eventIframe = event.target.getIframe();
		const id = eventIframe.getAttribute(selectors$D.attrId);
		const enableSound = document.querySelector(`#${id}`).getAttribute(selectors$D.dataEnableSound) === selectors$D.checkTrueString;

		eventIframe.setAttribute('tabindex', '-1');

		if (enableSound) {
			event.target.unMute();
		} else {
			event.target.mute();
		}
		event.target.playVideo();

		checkPlayerVisibility(id);

		window.addEventListener(
			'scroll',
			throttle(() => {
				checkPlayerVisibility(id);
			}, 150)
		);
	}

	function onPlayerStateChange(event) {
		// Loop video if state is ended
		if (event.data == 0) {
			event.target.playVideo();
		}
		if (event.data == 1) {
			// video is playing
			event.target.getIframe().parentElement.classList.add(selectors$D.loaded);
		}
	}

	function checkPlayerVisibility(id) {
		let playerID;
		if (typeof id === 'string') {
			playerID = id;
		} else if (id.data != undefined) {
			playerID = id.data.id;
		} else {
			return false;
		}

		const playerElement = document.getElementById(playerID + '-container');
		const player = players$1[playerID];
		const box = playerElement.getBoundingClientRect();
		let isVisible = visibilityHelper.isElementPartiallyVisible(playerElement) || visibilityHelper.isElementTotallyVisible(playerElement);

		// Fix the issue when element height is bigger than the viewport height
		if (box.top < 0 && playerElement.clientHeight + box.top >= 0) {
			isVisible = true;
		}

		if (isVisible && player && typeof player.playVideo === 'function') {
			player.playVideo();
		} else if (!isVisible && player && typeof player.pauseVideo === 'function') {
			player.pauseVideo();
		}
	}

	class FeaturedBackgroundVideo {
		/**
		 * FeaturedBackgroundVideo section constructor. Runs on page load as well as Theme Editor
		 * `section:load` events.
		 * @param {string} container - selector for the section container DOM element
		 */

		constructor(section) {
			const self = this;
			this.container = section.container;
			this.parentContainer = this.container.closest(selectors$D.shopifySection);
			this.parentContainerIdx = Array.from(this.parentContainer.parentElement.children).indexOf(this.parentContainer);
			const player = (this.player = this.container.querySelector(selectors$D.videoIframe));

			this.setMinHeight();

			window.addEventListener('resize', debounce(self.setMinHeight(), 250));

			if (player) {
				this.playerID = player.querySelector(selectors$D.youtubeWrapper).getAttribute(selectors$D.attrId);
				this.videoID = player.getAttribute(selectors$D.dataVideoId);
				this.videoType = player.getAttribute(selectors$D.dataVideoType);
				this.init();
			}

			document.addEventListener('touchstart', function (e) {
				if (e.target.matches(selectors$D.videoWrapper) || e.target.closest(selectors$D.videoWrapper)) {
					const playerID = e.target.querySelector(selectors$D.videoIframe).getAttribute(selectors$D.attrId);
					players$1[playerID].playVideo();
				}
			});
		}

		init() {
			if (this.videoType == 'youtube') {
				if (isYoutubeAPIReady) {
					this.loadYoutubePlayer();
				} else {
					// Load Youtube API if not loaded yet
					window.loadYoutubeAPI();
					document.body.addEventListener('youtubeAPIReady', this.loadYoutubePlayer.bind(this));
				}
			} else if (this.videoType == 'vimeo') {
				const oembed_url = 'https://vimeo.com/api/oembed.json';
				const vimeo_url = 'https://vimeo.com/' + this.videoID;
				let paramsString = '';
				const state = this;

				const params = {
					url: vimeo_url,
					background: true,
					muted: true,
					autoplay: true,
				};

				for (let key in params) {
					paramsString += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&';
				}

				fetch(`${oembed_url}?${paramsString}`)
					.then((response) => response.json())
					.then(function (data) {
						state.player.innerHTML = data.html;

						setTimeout(function () {
							state.player.parentElement.classList.add(selectors$D.loaded);
						}, 1000);
					})
					.catch(function () {
						console.log('error');
					});
			}
		}

		setMinHeight() {
			const isFirstSection = this.parentContainerIdx == 0;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const headerContent = document.querySelector(selectors$D.headerContent);
			let headerContentHeight = 0;
			if (headerContent) {
				headerContentHeight = parseFloat(getComputedStyle(headerContent, null).height.replace('px', ''));
			}

			let headerHeight = windowWidth >= 750 ? headerContentHeight : 0;
			if (!isFirstSection) headerHeight = 0;
			const contentHeight = this.container.querySelector(selectors$D.heroContent).offsetHeight;
			const minHeight = headerHeight + contentHeight;

			this.container.style.minHeight = `${minHeight}px`;
			this.container.querySelector(selectors$D.heroContentWrapper).style.paddingTop = `${headerHeight}px`;
		}

		loadYoutubePlayer() {
			const currentYoutubeOptions = Object.assign({}, defaultYoutubeOptions);
			currentYoutubeOptions.videoId = this.videoID;
			if (this.videoID.length) {
				players$1[this.playerID] = new YT.Player(this.playerID, currentYoutubeOptions);
			}
			isYoutubeAPIReady = true;
		}

		onReorder() {
			this.setMinHeight();
		}

		onUnload(evt) {
			const playerID = 'youtube-' + this.container.getAttribute(selectors$D.dataSectionId);
			players$1[playerID].destroy();
		}
	}

	const FeaturedBackgroundVideoSection = {
		onLoad() {
			sections$i[this.id] = new FeaturedBackgroundVideo(this);
		},
		onUnload(e) {
			sections$i[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$i[this.id].onBlockSelect(e);
		},
	};

	register('featured-background-video', FeaturedBackgroundVideoSection);

	const sections$j = {};

	const selectors$E = {
		shopifySection: '.shopify-section',
		slideshow: '#slideshow-',
		slideshowSlide: '.slideshow__slide',
		slideshowSlideImg: '.slide-image-img',
		headerContent: '.header__content',
		heroContent: '.hero__content',
		heroContentWrapper: '.hero__content__wrapper',
		heroContentTransparent: 'hero__content--transparent',
		slideshowPrev: '.slideshow__arrow--prev',
		slideshowNext: '.slideshow__arrow--next',
		sliderDots: '.flickity-page-dots',
		dataAspectRatio: 'data-aspectratio',
		dataDots: 'data-dots',
		dataAutoplay: 'data-autoplay',
		dataAutoplaySpeed: 'data-speed',
		dataColor: 'data-color',
		dataSlideIndex: 'data-slide-index',
		textDark: 'text-dark',
		textLight: 'text-light',
		classIsSelected: 'is-selected',
		checkTrueString: 'true',
		transparentWrapper: 'transparent__wrapper',
		flickityEnabled: 'flickity-enabled',
	};

	class Slideshow {
		constructor(section) {
			this.container = section.container;
			this.sectionId = section.id;
			this.slideshow = this.container.querySelector(selectors$E.slideshow + this.sectionId);
			this.parentContainer = this.container.closest(selectors$E.shopifySection);
			this.parentContainerIdx = Array.from(this.parentContainer.parentElement.children).indexOf(this.parentContainer);
			this.heroContentWrapper = this.slideshow.querySelector(selectors$E.heroContentWrapper);
			this.slideshowPrev = this.container.querySelector(selectors$E.slideshowPrev);
			this.slideshowNext = this.container.querySelector(selectors$E.slideshowNext);
			this.flkty = null;

			this.init();
		}

		init() {
			const self = this;

			const autoPlay = this.slideshow.getAttribute(selectors$E.dataAutoplay) === selectors$E.checkTrueString;
			let autoPlaySpeed = false;
			const showDots = this.slideshow.getAttribute(selectors$E.dataDots) === selectors$E.checkTrueString;

			if (autoPlay) {
				autoPlaySpeed = parseInt(this.slideshow.getAttribute(selectors$E.dataAutoplaySpeed));
			}

			this.setMinHeight();

			this.flkty = new Flickity(this.slideshow, {
				autoPlay: autoPlaySpeed,
				prevNextButtons: false,
				contain: true,
				pageDots: showDots,
				adaptiveHeight: true,
				wrapAround: true,
				on: {
					ready: function () {
						self.slideActions(this.slider, this.element);
					},
				},
			});

			this.slideshowPrev.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.previous(true);
			});

			this.slideshowNext.addEventListener('click', function (e) {
				e.preventDefault();

				self.flkty.next(true);
			});

			this.flkty.on('change', function (index) {
				self.slideActions(this.slider, this.element);
			});

			window.addEventListener('resize', debounce(self.setMinHeight.bind(self), 250));
		}

		slideActions(slider, element) {
			const currentSlide = slider.querySelector(`.${selectors$E.classIsSelected}`);
			const currentSlideColor = currentSlide.getAttribute(selectors$E.dataColor);

			this.slideshowPrev.classList.remove(selectors$E.textLight, selectors$E.textDark);
			this.slideshowNext.classList.remove(selectors$E.textLight, selectors$E.textDark);

			this.slideshowPrev.classList.add(currentSlideColor);
			this.slideshowNext.classList.add(currentSlideColor);

			this.setDotsColor(element, currentSlideColor);

			this.container.classList.remove(selectors$E.transparentWrapper);
			if (currentSlide.querySelector(selectors$E.heroContentWrapper).classList.contains(selectors$E.heroContentTransparent)) {
				this.container.classList.add(selectors$E.transparentWrapper);
			}

			this.setMinHeight();
		}

		setDotsColor(slider, currentColor) {
			const dots = slider.querySelector(selectors$E.sliderDots);

			if (dots) {
				dots.classList.remove(selectors$E.textLight, selectors$E.textDark);
				dots.classList.add(currentColor);
			}
		}

		setMinHeight() {
			const slideshow = this.slideshow;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const isFirstSection = this.parentContainerIdx == 0;
			const headerContent = document.querySelector(selectors$E.headerContent);
			let minHeight = 0;
			let headerContentHeight = 0;
			if (headerContent) {
				headerContentHeight = parseFloat(getComputedStyle(headerContent, null).height.replace('px', ''));
			}

			if (isFirstSection) {
				const selectedSlide = slideshow.querySelector(`.${selectors$E.classIsSelected} ${selectors$E.heroContent}`);
				let sliderHeight = 0;
				const headerHeight = windowWidth >= 750 ? headerContentHeight : 0;
				if (selectedSlide) {
					sliderHeight = selectedSlide.offsetHeight;
				}
				const extraPadding = windowWidth >= 750 ? 120 : 40;
				minHeight = headerHeight + sliderHeight + extraPadding;

				this.heroContentWrapper.style.paddingTop = `${headerHeight}px`;
				this.heroContentWrapper.style.marginTop = `0px`;
			}

			slideshow.querySelectorAll(selectors$E.slideshowSlide).forEach((element) => {
				const slideImageImg = element.querySelector(selectors$E.slideshowSlideImg);
				let slideAspectRatio = '';
				if (slideImageImg && slideImageImg.hasAttribute(selectors$E.dataAspectRatio)) {
					slideAspectRatio = slideImageImg.getAttribute(selectors$E.dataAspectRatio);
				}

				let slideTextContentHeight = 0;
				const slideTextContent = element.querySelector(selectors$E.heroContent);
				if (slideTextContent) {
					slideTextContentHeight = slideTextContent.offsetHeight;
				}

				const slideWidth = parseInt(getComputedStyle(element, null).width.replace('px', ''));
				let slideHeight = parseInt(slideWidth / slideAspectRatio) || 0;
				const isCurrentSlide = element.classList.contains(selectors$E.classIsSelected);

				if (isFirstSection && slideHeight < minHeight) {
					slideHeight = minHeight;
				}

				if (slideTextContentHeight > slideHeight) {
					slideHeight = slideTextContentHeight;
				}

				element.style.minHeight = `${slideHeight}px`;
				element.querySelector(selectors$E.heroContentWrapper).style.minHeight = `${slideHeight}px`;

				if (isCurrentSlide) {
					slideshow.parentElement.style.minHeight = `${slideHeight}px`;
				}
			});
		}

		onUnload() {
			this.flkty.destroy();
		}

		onBlockSelect(evt) {
			// Ignore the cloned version
			const slide = this.slideshow.querySelector(`${selectors$E.slideshowSlide}--${evt.detail.blockId}`);
			const slideIndex = parseInt(slide.getAttribute(selectors$E.dataSlideIndex));

			// Go to selected slide, pause autoplay
			this.slideshow.classList.add(selectors$E.classIsSelected);
			this.flkty.select(slideIndex);
			this.flkty.stopPlayer();
		}

		onBlockDeselect(evt) {
			this.slideshow.classList.remove(selectors$E.classIsSelected);
			this.flkty.playPlayer();
		}
	}

	const SlideshowSection = {
		onLoad() {
			sections$j[this.id] = new Slideshow(this);
		},
		onUnload(e) {
			sections$j[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$j[this.id].onBlockSelect(e);
		},
		onBlockDeselect(e) {
			sections$j[this.id].onBlockDeselect(e);
		},
	};

	register('slideshow', SlideshowSection);

	const sections$k = {};

	const selectors$F = {
		videoPlay: '.image__video__play',
		dataHtml: 'data-html',
	};

	class ImageWithText {
		constructor(section) {
			this.container = section.container;
			this.id = section.id;
			this.videoPlay = this.container.querySelector(selectors$F.videoPlay);

			if (this.videoPlay) {
				this.videoPlay.addEventListener('click', function (e) {
					if (this.hasAttribute(selectors$F.dataHtml) && this.getAttribute(selectors$F.dataHtml).trim() !== '') {
						e.preventDefault();

						const items = [
							{
								html: this.getAttribute(selectors$F.dataHtml),
							},
						];

						openPhotoSwipe(items);
					}
				});
			}
		}
	}

	const ImageWithTextSection = {
		onLoad() {
			sections$k[this.id] = new ImageWithText(this);
		},
		onUnload(e) {
			sections$k[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$k[this.id].onBlockSelect(e);
		},
	};

	register('image-with-text', ImageWithTextSection);

	/**
	 * Cart functions
	 * ------------------------------------------------------------------------------
	 * Functions that should execute everywhere
	 *
	 */

	const selectors$G = {
		showCalculator: '.show-calculator',
		shippingCalculator: '#ShippingCalculator',
		showNote: '.show-note',
		note: '#NoteForSeller',
		additionalInsert: '[data-additional-insert]',
		cartAdditional: '[data-cart-additional]',
		cartButton: '#CartButton .cart-count-js',
		cartSubtotal: '.cart__subtotal',
		qtyWrapper: '.js-qty',
		qtyAdjust: '.js-qty__adjust',
		dataNewQty: 'data-new-quantity',
		dataLineKey: 'data-line-key',
		dataCartFormWrapper: 'data-cart-form-wrapper',
		dataVariantId: 'data-variant-id',
		formErrors: '[data-form-errors]',
	};

	const classes$a = {
		open: 'open',
		pulseCartIcon: 'pulse-cart-icon',
		qtyDisabled: 'qty--disabled',
		qtyNum: 'js-qty__num',
		qtyPlus: 'js-qty__adjust--plus',
		qtyMinus: 'js-qty__adjust--minus',
	};

	const sections$l = {};

	class Cart {
		constructor(section) {
			this.init();
			const cartAdditional = document.querySelector(selectors$G.cartAdditional);
			document.querySelector(selectors$G.additionalInsert).append(cartAdditional);
			slideDown(cartAdditional);
		}

		init() {
			this.initQuantity();

			document.querySelector(selectors$G.showCalculator).addEventListener('click', function () {
				slideToggle(document.querySelector(selectors$G.shippingCalculator));
				this.classList.toggle(classes$a.open);
			});

			document.querySelector(selectors$G.showNote).addEventListener('click', function () {
				slideToggle(document.querySelector(selectors$G.note));
				this.classList.toggle(classes$a.open);
			});

			Shopify.Cart.ShippingCalculator.show({
				submitButton: theme.strings.shippingCalcSubmitButton,
				submitButtonDisabled: theme.strings.shippingCalcSubmitButtonDisabled,
				customerIsLoggedIn: theme.strings.shippingCalcCustomerIsLoggedIn,
				moneyFormat: theme.strings.shippingCalcMoneyFormat,
			});
		}

		updateHeader() {
			try {
				window
					.fetch('/cart.js')
					.then(function (response) {
						return response.json();
					})
					.then(function (response) {
						const count = response.item_count;
						const cartCount = document.querySelector(selectors$G.cartButton);
						if (cartCount) {
							cartCount.textContent = count;
							showElement(cartCount);
							cartCount.classList.add(classes$a.pulseCartIcon);
							setTimeout(function () {
								document.querySelector(selectors$G.cartButton).classList.remove(classes$a.pulseCartIcon);
							}, 800);
						}
					});
			} catch (e) {
				console.warn(e);
			}
		}

		initQuantity() {
			const instance = this;
			const adjustButtons = document.querySelectorAll(selectors$G.qtyAdjust);
			if (adjustButtons) {
				adjustButtons.forEach((element) => {
					element.addEventListener('click', function () {
						const element = this;
						const elementParent = element.closest(selectors$G.qtyWrapper);
						elementParent.classList.add(classes$a.qtyDisabled);
						const children = elementParent.children;
						for (const child of children) {
							child.disabled = true;
						}
						const newQuantity = parseInt(element.getAttribute(selectors$G.dataNewQty));
						const itemKey = element.getAttribute(selectors$G.dataLineKey);

						instance.changeQuantity('/cart/change.js', newQuantity, itemKey);
					});
				});
			}
		}

		changeQuantity(endpoint, quantity, id) {
			const instance = this;
			const data = {
				quantity: quantity,
				id: id,
			};

			fetch(endpoint, {
				method: 'POST',
				headers: {
					Accept: 'application/json, text/plain, */*',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			})
				.then((response) => response.json())
				.then((data) => {
					const items = document.querySelectorAll(`[${selectors$G.dataCartFormWrapper}] [${selectors$G.dataVariantId}]`);
					const priceElem = document.querySelector(selectors$G.cartSubtotal);

					if (priceElem) {
						priceElem.textContent = themeCurrency.formatMoney(data.total_price, theme.moneyFormat);
					}

					if (data.items.length !== items.length) {
						let flag = false;
						for (const element of items) {
							const elementId = parseInt(element.getAttribute(selectors$G.dataVariantId));

							for (const [i, dataItem] of data.items.entries()) {
								const dataItemId = dataItem.id;

								if (elementId === dataItemId) {
									break;
								}

								if (elementId !== dataItemId && i === data.items.length - 1) {
									element.parentNode.removeChild(element);
									flag = true;
									break;
								}
							}

							if (flag) {
								break;
							}
						}
					} else {
						for (const element of data.items) {
							const elementId = element.id;
							const elementQty = element.quantity;
							const item = document.querySelector(`[${selectors$G.dataCartFormWrapper}] [${selectors$G.dataVariantId}="${elementId}"]`);
							const itemQty = item.querySelector(selectors$G.qtyWrapper);
							itemQty.classList.remove(classes$a.qtyDisabled);
							const children = itemQty.children;

							for (const child of children) {
								child.disabled = false;
								if (child.classList.contains(classes$a.qtyNum)) {
									child.value = elementQty;
								} else if (child.classList.contains(classes$a.qtyPlus)) {
									child.setAttribute(selectors$G.dataNewQty, elementQty + 1);
								} else if (child.classList.contains(classes$a.qtyMinus)) {
									child.setAttribute(selectors$G.dataNewQty, elementQty - 1);
								}
							}
						}
					}

					instance.init();
					instance.updateHeader();
				})
				.catch((error) => {
					const errors = document.querySelector(selectors$G.formErrors);
					while (errors.firstChild) errors.removeChild(errors.firstChild);
					slideUp(errors, 100);
					errors.innerHTML = error;
					setTimeout(function () {
						slideDown(errors);
					}, 100);
				});
		}
	}

	const CartSection = {
		onLoad() {
			sections$l[this.id] = new Cart(this);
		},
		onUnload(e) {
			sections$l[this.id].onUnload(e);
		},
		onBlockSelect(e) {
			sections$l[this.id].onBlockSelect(e);
		},
	};

	register('cart', CartSection);

	var styles = {};
	styles.basic = [];
	/* eslint-disable */
	styles.light = [
		{ featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'simplified' }, { lightness: '64' }, { hue: '#ff0000' }] },
		{ featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
		{ featureType: 'administrative', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
		{ featureType: 'landscape', elementType: 'all', stylers: [{ color: '#f0f0f0' }, { visibility: 'simplified' }] },
		{ featureType: 'landscape.natural.landcover', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'landscape.natural.terrain', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'poi', elementType: 'geometry.fill', stylers: [{ visibility: 'off' }] },
		{ featureType: 'poi', elementType: 'labels', stylers: [{ lightness: '100' }] },
		{ featureType: 'poi.park', elementType: 'all', stylers: [{ visibility: 'on' }] },
		{ featureType: 'poi.park', elementType: 'geometry', stylers: [{ saturation: '-41' }, { color: '#e8ede7' }] },
		{ featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road', elementType: 'all', stylers: [{ saturation: '-100' }] },
		{ featureType: 'road', elementType: 'labels', stylers: [{ lightness: '25' }, { gamma: '1.06' }, { saturation: '-100' }] },
		{ featureType: 'road.highway', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
		{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ gamma: '10.00' }] },
		{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ weight: '0.01' }, { visibility: 'simplified' }] },
		{ featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ weight: '0.8' }] },
		{ featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.arterial', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.local', elementType: 'geometry.fill', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ gamma: '10.00' }, { lightness: '100' }, { weight: '0.4' }] },
		{ featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'simplified' }, { weight: '0.01' }, { lightness: '39' }] },
		{ featureType: 'road.local', elementType: 'labels.text.stroke', stylers: [{ weight: '0.50' }, { gamma: '10.00' }, { lightness: '100' }] },
		{ featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'water', elementType: 'all', stylers: [{ color: '#cfe5ee' }, { visibility: 'on' }] },
	];

	styles.white_label = [
		{ featureType: 'all', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
		{ featureType: 'all', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
		{ featureType: 'administrative', elementType: 'labels', stylers: [{ gamma: '3.86' }, { lightness: '100' }] },
		{ featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#cccccc' }] },
		{ featureType: 'landscape', elementType: 'all', stylers: [{ color: '#f2f2f2' }] },
		{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road', elementType: 'all', stylers: [{ saturation: -100 }, { lightness: 45 }] },
		{ featureType: 'road.highway', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
		{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ weight: '0.8' }] },
		{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ weight: '0.8' }] },
		{ featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ weight: '0.8' }] },
		{ featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ weight: '0' }] },
		{ featureType: 'road.arterial', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.local', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
		{ featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'water', elementType: 'all', stylers: [{ color: '#e4e4e4' }, { visibility: 'on' }] },
	];

	styles.dark_label = [
		{ featureType: 'all', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'all', elementType: 'labels.text.fill', stylers: [{ saturation: 36 }, { color: '#000000' }, { lightness: 40 }] },
		{ featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ visibility: 'on' }, { color: '#000000' }, { lightness: 16 }] },
		{ featureType: 'all', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
		{ featureType: 'administrative', elementType: 'geometry.fill', stylers: [{ color: '#000000' }, { lightness: 20 }] },
		{ featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }, { lightness: 17 }, { weight: 1.2 }] },
		{ featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'simplified' }, { lightness: '-82' }] },
		{ featureType: 'administrative', elementType: 'labels.text.stroke', stylers: [{ invert_lightness: true }, { weight: '7.15' }] },
		{ featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 20 }] },
		{ featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 21 }] },
		{ featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
		{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#000000' }, { lightness: 17 }, { weight: '0.8' }] },
		{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }, { lightness: 29 }, { weight: '0.01' }] },
		{ featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 18 }] },
		{ featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 16 }] },
		{ featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ weight: '0.01' }] },
		{ featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
		{ featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
		{ featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 19 }] },
		{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 17 }] },
	];
	/* eslint-enable */

	function mapStyle(key) {
		return styles[key];
	}

	window.theme.allMaps = window.theme.allMaps || {};
	let allMaps = window.theme.allMaps;

	window.theme.mapAPI = window.theme.mapAPI || null;

	/* global google */

	class Map {
		constructor(section) {
			this.container = section.container;
			this.mapContainer = this.container.querySelector('[data-map-container]');
			this.key = this.container.getAttribute('data-api-key');
			this.styleString = this.container.getAttribute('data-style') || '';
			this.zoomString = this.container.getAttribute('data-zoom') || 14;
			this.address = this.container.getAttribute('data-address');
			this.enableCorrection = this.container.getAttribute('data-latlong-correction');
			this.lat = this.container.getAttribute('data-lat');
			this.long = this.container.getAttribute('data-long');

			if (this.key) {
				this.initMaps();
			}
		}

		initMaps() {
			const apiLoaded = loadAPI(this.key);
			apiLoaded
				.then(() => {
					return this.enableCorrection === 'true' && this.lat !== '' && this.long !== ''
						? new google.maps.LatLng(this.lat, this.long)
						: geocodeAddressPromise(this.address);
				})
				.then((center) => {
					const zoom = parseInt(this.zoomString, 10);
					const styles = mapStyle(this.styleString);
					const mapOptions = {
						zoom,
						styles,
						center,
						draggable: true,
						clickableIcons: false,
						scrollwheel: false,
						zoomControl: false,
						disableDefaultUI: true,
					};
					const map = createMap(this.mapContainer, mapOptions);

					return map;
				})
				.then((map) => {
					this.map = map;
					allMaps[this.id] = map;
				})
				.catch((e) => {
					console.log('Failed to load Google Map');
					console.log(e);
				});
		}

		unload() {
			if (typeof window.google !== 'undefined') {
				google.maps.event.clearListeners(this.map, 'resize');
			}
		}
	}

	const mapSection = {
		onLoad() {
			allMaps[this.id] = new Map(this);
		},
		onUnload() {
			if (typeof allMaps[this.id].unload === 'function') {
				allMaps[this.id].unload();
			}
		},
	};

	register('map', mapSection);

	function loadAPI(key) {
		if (window.theme.mapAPI === null) {
			const urlKey = `https://maps.googleapis.com/maps/api/js?key=${key}`;
			window.theme.mapAPI = loadScript({ url: urlKey });
		}
		return window.theme.mapAPI;
	}

	function createMap(container, options) {
		var map = new google.maps.Map(container, options);
		var center = map.getCenter();

		// eslint-disable-next-line no-unused-vars
		var marker = new google.maps.Marker({
			map: map,
			position: center,
		});

		google.maps.event.addDomListener(window, 'resize', function () {
			google.maps.event.trigger(map, 'resize');
			map.setCenter(center);
		});
		return map;
	}

	function geocodeAddressPromise(address) {
		return new Promise((resolve, reject) => {
			var geocoder = new google.maps.Geocoder();
			geocoder.geocode({ address: address }, function (results, status) {
				if (status == 'OK') {
					var latLong = {
						lat: results[0].geometry.location.lat(),
						lng: results[0].geometry.location.lng(),
					};
					resolve(latLong);
				} else {
					reject(status);
				}
			});
		});
	}

	register('search', [quickviewSection]);

	const selectors$H = {
		loginToggle: '#AdminLoginToggle',
		newsletterToggle: '#NewsletterToggle',
		login: '#AdminLogin',
		signup: '#CustomerSignup',
		errors: '.errors',
		contactErrors: '#contact_form .errors',
		loginErrors: '#login_form .errors',
	};

	class Password {
		constructor(section) {
			this.container = section.container;
			this.loginToggle = this.container.querySelector(selectors$H.loginToggle);
			this.newsletterToggle = this.container.querySelector(selectors$H.newsletterToggle);
			this.login = this.container.querySelector(selectors$H.login);
			this.signup = this.container.querySelector(selectors$H.signup);
			this.errors = this.container.querySelector(selectors$H.errors);
			this.contactErrors = this.container.querySelector(selectors$H.contactErrors);
			this.loginErrors = this.container.querySelector(selectors$H.loginErrors);
			this.init();
		}

		init() {
			const login = this.login;
			const signup = this.signup;
			const errors = this.errors;
			const contactErrors = this.contactErrors;
			const loginErrors = this.loginErrors;

			this.loginToggle.addEventListener('click', function (e) {
				e.preventDefault();
				slideDown(login);
				hideElement(signup);
				if (errors) {
					hideElement(errors);
				}
			});

			this.newsletterToggle.addEventListener('click', function (e) {
				e.preventDefault();
				hideElement(login);
				slideDown(signup);
				if (errors) {
					hideElement(errors);
				}
			});

			if (contactErrors) {
				hideElement(login);
				slideDown(signup);
			}

			if (loginErrors) {
				slideDown(login);
				hideElement(signup);
			}
		}
	}

	const passwordSection = {
		onLoad() {
			new Password(this);
		},
	};

	register('password-template', passwordSection);

	const wrap = (toWrap, wrapperClass = '', wrapper) => {
		wrapper = wrapper || document.createElement('div');
		wrapper.classList.add(wrapperClass);
		toWrap.parentNode.insertBefore(wrapper, toWrap);
		return wrapper.appendChild(toWrap);
	};

	const selectors$I = {
		promoModal: '#PromoModal',
		close: '.close',
		dataDevMode: 'data-dev-mode',
		cookieName: 'promo',
		modalWrapper: '.modal__wrapper',
	};

	function showPromo() {
		let promoDevMode = false;
		if (document.querySelector(selectors$I.promoModal) && document.querySelector(selectors$I.promoModal).getAttribute(selectors$I.dataDevMode)) {
			promoDevMode = document.querySelector(selectors$I.promoModal).getAttribute(selectors$I.dataDevMode);
			promoDevMode = promoDevMode === 'true';
		}

		let PromoCookie = {};
		PromoCookie.cookie = {
			configuration: {
				expires: null, // session cookie
				path: '/',
				domain: window.location.hostname,
			},
			name: selectors$I.cookieName,
			write: function (promo) {
				const hasCookie = document.cookie.indexOf('; ') !== -1 && !document.cookie.split('; ').find((row) => row.startsWith(this.name));
				if (hasCookie || document.cookie.indexOf('; ') === -1) {
					document.cookie = `${this.name}=${promo}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
				}
			},
			read: function () {
				if (document.cookie.indexOf('; ') !== -1 && document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
					const returnCookie = document.cookie
						.split('; ')
						.find((row) => row.startsWith(this.name))
						.split('=')[1];

					return returnCookie;
				}
			},
			destroy: function () {
				if (document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
					const emptyCookie = null;
					document.cookie = `${this.name}=${emptyCookie}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
				}
			},
		};

		const cookieExists = promoDevMode ? false : PromoCookie.cookie.read();

		if (!cookieExists) {
			if (!promoDevMode) {
				PromoCookie.cookie.write(selectors$I.cookieName);
			}

			const thisModal = document.querySelector(selectors$I.promoModal);
			const close = thisModal.querySelector(selectors$I.close);

			fadeIn(thisModal);

			close.addEventListener('click', function (e) {
				e.preventDefault();
				fadeOut(thisModal);
			});

			// Close on overlay click
			thisModal.addEventListener('click', function (e) {
				if (e.target === thisModal.querySelector(selectors$I.modalWrapper)) {
					fadeOut(thisModal);
				}
			});
		}
	}

	document.addEventListener('DOMContentLoaded', function () {
		// Load all registered sections on the page.
		load('*');

		// Animate on scroll
		const showAnimations = document.body.getAttribute('data-animations') === 'true';
		if (showAnimations) {
			AOS.init({ once: true });
			document.body.classList.add('aos-initialized');
		}

		// When images load, clear the background color
		document.addEventListener('lazyloaded', function (event) {
			const lazyImage = event.target.parentNode;
			if (lazyImage.classList.contains('lazy-image')) {
				lazyImage.style.backgroundImage = 'none';
			}
		});

		// Target tables to make them scrollable
		const tableSelectors = '.rte table';
		const tables = document.querySelectorAll(tableSelectors);
		tables.forEach((table) => {
			wrap(table, 'rte__table-wrapper');
		});

		// Target iframes to make them responsive
		const iframeSelectors = '.rte iframe[src*="youtube.com/embed"], .rte iframe[src*="player.vimeo"], .rte iframe#admin_bar_iframe';
		const frames = document.querySelectorAll(iframeSelectors);
		frames.forEach((frame) => {
			wrap(frame, 'rte__video-wrapper');
		});

		if (window.self !== window.top) {
			document.querySelector('html').classList.add('iframe');
		}

		const showPromoModal = document.body.getAttribute('data-promo') === 'true';
		if (showPromoModal) {
			showPromo();
		}

		// Safari smoothscroll polyfill
		let hasNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
		if (!hasNativeSmoothScroll) {
			loadScript({ url: window.theme.assets.smoothscroll });
		}
	});

	// Apply a specific class to the html element for browser support of cookies.
	if (window.navigator.cookieEnabled) {
		document.documentElement.className = document.documentElement.className.replace('supports-no-cookies', 'supports-cookies');
	}
})(
	themeVendor.themeCurrency,
	themeVendor.themeImages,
	themeVendor.themeAddresses,
	themeVendor.Sqrl,
	themeVendor.Flickity,
	themeVendor.Rellax,
	themeVendor.FlickityFade,
	themeVendor.AOS
);
